import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { format, addDays, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ArrowLeft, User, Wallet, FileCheck, History, Save, Eye, 
  TrendingUp, Play, Settings, AlertCircle, CheckCircle2, Zap, DollarSign,
  ChevronLeft, ChevronRight, Filter, Clock, CheckCircle, Key, Lock,
  UserX, UserCheck, Phone, MapPin, Calendar, Flag, Users, FileText,
  Copy, XCircle, Plus, Edit, Trash, CalendarPlus, StickyNote, Mail, Star, Loader2,
  QrCode, Bitcoin, Upload, Wifi, WifiOff, Globe, Monitor, Smartphone, CreditCard,
  ArrowDownCircle, ArrowUpCircle, RefreshCw, ArrowRightLeft, Briefcase, EyeOff, Send, Banknote, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== METHOD 3: POSITION PILOTING COMPONENT ====================
function Method3Piloting({ clientId, token, primaryColor }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoParams, setAutoParams] = useState({});
  const [manualAllowed, setManualAllowed] = useState({ allowed: false, source: 'global' });
  const [marketPrices, setMarketPrices] = useState({});

  const fetchPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trade-positions/${clientId}/live`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPositions(res.data || []);
    } catch {} finally { setLoading(false); }
  }, [clientId, token]);

  useEffect(() => { fetchPositions(); }, [fetchPositions]);

  // Fetch manual trading allowed status
  useEffect(() => {
    axios.get(`${API}/trading/manual-allowed/${clientId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setManualAllowed(res.data)).catch(() => {});
  }, [clientId, token]);

  const toggleManualAllowed = async (allowed) => {
    try {
      await axios.put(`${API}/trading/manual-allowed/${clientId}`, { allowed }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setManualAllowed(prev => ({ ...prev, allowed, source: 'client' }));
    } catch {}
  };

  const resetManualToGlobal = async () => {
    try {
      await axios.put(`${API}/trading/manual-allowed/${clientId}`, { reset_to_global: true }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API}/trading/manual-allowed/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setManualAllowed(res.data);
    } catch {}
  };

  // Poll every 2s for live updates (preserve references to avoid re-render disruption)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/trade-positions/${clientId}/live`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newPositions = res.data || [];
        setPositions(prev => {
          // Only update if data actually changed to avoid disrupting user interactions
          const prevJson = JSON.stringify(prev.map(p => p.id + p.simulated_price + p.status + p.simulation_active + p.simulation_end));
          const newJson = JSON.stringify(newPositions.map(p => p.id + p.simulated_price + p.status + p.simulation_active + p.simulation_end));
          return prevJson !== newJson ? newPositions : prev;
        });
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [clientId, token]);

  // Fetch live market prices so P&L reflects what client sees
  const fetchMarketPrices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market/prices`);
      const priceMap = {};
      const eurRate = res.data?.eur_rate || 1.08;
      const prices = res.data?.prices || [];
      if (Array.isArray(prices)) {
        for (const item of prices) {
          // Convert USD price to EUR (same as client sees)
          priceMap[item.symbol] = item.price / eurRate;
        }
      }
      setMarketPrices(priceMap);
    } catch {}
  }, []);

  useEffect(() => {
    fetchMarketPrices();
    const interval = setInterval(fetchMarketPrices, 5000);
    return () => clearInterval(interval);
  }, [fetchMarketPrices]);

  // Force re-render every second for timer display
  const [timerTick, setTimerTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTimerTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const handleManualPrice = async (posId, newPrice) => {
    try {
      await axios.put(`${API}/trade-positions/${posId}/simulate-price`, {
        simulated_price: newPrice,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
  };

  const handleAutoStart = async (pos) => {
    const params = autoParams[pos.id] || { target_pct: 4, duration: 60 };
    try {
      await axios.post(`${API}/trade-positions/${pos.id}/auto-simulate`, {
        target_pct: params.target_pct,
        duration_seconds: params.duration,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleStop = async (posId) => {
    try {
      await axios.post(`${API}/trade-positions/${posId}/stop-simulate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  };

  const handleClosePosition = async (pos) => {
    const liveMarket = marketPrices[pos.symbol] || 0;
    const simPrice = pos.simulated_price || (liveMarket > 0 ? liveMarket : pos.open_price);
    if (!window.confirm(`Fermer cette position au prix ${simPrice.toFixed(2)} € ?`)) return;
    try {
      await axios.post(`${API}/trade-positions/manual/${pos.id}/close`, {
        close_price: simPrice,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchPositions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const setParam = (posId, key, value) => {
    setAutoParams(prev => ({ ...prev, [posId]: { ...(prev[posId] || { target_pct: 4, duration: 60 }), [key]: value } }));
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;

  if (positions.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed rounded-lg">
        <TrendingUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground">Aucune position ouverte</p>
        <p className="text-xs text-muted-foreground mt-1">Le client doit d'abord ouvrir une position dans la salle des marchés</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="method3-piloting">
      {/* Toggle trading manuel pour ce client */}
      <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
        <div>
          <p className="font-semibold text-sm">Trading manuel pour ce client</p>
          <p className="text-xs text-muted-foreground">
            {manualAllowed.source === 'client' ? 'Paramètre spécifique au client' : 'Suit le paramètre global (Template client)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {manualAllowed.source === 'client' && (
            <button onClick={resetManualToGlobal} className="text-xs text-muted-foreground hover:text-foreground underline">
              Réinitialiser au global
            </button>
          )}
          <button
            onClick={() => toggleManualAllowed(!manualAllowed.allowed)}
            className={`w-11 h-6 rounded-full transition-colors ${manualAllowed.allowed ? 'bg-green-500' : 'bg-gray-600'}`}
            data-testid="pilot-manual-toggle"
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${manualAllowed.allowed ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className={`text-sm font-medium ${manualAllowed.allowed ? 'text-green-500' : 'text-muted-foreground'}`}>
            {manualAllowed.allowed ? 'Activé' : 'Désactivé'}
          </span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Contrôlez le prix affiché au client en temps réel. Le client voit le prix bouger dans sa salle des marchés.
      </p>
      {positions.map(pos => {
        const liveMarket = marketPrices[pos.symbol] || 0;
        const simPrice = pos.simulated_price || (liveMarket > 0 ? liveMarket : pos.open_price);
        const lev = parseFloat((pos.leverage || 'x1').toString().replace('x', '')) || 1;
        const pnl = pos.trade_type === 'BUY'
          ? (simPrice - pos.open_price) * pos.lot * lev
          : (pos.open_price - simPrice) * pos.lot * lev;
        const pnlPct = pos.open_price > 0 ? ((simPrice - pos.open_price) / pos.open_price * 100) * (pos.trade_type === 'BUY' ? 1 : -1) : 0;
        const params = autoParams[pos.id] || { target_pct: 4, duration: 60 };
        const isRunning = pos.simulation_active;
        const now = new Date();
        const remaining = isRunning && pos.simulation_end
          ? Math.max(0, Math.ceil((new Date(pos.simulation_end).getTime() - now.getTime()) / 1000))
          : 0;
        void timerTick; // force recalc

        return (
          <div key={pos.id} className="p-4 rounded-lg border bg-card" data-testid={`pilot-pos-${pos.id}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-bold text-lg">{pos.symbol}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${pos.trade_type === 'BUY' ? 'bg-green-600/20 text-green-500' : 'bg-red-600/20 text-red-500'}`}>
                  {pos.trade_type === 'BUY' ? 'Achat' : 'Vente'}
                </span>
                <span className="text-xs text-muted-foreground">Lot: {pos.lot} | Levier: {pos.leverage}</span>
              </div>
              <button
                onClick={() => handleClosePosition(pos)}
                className="px-3 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white"
                data-testid={`pilot-close-${pos.id}`}
              >
                Fermer la position
              </button>
            </div>

            {/* Prices & PnL */}
            <div className="grid grid-cols-4 gap-4 mb-4 p-3 rounded bg-secondary/30">
              <div>
                <p className="text-xs text-muted-foreground">Prix ouverture</p>
                <p className="font-mono font-bold">{pos.open_price?.toFixed(2)} €</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{pos.simulated_price ? 'Prix actuel (simulé)' : 'Prix marché (live)'}</p>
                <p className="font-mono font-bold text-primary">{simPrice?.toFixed(2)} €</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">P&L</p>
                <p className={`font-mono font-bold ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} €
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Variation</p>
                <p className={`font-mono font-bold ${pnlPct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)} %
                </p>
              </div>
            </div>

            {/* Manual Control */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded border space-y-3">
                <p className="text-sm font-semibold">Mode Manuel</p>
                <p className="text-center font-mono text-lg font-bold" style={{ color: primaryColor }}>{simPrice?.toFixed(simPrice < 10 ? 4 : 2)} €</p>
                {/* Percentage buttons - 2 rows */}
                <div className="grid grid-cols-4 gap-1">
                  {[-2, -1, -0.5, -0.1].map(pct => (
                    <button key={pct}
                      onClick={() => handleManualPrice(pos.id, simPrice + pos.open_price * (pct / 100))}
                      className="py-1.5 rounded bg-red-600/20 text-red-400 hover:bg-red-600/40 text-xs font-bold"
                    >{pct}%</button>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[0.1, 0.5, 1, 2].map(pct => (
                    <button key={pct}
                      onClick={() => handleManualPrice(pos.id, simPrice + pos.open_price * (pct / 100))}
                      className="py-1.5 rounded bg-green-600/20 text-green-400 hover:bg-green-600/40 text-xs font-bold"
                      data-testid={pct === 0.5 ? `pilot-plus-${pos.id}` : undefined}
                    >+{pct}%</button>
                  ))}
                </div>
                {/* Reset to market price */}
                <button
                  onClick={() => handleManualPrice(pos.id, pos.open_price)}
                  className="w-full py-1 rounded border border-border/50 text-xs text-muted-foreground hover:bg-secondary/50"
                >Réinitialiser au prix d'ouverture</button>
              </div>

              {/* Auto Control */}
              <div className="p-3 rounded border space-y-2">
                <p className="text-sm font-semibold">Mode Automatique</p>
                {isRunning ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs">
                        Cible: <span className={`font-bold ${pos.simulation_target_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>{pos.simulation_target_pct >= 0 ? '+' : ''}{pos.simulation_target_pct}%</span>
                      </span>
                      <span className="text-xs font-mono px-2 py-0.5 rounded bg-primary/20 text-primary">
                        {remaining >= 3600 ? `${Math.floor(remaining/3600)}h${String(Math.floor((remaining%3600)/60)).padStart(2,'0')}m` : remaining >= 60 ? `${Math.floor(remaining/60)}m${String(remaining%60).padStart(2,'0')}s` : `${remaining}s`} restantes
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${pos.simulation_duration ? Math.min(100, ((pos.simulation_duration - remaining) / pos.simulation_duration) * 100) : 0}%`,
                          backgroundColor: primaryColor,
                        }}
                      />
                    </div>
                    <button
                      onClick={() => handleStop(pos.id)}
                      className="w-full py-1.5 rounded text-xs font-bold bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid={`pilot-stop-${pos.id}`}
                    >
                      Arrêter
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground w-16">Gain :</label>
                      <Select
                        value={String(params.target_pct)}
                        onValueChange={(v) => setParam(pos.id, 'target_pct', parseFloat(v))}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1" data-testid={`pilot-target-${pos.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 8, 10, 15, 20].map(v => (
                            <SelectItem key={v} value={String(v)}>+{v}%</SelectItem>
                          ))}
                          {[-1, -2, -3, -5, -10].map(v => (
                            <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground w-16">Durée :</label>
                      <Select
                        value={String(params.duration)}
                        onValueChange={(v) => setParam(pos.id, 'duration', parseInt(v))}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1" data-testid={`pilot-duration-${pos.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 secondes</SelectItem>
                          <SelectItem value="60">1 minute</SelectItem>
                          <SelectItem value="120">2 minutes</SelectItem>
                          <SelectItem value="300">5 minutes</SelectItem>
                          <SelectItem value="600">10 minutes</SelectItem>
                          <SelectItem value="1200">20 minutes</SelectItem>
                          <SelectItem value="1800">30 minutes</SelectItem>
                          <SelectItem value="2700">45 minutes</SelectItem>
                          <SelectItem value="3600">1 heure</SelectItem>
                          <SelectItem value="7200">2 heures</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      onClick={() => handleAutoStart(pos)}
                      className="w-full py-1.5 rounded text-xs font-bold text-white"
                      style={{ backgroundColor: primaryColor }}
                      data-testid={`pilot-auto-start-${pos.id}`}
                    >
                      Lancer la simulation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Guide Méthode 3 */}
      <div className="p-4 rounded-lg border border-border/50 bg-secondary/20 space-y-3 mt-4">
        <p className="font-semibold text-sm">Guide d'utilisation — Méthode 3 (Pilotage)</p>
        <div className="text-xs text-muted-foreground space-y-2">
          <p><strong>Mode Manuel :</strong> Utilisez les boutons +/- pour ajuster le prix affiché au client en temps réel. Le client voit instantanément le prix changer dans sa salle des marchés.</p>
          <p><strong>Mode Automatique :</strong> Sélectionnez un gain cible (ex: +4%) et une durée (ex: 1 minute). Le prix montera et descendra de façon réaliste, puis arrivera au gain cible à la fin du timer. Le client peut vendre à tout moment.</p>
          <p><strong>Après la fin du timer :</strong> Le prix continue de bouger légèrement autour du gain cible. Le client a le temps d'appuyer sur Vendre.</p>
          <p><strong>Fermer la position :</strong> Vous pouvez fermer une position depuis cet écran. Le gain/perte sera calculé sur le prix simulé et crédité au portefeuille du client.</p>
          <p><strong>Priorité :</strong> La Méthode 3 prend le dessus sur la Méthode 4. Si vous pilotez une position, le ratio automatique ne s'applique pas.</p>
        </div>
      </div>
    </div>
  );
}

// ==================== METHOD 4: TRADING BIAS COMPONENT ====================
function Method4Bias({ clientId, token, primaryColor }) {
  const [bias, setBias] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/trading/bias/${clientId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setBias(res.data?.bias ?? 0)).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, token]);

  const handleSave = async (newBias) => {
    setSaving(true);
    try {
      await axios.put(`${API}/trading/bias/${clientId}`, { bias: newBias }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBias(newBias);
    } catch {} finally { setSaving(false); }
  };

  const biasLabel = (v) => {
    if (v === 0) return 'Neutre';
    if (v > 0) return `+${v} (favorable)`;
    return `${v} (défavorable)`;
  };

  const biasColor = (v) => {
    if (v === 0) return 'text-muted-foreground';
    if (v > 0) return 'text-green-500';
    return 'text-red-500';
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="method4-bias">
      {/* Current setting */}
      <div className="p-5 rounded-lg border bg-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-semibold text-lg">Ratio automatique</p>
            <p className="text-sm text-muted-foreground">
              Ce paramètre influence automatiquement toutes les positions du client
            </p>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold font-mono ${biasColor(bias)}`}>{bias > 0 ? '+' : ''}{bias}</p>
            <p className={`text-xs ${biasColor(bias)}`}>{biasLabel(bias)}</p>
          </div>
        </div>

        {/* Slider visual */}
        <div className="space-y-3">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span className="text-red-500">Perte -10</span>
            <span>Neutre 0</span>
            <span className="text-green-500">Gain +10</span>
          </div>
          <input
            type="range"
            min={-10}
            max={10}
            value={bias}
            onChange={(e) => handleSave(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${(bias + 10) / 20 * 100 - 2}%, ${primaryColor} ${(bias + 10) / 20 * 100}%, #22c55e ${(bias + 10) / 20 * 100 + 2}%, #22c55e 100%)`,
            }}
            data-testid="bias-slider"
            disabled={saving}
          />
          {/* Quick select buttons */}
          <div className="flex gap-1 flex-wrap">
            {[-10,-5,-3,-1,0,1,3,5,10].map(v => (
              <button
                key={v}
                onClick={() => handleSave(v)}
                className={`px-3 py-1 text-xs rounded border transition-colors ${
                  bias === v ? 'border-primary bg-primary/20 text-primary font-bold' : 'border-border/50 text-muted-foreground hover:border-primary/50'
                }`}
                data-testid={`bias-btn-${v}`}
              >
                {v > 0 ? '+' : ''}{v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Guide */}
      <div className="p-4 rounded-lg border border-border/50 bg-secondary/20 space-y-3">
        <p className="font-semibold text-sm">Guide d'utilisation — Méthode 4</p>
        <div className="text-xs text-muted-foreground space-y-2">
          <p><strong>0 (Neutre) :</strong> Le prix suit le marché réel avec de légères fluctuations naturelles. Pas de biais, le client peut gagner ou perdre selon le marché.</p>
          <p><strong>+1 à +10 (Favorable) :</strong> Le prix dérive progressivement en faveur du client. Plus la valeur est élevée, plus le client gagne facilement. Exemple : +5 fait gagner environ 0.5% de plus par minute qu'en conditions normales.</p>
          <p><strong>-1 à -10 (Défavorable) :</strong> Le prix dérive contre le client. Plus la valeur est basse, plus le client perd. Utile pour simuler des conditions de marché difficiles.</p>
          <p><strong>Important :</strong> Ce réglage s'applique automatiquement à TOUTES les positions que le client ouvre. Il reste actif jusqu'à modification. Si la Méthode 3 (Pilotage) est utilisée sur une position, elle prend le dessus.</p>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export function AdminClientDetailInner({ clientIdProp, inWorkspace, onDirtyChange, onClientNameResolved, onBack }) {
  const { clientId: routeClientId } = useParams();
  const clientId = clientIdProp || routeClientId;
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user, enterMirrorMode } = useAuth();
  const { currentTemplate } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [tradePositions, setTradePositions] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [kycDocs, setKycDocs] = useState([]);
  const [clientKycRequirements, setClientKycRequirements] = useState([]);
  const [portfolioTransfers, setPortfolioTransfers] = useState([]);
  const [tradingPortfolioEnabled, setTradingPortfolioEnabled] = useState({ enabled: false, source: 'global' });
  
  // Active tab - use URL param only when standalone (not in workspace)
  const [activeTab, setActiveTab] = useState(inWorkspace ? 'profile' : (searchParams.get('tab') || 'profile'));
  const [statementStartDate, setStatementStartDate] = useState('');
  const [statementEndDate, setStatementEndDate] = useState('');
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementList, setStatementList] = useState([]);

  const fetchStatements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kyc/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const docs = Array.isArray(res.data) ? res.data : [];
      setStatementList(docs.filter(d => d.document_type === 'account_statement'));
    } catch {}
  }, [clientId, token]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);
  
  // Update tab when URL changes (only when standalone)
  useEffect(() => {
    if (inWorkspace) return;
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, inWorkspace]);
  
  // Fetch portfolio transfers
  const fetchPortfolioTransfers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio/transfers?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolioTransfers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch portfolio transfers:', error);
    }
  }, [clientId, token]);
  
  // Fetch trading portfolio status
  const fetchTradingPortfolioStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio/trading-enabled/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTradingPortfolioEnabled(response.data);
    } catch (error) {
      console.error('Failed to fetch trading portfolio status:', error);
    }
  }, [clientId, token]);
  
  // Fetch transfers and trading status when component mounts
  useEffect(() => {
    if (clientId && token) {
      fetchPortfolioTransfers();
      fetchTradingPortfolioStatus();
    }
  }, [clientId, token, fetchPortfolioTransfers, fetchTradingPortfolioStatus]);
  
  // Update URL when tab changes (only when standalone)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (!inWorkspace) {
      setSearchParams({ tab: newTab });
    }
  };
  
  // Transactions management
  const [transactions, setTransactions] = useState([]);
  const [placementSubscriptions, setPlacementSubscriptions] = useState([]);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [newTransaction, setNewTransaction] = useState({
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    transaction_type: 'depot',
    amount: 0,
    currency: 'EUR',
    status: 'en_attente',
    notes: '',
    visible_to_client: true
  });
  
  // Placement editing modal
  const [showPlacementEditModal, setShowPlacementEditModal] = useState(false);
  const [showCustomSavingsModal, setShowCustomSavingsModal] = useState(false);
  const [showRetroactiveModal, setShowRetroactiveModal] = useState(false);
  const [retroactiveForm, setRetroactiveForm] = useState({
    product_id: '', name: '', amount: '', rentability_rate: '', rentability_variable: '',
    start_date: '', duration_months: 12, rentability_period: 'Mensuel',
    interest_period: 'Mensuel', interest_compound: false,
    interest_payout_target: 'livret', fund_availability: 'immediate',
    fund_guarantee: 'total', contract_option: 'none', contract_file: null,
    movements: []
  });
  const [showSavingsGuide, setShowSavingsGuide] = useState(false);
  const [showTradingGuide, setShowTradingGuide] = useState(false);
  const [livretTransferModal, setLivretTransferModal] = useState({ open: false, type: '', sub: null, amount: '', loading: false });
  const [customSavingsForm, setCustomSavingsForm] = useState({
    name: '', amount: '', rentability_rate: '', rentability_variable: '',
    start_date: new Date().toISOString().slice(0, 10), end_date: '',
    rentability_period: 'Mensuel', interest_period: 'Mensuel',
    fund_availability: 'immediate', fund_guarantee: 'total',
    terms_conditions: '', interest_compound: false, interest_payout_target: 'available',
    duration_months: 12
  });
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [placementEditData, setPlacementEditData] = useState({
    description: '',
    amount: 0,
    status: 'pending',
    start_date: '',
    end_date: '',
    custom_terms: '',
    apply_to_capital: false
  });
  
  // Client management dialogs
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [customPin, setCustomPin] = useState('');
  const [forcePasswordChange, setForcePasswordChange] = useState(true);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [generatedPin, setGeneratedPin] = useState('');
  const [clientCredentials, setClientCredentials] = useState(null);
  const [newNote, setNewNote] = useState({ content: '', is_private: false });
  const [newAppointment, setNewAppointment] = useState({ 
    title: '', 
    date: '', 
    time: '', 
    notes: '' 
  });
  
  // Crypto Wallets management
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [walletRequests, setWalletRequests] = useState([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showProcessRequestModal, setShowProcessRequestModal] = useState(false);
  const [selectedWalletRequest, setSelectedWalletRequest] = useState(null);
  const [newWallet, setNewWallet] = useState({
    currency_code: '',
    wallet_address: '',
    qr_code_url: ''
  });
  const [walletApprovalData, setWalletApprovalData] = useState({
    wallet_address: '',
    qr_code_url: ''
  });
  
  // Crypto Portfolio (new)
  const [clientCrypto, setClientCrypto] = useState(null);
  const [loadingCrypto, setLoadingCrypto] = useState(false);
  const [assignWalletDialog, setAssignWalletDialog] = useState(false);
  const [assignWalletData, setAssignWalletData] = useState({ id: '', address: '', network: '' });
  const [creditCryptoDialog, setCreditCryptoDialog] = useState(false);
  const [creditCryptoForm, setCreditCryptoForm] = useState({ symbol: '', amount_eur: '', use_funds: true });
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [clientFundsAvailable, setClientFundsAvailable] = useState(0);
  const [needsWallet, setNeedsWallet] = useState(false);
  const [inlineWalletAddr, setInlineWalletAddr] = useState('');
  const [inlineWalletQr, setInlineWalletQr] = useState('');

  // Stock Portfolio management
  const [stockHoldings, setStockHoldings] = useState([]);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [stockPricesUS, setStockPricesUS] = useState([]);
  const [stockPricesEU, setStockPricesEU] = useState([]);
  const [stockEurRate, setStockEurRate] = useState(1.08);
  const [creditStockDialog, setCreditStockDialog] = useState(false);
  const [debitStockDialog, setDebitStockDialog] = useState(false);
  const [editStockTxnDialog, setEditStockTxnDialog] = useState(false);
  const [editStockTxn, setEditStockTxn] = useState(null);
  const [stockCategory, setStockCategory] = useState('bourse');
  const [creditStockForm, setCreditStockForm] = useState({ symbol: '', name: '', quantity: '', buy_price: '', use_current: false, use_funds: true, date: '' });
  const [debitStockForm, setDebitStockForm] = useState({ symbol: '', quantity: '', sell_price: '', date: '' });
  const [sellStockDialog, setSellStockDialog] = useState(false);
  const [sellStockForm, setSellStockForm] = useState({ symbol: '', name: '', category: '', quantity: '', max_qty: 0 });
  const [stockClientFunds, setStockClientFunds] = useState(0);

  const fetchStockPortfolio = useCallback(async () => {
    setLoadingStocks(true);
    try {
      const [holdingsRes, txnRes, stockListRes, usRes, euRes, fundsRes] = await Promise.all([
        axios.get(`${API}/stocks/portfolio/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stocks/transactions/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/market/stocks-list`).catch(() => null),
        axios.get(`${API}/market/stocks`).catch(() => null),
        axios.get(`${API}/market/euronext`).catch(() => null),
        axios.get(`${API}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      setStockHoldings(holdingsRes.data.holdings || []);
      setStockTransactions(txnRes.data || []);
      const rate = usRes?.data?.eur_rate || 1.08;
      setStockEurRate(rate);

      // Use live prices if available, otherwise use static list (always available)
      const liveUS = (usRes?.data?.prices || []).map(p => ({ ...p, priceEur: p.price / rate }));
      const liveEU = euRes?.data?.prices || [];
      const staticUS = (stockListRes?.data?.us || []).map(s => ({ ...s, price: 0, priceEur: 0 }));
      const staticEU = (stockListRes?.data?.eu || []).map(s => ({ ...s, price: 0 }));

      // Merge: live prices take priority, static fills gaps
      const mergedUS = liveUS.length > 0 ? liveUS : staticUS;
      const mergedEU = liveEU.length > 0 ? liveEU : staticEU;
      setStockPricesUS(mergedUS);
      setStockPricesEU(mergedEU);

      const portfolios = fundsRes.data?.portfolios || [];
      const avail = portfolios.filter(p => p.portfolio_type === 'available').reduce((s, p) => s + (p.available || 0), 0);
      setStockClientFunds(avail);
    } catch (err) { console.error('Error fetching stock portfolio', err); }
    finally { setLoadingStocks(false); }
  }, [clientId, token]);

  const getStockCurrentPrice = (symbol, category) => {
    if (category === 'euronext') {
      const p = stockPricesEU.find(s => s.symbol === symbol);
      return p?.price || 0;
    }
    const p = stockPricesUS.find(s => s.symbol === symbol);
    return p?.priceEur || 0;
  };

  const handleCreditStock = async () => {
    const { symbol, name, quantity, buy_price, use_current, use_funds, date } = creditStockForm;
    if (!symbol || !quantity || parseFloat(quantity) <= 0) { toast.error('Données invalides'); return; }
    const price = use_current ? getStockCurrentPrice(symbol, stockCategory) : parseFloat(buy_price);
    if (price <= 0 && !use_current) { toast.error('Prix invalide'); return; }
    try {
      await axios.post(`${API}/stocks/credit`, {
        client_id: clientId, symbol, name, category: stockCategory,
        quantity: parseFloat(quantity), buy_price: price, use_funds: use_funds, date: date || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${quantity} ${symbol} crédité(s)${use_funds ? ' — fonds déduits' : ''}`);
      setCreditStockDialog(false);
      fetchStockPortfolio();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDebitStock = async () => {
    const { symbol, quantity, sell_price, date } = debitStockForm;
    if (!symbol || !quantity || parseFloat(quantity) <= 0) { toast.error('Données invalides'); return; }
    try {
      await axios.post(`${API}/stocks/debit`, {
        client_id: clientId, symbol,
        quantity: parseFloat(quantity), sell_price: parseFloat(sell_price) || 0,
        date: date || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${quantity} ${symbol} débité(s)`);
      setDebitStockDialog(false);
      fetchStockPortfolio();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEditStockTxn = async () => {
    if (!editStockTxn) return;
    try {
      await axios.put(`${API}/stocks/transaction/${editStockTxn.id}`, {
        date: editStockTxn.date, price: editStockTxn.price,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction mise à jour');
      setEditStockTxnDialog(false);
      fetchStockPortfolio();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleSellStock = async () => {
    const { symbol, quantity, category } = sellStockForm;
    if (!symbol || !quantity || parseFloat(quantity) <= 0) { toast.error('Données invalides'); return; }
    const sellPrice = getStockCurrentPrice(symbol, category);
    if (sellPrice <= 0) { toast.error('Cours du marché indisponible'); return; }
    try {
      await axios.post(`${API}/stocks/sell`, {
        client_id: clientId, symbol, quantity: parseFloat(quantity), sell_price: sellPrice,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${quantity} ${symbol} vendu(s) — +${(parseFloat(quantity) * sellPrice).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € crédités`);
      setSellStockDialog(false);
      fetchStockPortfolio();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const openCreditCryptoDialog = async () => {
    setCreditCryptoForm({ symbol: '', amount_eur: '', use_funds: true });
    setNeedsWallet(false); setInlineWalletAddr(''); setInlineWalletQr('');
    setCreditCryptoDialog(true);
    try {
      const [pricesRes, fundsRes] = await Promise.all([
        axios.get(`${API}/crypto/prices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      setCryptoPrices(pricesRes.data || []);
      const portfolios = fundsRes.data?.portfolios || [];
      const avail = portfolios.filter(p => p.portfolio_type === 'available').reduce((s, p) => s + (p.available || 0), 0);
      setClientFundsAvailable(avail);
    } catch {}
  };

  const checkWalletNeeded = (symbol) => {
    if (!clientCrypto) { setNeedsWallet(false); return; }
    const hasWallet = (clientCrypto.wallets || []).some(w => w.currency_code === symbol);
    setNeedsWallet(!hasWallet);
    setInlineWalletAddr(''); setInlineWalletQr('');
  };

  const getCryptoPrice = (symbol) => {
    const p = cryptoPrices.find(c => c.symbol === symbol);
    return p?.current_price || 0;
  };

  // Advances
  const [advances, setAdvances] = useState([]);
  const [advanceDialog, setAdvanceDialog] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), due_date: '', penalty_rate: '', penalty_period: 'month' });
  const [editAdvanceId, setEditAdvanceId] = useState(null);
  const [repayDialog, setRepayDialog] = useState(false);
  const [repayAdvance, setRepayAdvance] = useState(null);
  const [repayAmount, setRepayAmount] = useState('');

  const fetchAdvances = async () => {
    try {
      const res = await axios.get(`${API}/advances?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAdvances(res.data || []);
    } catch {}
  };

  const handleCreateAdvance = async () => {
    if (!advanceForm.amount) { toast.error('Montant requis'); return; }
    try {
      if (editAdvanceId) {
        await axios.put(`${API}/advances/${editAdvanceId}`, { action: 'edit', amount: parseFloat(advanceForm.amount), date: advanceForm.date, due_date: advanceForm.due_date, penalty_rate: parseFloat(advanceForm.penalty_rate) || 0, penalty_period: advanceForm.penalty_period }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Avance modifiée');
      } else {
        await axios.post(`${API}/advances`, { client_id: clientId, amount: parseFloat(advanceForm.amount), date: advanceForm.date, due_date: advanceForm.due_date, penalty_rate: parseFloat(advanceForm.penalty_rate) || 0, penalty_period: advanceForm.penalty_period }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Avance de fonds créée');
      }
      setAdvanceDialog(false); setEditAdvanceId(null);
      setAdvanceForm({ amount: '', date: new Date().toISOString().slice(0, 10) });
      fetchAdvances();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleApproveAdvance = async (id) => {
    try {
      await axios.put(`${API}/advances/${id}`, { action: 'approve' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Avance validée et créditée');
      fetchAdvances(); fetchClientData(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleRepayAdvance = async () => {
    if (!repayAmount || !repayAdvance) return;
    try {
      await axios.put(`${API}/advances/${repayAdvance.id}`, { action: 'repayment', amount: parseFloat(repayAmount) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Remboursement enregistré');
      setRepayDialog(false); fetchAdvances(); fetchClientData(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteAdvance = async (id) => {
    if (!window.confirm('Supprimer cette avance ?')) return;
    try { await axios.delete(`${API}/advances/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Supprimée'); fetchAdvances(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Mandates
  const [mandates, setMandates] = useState([]);
  const [mandateDialog, setMandateDialog] = useState(false);

  const fetchMandates = async () => {
    try { const r = await axios.get(`${API}/mandates?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` } }); setMandates(r.data || []); } catch {}
  };

  const handleCreateMandate = async () => {
    try { await axios.post(`${API}/mandates`, { client_id: clientId }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Mandat créé'); setMandateDialog(false); fetchMandates(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleApproveMandate = async (id) => {
    try { await axios.put(`${API}/mandates/${id}`, { action: 'approve' }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Mandat validé'); fetchMandates(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteMandate = async (id) => {
    if (!window.confirm('Supprimer ce mandat ?')) return;
    try { await axios.delete(`${API}/mandates/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Supprimé'); fetchMandates(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCreditCrypto = async () => {
    if (!creditCryptoForm.symbol || !creditCryptoForm.amount_eur) { toast.error('Remplir tous les champs'); return; }
    const amt = parseFloat(creditCryptoForm.amount_eur);
    if (creditCryptoForm.use_funds && amt > clientFundsAvailable) { toast.error(`Fonds insuffisants (${clientFundsAvailable.toLocaleString('fr-FR')} € disponibles)`); return; }
    if (needsWallet && !inlineWalletAddr) { toast.error('Adresse wallet requise'); return; }

    try {
      // Create wallet if needed
      if (needsWallet && inlineWalletAddr) {
        await axios.post(`${API}/crypto/wallet-request`, { symbol: creditCryptoForm.symbol }, { headers: { Authorization: `Bearer ${token}`, 'X-Mirror-Client-Id': clientId } }).catch(() => {});
        // Find the pending request and approve it
        const reqs = await axios.get(`${API}/crypto/requests/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }));
        const pending = (reqs.data || []).find(r => r.currency_code === creditCryptoForm.symbol && r.status === 'pending');
        if (pending) {
          await axios.put(`${API}/crypto/requests/${pending.id}`, { action: 'approve', wallet_address: inlineWalletAddr, qr_code_url: inlineWalletQr }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }

      const res = await axios.post(`${API}/crypto/admin/credit`, {
        client_id: clientId,
        symbol: creditCryptoForm.symbol,
        amount_eur: amt,
        use_funds: creditCryptoForm.use_funds,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setCreditCryptoDialog(false);
      setCreditCryptoForm({ symbol: '', amount_eur: '', use_funds: true });
      setNeedsWallet(false);
      fetchClientCrypto();
      fetchClientData(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const fetchClientCrypto = async () => {
    if (!clientId) return;
    setLoadingCrypto(true);
    try {
      const res = await axios.get(`${API}/crypto/client/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      setClientCrypto(res.data);
    } catch (err) { console.error('Crypto fetch error:', err); }
    finally { setLoadingCrypto(false); }
  };

  const handleAssignWallet = async () => {
    if (!assignWalletData.address) { toast.error('Adresse requise'); return; }
    try {
      await axios.put(`${API}/crypto/requests/${assignWalletData.id}`, {
        action: 'approve',
        wallet_address: assignWalletData.address,
        qr_code_url: assignWalletData.qr_code_url || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Wallet assigné !');
      setAssignWalletDialog(false);
      fetchClientCrypto();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  
  // Connection/Session tracking
  const [userSessions, setUserSessions] = useState([]);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [isClientOnline, setIsClientOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Fund Requests (pending deposits/withdrawals)
  const [fundRequests, setFundRequests] = useState([]);
  const [loadingFundRequests, setLoadingFundRequests] = useState(false);
  
  // IBAN Management
  const [ibanRequests, setIbanRequests] = useState([]);
  const [editingIbanId, setEditingIbanId] = useState(null);
  const [loadingIban, setLoadingIban] = useState(false);
  const [ibanMode, setIbanMode] = useState('analyst');
  const [poolIbans, setPoolIbans] = useState([]);
  const [clientPoolIbans, setClientPoolIbans] = useState([]);
  // Placement Access Control
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [hiddenPlacements, setHiddenPlacements] = useState([]);
  const [placementPasswords, setPlacementPasswords] = useState({});
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [categoryPasswords, setCategoryPasswords] = useState({});
  const [ibanValidationData, setIbanValidationData] = useState({
    beneficiary_name: '',
    iban: '',
    swift: '',
    bank_name: '',
    address: '',
    postal_code: '',
    city: '',
    transfer_reference: '',
    logo_base64: ''
  });
  const [selectedIbanRequest, setSelectedIbanRequest] = useState(null);
  const [showCreateIbanForm, setShowCreateIbanForm] = useState(false);
  
  // Auto Trading Config - Start with empty values so user must fill them
  const [autoTradingConfig, setAutoTradingConfig] = useState({
    enabled: false,
    amount: '',
    positions_count: 0,
    winning_positions: '',
    losing_positions: '',
    start_date: '',
    end_date: '',
    category: 'crypto',
    categories: [],
    target_profit: '',
    target_profit_percent: '',
    profit_mode: 'euro',
    frequency: 'daily',
    trading_hour_start: '08:30',
    trading_hour_end: '20:00',
  });
  const [tradingMethod, setTradingMethod] = useState('method1');
  const [compoundConfig, setCompoundConfig] = useState({
    daily_rate: '1.16',
    min_positions: 1,
    max_positions: 4,
    trading_hour_start: '08:30',
    trading_hour_end: '20:00',
    categories: ['crypto'],
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
  });
  const [compoundPreview, setCompoundPreview] = useState(null);
  const [executingTrade, setExecutingTrade] = useState(false);
  const [simulationPreview, setSimulationPreview] = useState([]);
  const [simulationTotalProfit, setSimulationTotalProfit] = useState(0);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulationParams, setSimulationParams] = useState(null);

  const quickRangeOptions = [
    { label: '2 jours', days: 2 },
    { label: '4 jours', days: 4 },
    { label: '1 semaine', days: 7 },
    { label: '2 semaines', days: 14 },
    { label: '3 semaines', days: 21 },
    { label: '1 mois', months: 1 },
    { label: '3 mois', months: 3 },
    { label: '6 mois', months: 6 }
  ];
  const [simulationDateRange, setSimulationDateRange] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  
  // Trade positions pagination and filtering
  const [positionsPage, setPositionsPage] = useState(1);
  const [positionsTotalPages, setPositionsTotalPages] = useState(1);
  const [positionsStatusFilter, setPositionsStatusFilter] = useState(''); // '', 'pending', 'visible'
  const [positionsSummary, setPositionsSummary] = useState({ pending: 0, visible: 0, all: 0 });
  const [positionsTotalProfit, setPositionsTotalProfit] = useState(0);
  
  // Bulk selection for positions
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';
  const accentColor = currentTemplate?.colors?.accent || '#10B981';
  
  // Transaction types and statuses
  const transactionTypes = [
    { value: 'depot', label: 'Dépôt', isCredit: true },
    { value: 'retrait', label: 'Retrait', isCredit: false },
    { value: 'bonus', label: 'Bonus', isCredit: true },
    { value: 'interet', label: 'Intérêt', isCredit: true },
    { value: 'vente', label: 'Vente', isCredit: true },
    { value: 'credit', label: 'Crédit', isCredit: true },
    { value: 'achat', label: 'Achat', isCredit: false },
    { value: 'souscription', label: 'Souscription', isCredit: false },
    { value: 'frais', label: 'Frais', isCredit: false },
    { value: 'perte', label: 'Perte', isCredit: false },
    { value: 'trade', label: 'Trade', isCredit: true },
    { value: 'correction', label: 'Correction', isCredit: true },
    { value: 'transfert', label: 'Transfert', isCredit: false },
  ];
  
  const transactionStatuses = [
    { value: 'en_verification', label: 'En vérification' },
    { value: 'en_attente', label: 'En attente' },
    { value: 'a_payer', label: 'A payer' },
    { value: 'payee', label: 'Payée' },
    { value: 'valide', label: 'Validé' },
    { value: 'validee', label: 'Validée' },
    { value: 'confirme', label: 'Confirmé' },
    { value: 'en_cours', label: 'En cours' },
    { value: 'cloture', label: 'Clôturé' },
    { value: 'vendu', label: 'Vendu' },
    { value: 'termine', label: 'Terminé' },
    { value: 'conteste', label: 'Contesté' },
    { value: 'annule', label: 'Annulé' },
  ];

  const placementStatuses = [
    { value: 'pending', label: 'En attente' },
    { value: 'active', label: 'Actif' },
    { value: 'cancelled', label: 'Annulé' },
  ];

  const normalizePlacementStatus = (status) => {
    if (!status) return 'pending';
    const normalized = String(status).toLowerCase().trim();
    if (['pending', 'en_attente', 'en attente', 'en-attente'].includes(normalized)) {
      return 'pending';
    }
    if (['active', 'actif'].includes(normalized)) {
      return 'active';
    }
    if (['cancelled', 'annule', 'annulé'].includes(normalized)) {
      return 'cancelled';
    }
    return 'pending';
  };

  // Helper function to parse number on blur (final value) - supports both . and , for decimals
  const parseNumberOnBlur = (value, isInteger = false) => {
    if (!value && value !== 0) return 0;
    // Replace comma with dot for parsing
    const normalized = String(value).replace(',', '.');
    return isInteger ? parseInt(normalized) || 0 : parseFloat(normalized) || 0;
  };

  // Helper function to select all text on focus
  const handleInputFocus = (e) => {
    e.target.select();
  };

  // Calculate profit from percentage or vice versa
  const calculateProfitFromPercent = (percent, amount) => {
    return (parseFloat(String(percent).replace(',', '.')) || 0) * (parseFloat(String(amount).replace(',', '.')) || 0) / 100;
  };

  const calculatePercentFromProfit = (profit, amount) => {
    const amt = parseFloat(String(amount).replace(',', '.')) || 0;
    if (amt === 0) return 0;
    return ((parseFloat(String(profit).replace(',', '.')) || 0) / amt) * 100;
  };

  const fetchClientData = useCallback(async (isRefresh = false) => {
    try {
      const [clientRes, auditRes, tradingConfigRes, transactionsRes, analystsRes, kycRes, kycRequirementsRes, credentialsRes, placementsRes, walletsRes, walletRequestsRes, currenciesRes, officesRes, advancesRes, mandatesRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch((err) => { 
          console.error('Failed to fetch client:', err); 
          return null; 
        }),
        axios.get(`${API}/audit?user_id=${clientId}&limit=50`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/auto-trading/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: null })),
        axios.get(`${API}/transactions/${clientId}?limit=10000`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/kyc/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/kyc-requirements/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/clients/${clientId}/credentials`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: null })),
        axios.get(`${API}/placements/subscriptions/client/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/wallets/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/requests/${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/currencies`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/advances?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] })),
        axios.get(`${API}/mandates?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: [] }))
      ]);
      
      // Only navigate away if this is initial load AND client fetch failed
      if (!clientRes?.data) {
        if (!isRefresh) {
          toast.error(t('error'), { description: 'Client non trouvé' });
          if (onBack) { onBack(); } else { navigate('/admin/clients'); }
        }
        return;
      }
      
      setClient(clientRes.data);
      // Pre-fill password/PIN from the role-gated credentials endpoint
      const visiblePwd = credentialsRes?.data?.client_visible_password || credentialsRes?.data?.last_provisional_password || '';
      setCustomPassword(visiblePwd);
      setCustomPin(credentialsRes?.data?.last_provisional_pin || '');
      setAuditLogs(auditRes.data || []);
      if (tradingConfigRes.data) {
        // Only load 'enabled' and 'categories' from saved config
        // Keep other fields empty so user must fill them each time
        setAutoTradingConfig(prev => ({
          ...prev,
          enabled: tradingConfigRes.data.enabled || false,
          categories: tradingConfigRes.data.categories || [],
          category: tradingConfigRes.data.category || 'crypto'
        }));
      }
      setTransactions(transactionsRes.data?.transactions || []);
      setAnalysts(analystsRes.data || []);
      setOffices(Array.isArray(officesRes.data) ? officesRes.data : []);
      setKycDocs(kycRes.data || []);
      setClientKycRequirements(kycRequirementsRes.data?.documents || []);
      setClientCredentials(credentialsRes.data);
      setPlacementSubscriptions(placementsRes.data || []);
      setCryptoWallets(walletsRes.data || []);
      setWalletRequests(walletRequestsRes.data || []);
      setSupportedCurrencies(currenciesRes.data || []);
      setAdvances(advancesRes.data || []);
      setMandates(mandatesRes.data || []);
    } catch (error) {
      console.error('fetchClientData error:', error);
      // Only navigate away if this is initial load, not a refresh
      if (!isRefresh) {
        toast.error(t('error'), { description: 'Client non trouvé' });
        if (onBack) { onBack(); } else { navigate('/admin/clients'); }
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, token, navigate, t]);

  // Fetch trade positions with pagination
  const fetchTradePositions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', positionsPage);
      params.append('limit', 50);
      if (positionsStatusFilter) {
        params.append('status_filter', positionsStatusFilter);
      }
      
      const response = await axios.get(`${API}/trade-positions/${clientId}/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const positions = response.data.positions || [];
      const sortedPositions = [...positions].sort((a, b) => {
        // Pending positions first, then visible, then closed
        const statusOrder = { pending: 0, visible: 1, closed: 2 };
        const sa = statusOrder[a?.status] ?? 1;
        const sb = statusOrder[b?.status] ?? 1;
        if (sa !== sb) return sa - sb;
        // For closed positions: most recent first (descending)
        // For pending/visible: soonest first (ascending)
        const getTs = (p) => {
          if (p?.close_at) { const t = new Date(p.close_at).getTime(); if (!isNaN(t)) return t; }
          if (p?.closed_at) { const t = new Date(p.closed_at).getTime(); if (!isNaN(t)) return t; }
          if (p?.scheduled_date) { const t = new Date(p.scheduled_date).getTime(); if (!isNaN(t)) return t; }
          if (p?.created_at) { const t = new Date(p.created_at).getTime(); if (!isNaN(t)) return t; }
          return 0;
        };
        if (a?.status === 'closed') return getTs(b) - getTs(a); // Newest first for closed
        return getTs(a) - getTs(b); // Soonest first for pending
      });
      setTradePositions(sortedPositions);
      setPositionsTotalPages(response.data.total_pages || 1);
      setPositionsSummary(response.data.summary || { pending: 0, visible: 0, all: 0, pending_profit: 0, closed_profit: 0 });
      setPositionsTotalProfit(response.data.all_profit || response.data.total_profit || 0);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
      setTradePositions([]);
    }
  }, [clientId, token, positionsPage, positionsStatusFilter]);

  // Fetch user sessions and navigation history
  const fetchSessionsAndNavigation = useCallback(async () => {
    setLoadingSessions(true);
    try {
      // Fetch sessions
      const sessionsRes = await axios.get(`${API}/users/${clientId}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSessions(sessionsRes.data.sessions || []);
      setIsClientOnline(sessionsRes.data.is_online || false);
      setLastSeen(sessionsRes.data.last_seen);

      // Fetch navigation history
      const navRes = await axios.get(`${API}/users/${clientId}/navigation-history?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNavigationHistory(navRes.data.page_views || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  }, [clientId, token]);

  useEffect(() => {
    fetchClientData();
    // Clean up mirrorClientId after returning from mirror mode
    localStorage.removeItem('mirrorClientId');
  }, [fetchClientData]);

  useEffect(() => {
    if (clientId && token) {
      fetchTradePositions();
    }
  }, [fetchTradePositions, clientId, token]);

  // Fetch fund requests (pending deposits/withdrawals) for client
  const fetchFundRequests = useCallback(async () => {
    setLoadingFundRequests(true);
    try {
      const response = await axios.get(`${API}/funds/requests?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFundRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch fund requests:', error);
      setFundRequests([]);
    } finally {
      setLoadingFundRequests(false);
    }
  }, [clientId, token]);

  // Fetch fund requests when transactions tab is opened or initially
  useEffect(() => {
    if (activeTab === 'transactions' && clientId && token) {
      fetchFundRequests();
    }
  }, [activeTab, clientId, token, fetchFundRequests]);

  // Fetch IBAN requests for client
  const fetchIbanRequests = useCallback(async () => {
    setLoadingIban(true);
    try {
      const [ibanRes, modeRes] = await Promise.all([
        axios.get(`${API}/iban-requests/client/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/iban-pool/mode`).catch(() => ({ data: { iban_mode: 'analyst' } })),
      ]);
      setIbanRequests(ibanRes.data.ibans || []);
      setIbanMode(modeRes.data?.iban_mode || 'analyst');
      // Admins always load pool IBANs; analysts only when pool mode active
      const isAdmin = user?.role !== 'analyst';
      if (isAdmin || modeRes.data?.iban_mode === 'pool') {
        const [availRes, clientPoolRes] = await Promise.all([
          axios.get(`${API}/iban-pool/client/${clientId}/available`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/iban-pool`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        setPoolIbans(availRes.data || []);
        setClientPoolIbans((clientPoolRes.data || []).filter(i => (i.assigned_clients || []).includes(clientId)));
      }
    } catch (error) {
      console.error('Failed to fetch IBAN requests:', error);
      setIbanRequests([]);
    } finally {
      setLoadingIban(false);
    }
  }, [clientId, token]);

  const fetchPlacementAccess = useCallback(async () => {
    try {
      const [accessRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/placement-access`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/placements/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/placements/categories`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setHiddenPlacements(accessRes.data.hidden_placements || []);
      setPlacementPasswords(accessRes.data.placement_passwords || {});
      setHiddenCategories(accessRes.data.hidden_categories || []);
      setCategoryPasswords(accessRes.data.category_passwords || {});
      setAllProducts(productsRes.data || []);
      setAllCategories(categoriesRes.data || []);
    } catch (e) { console.error('Failed to fetch placement access:', e); }
  }, [clientId, token]);

  const savePlacementAccess = async () => {
    try {
      await axios.put(`${API}/clients/${clientId}/placement-access`, {
        hidden_placements: hiddenPlacements,
        placement_passwords: placementPasswords,
        hidden_categories: hiddenCategories,
        category_passwords: categoryPasswords
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Accès placements mis à jour');
    } catch { toast.error('Erreur'); }
  };



  // Handle fund request approval/rejection
  const handleProcessFundRequest = async (requestId, action) => {
    try {
      // Map action to status
      const statusMap = {
        'approve': 'approved',
        'validate': 'validated',
        'reject': 'rejected'
      };
      const status = statusMap[action] || action;
      
      await axios.put(`${API}/funds/requests/${requestId}`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const messages = {
        'approved': 'Demande approuvée',
        'validated': 'Demande validée et compte mis à jour',
        'rejected': 'Demande rejetée'
      };
      toast.success(messages[status] || 'Statut mis à jour');
      fetchFundRequests();
      fetchClientData(true); // Refresh client data to update balances
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    }
  };

  const handleValidateIban = async (ibanId) => {
    if (!ibanValidationData.beneficiary_name || !ibanValidationData.iban || !ibanValidationData.swift) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      await axios.put(`${API}/iban-requests/${ibanId}/validate`, ibanValidationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('IBAN validé avec succès');
      fetchIbanRequests();
      setSelectedIbanRequest(null);
      setIbanValidationData({ beneficiary_name: '', iban: '', swift: '', bank_name: '', address: '', postal_code: '', city: '', transfer_reference: '', logo_base64: '' });
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleCreateIban = async () => {
    if (!ibanValidationData.beneficiary_name || !ibanValidationData.iban || !ibanValidationData.swift) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      if (editingIbanId) {
        // Update existing
        await axios.put(`${API}/iban-requests/${editingIbanId}`, ibanValidationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('IBAN mis à jour');
      } else {
        // Create new
        await axios.post(`${API}/iban-requests/admin/create/${clientId}`, ibanValidationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('IBAN créé avec succès');
      }
      fetchIbanRequests();
      setShowCreateIbanForm(false);
      setEditingIbanId(null);
      setIbanValidationData({ beneficiary_name: '', iban: '', swift: '', bank_name: '', address: '', postal_code: '', city: '', transfer_reference: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setIbanValidationData(prev => ({ ...prev, logo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateIbanStatus = async (ibanId, status) => {
    try {
      await axios.put(`${API}/iban-requests/${ibanId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Statut mis à jour: ${status}`);
      fetchIbanRequests();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteIban = async (ibanId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet IBAN ?')) return;
    
    try {
      await axios.delete(`${API}/iban-requests/${ibanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('IBAN supprimé');
      fetchIbanRequests();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        first_name: client.user?.first_name,
        last_name: client.user?.last_name,
        email: client.user?.email,
        phone: client.phone_primary,
        phone_primary: client.phone_primary,
        phone_secondary: client.phone_secondary,
        date_of_birth: client.date_of_birth,
        place_of_birth: client.place_of_birth,
        nationality: client.nationality,
        address: client.address,
        postal_code: client.postal_code,
        city: client.city,
        country: client.country,
        beneficiary_name: client.beneficiary_name,
        beneficiary_relation: client.beneficiary_relation,
        beneficiaries: client.beneficiaries || [],
        profession: client.profession,
        annual_income: client.annual_income,
        source_of_funds: client.source_of_funds,
        tax_id: client.tax_id,
        client_code: client.client_code,
        account_type: client.account_type || 'particulier',
        company_name: client.company_name,
        company_siret: client.company_siret,
        company_legal_form: client.company_legal_form,
        company_address: client.company_address,
        company_city: client.company_city,
        company_postal_code: client.company_postal_code,
        company_country: client.company_country,
        company_activity: client.company_activity,
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(t('success'));
      if (onDirtyChange) onDirtyChange(false);
      fetchClientData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAutoTrading = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auto-trading/${clientId}`, autoTradingConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration du trading automatique enregistrée');
      if (onDirtyChange) onDirtyChange(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteAutoTrading = async () => {
    const config = {
      categories: autoTradingConfig.categories?.length > 0 ? autoTradingConfig.categories : [autoTradingConfig.category || 'crypto'],
      winning_positions: autoTradingConfig.winning_positions,
      losing_positions: autoTradingConfig.losing_positions,
      amount: autoTradingConfig.amount,
      target_profit: autoTradingConfig.target_profit,
      target_profit_percent: autoTradingConfig.target_profit_percent,
      profit_mode: autoTradingConfig.profit_mode,
      start_date: autoTradingConfig.start_date,
      end_date: autoTradingConfig.end_date
    };

    // Validate required fields
    const amount = parseFloat(config.amount) || 0;
    const winningPos = parseInt(config.winning_positions) || 0;
    const losingPos = parseInt(config.losing_positions) || 0;
    const targetProfit = parseFloat(config.target_profit) || 0;
    const targetProfitPercent = parseFloat(config.target_profit_percent) || 0;
    const targetProfitPercentPayload = config.profit_mode === 'percent' ? targetProfitPercent : null;
    
    if (amount <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }
    if (winningPos <= 0 && losingPos <= 0) {
      toast.error('Veuillez entrer au moins une position gagnante ou perdante');
      return;
    }
    if (!config.start_date || !config.end_date) {
      toast.error('Veuillez sélectionner les dates de début et fin');
      return;
    }
    if (targetProfit === 0 && config.profit_mode === 'euro') {
      toast.error('Veuillez entrer un objectif de profit');
      return;
    }
    if (targetProfitPercent === 0 && config.profit_mode === 'percent') {
      toast.error('Veuillez entrer un objectif de profit en %');
      return;
    }
    
    setExecutingTrade(true);
    try {
      const response = await axios.post(`${API}/auto-trading/${clientId}/execute`, {
        categories: config.categories,
        winning_positions: winningPos,
        losing_positions: losingPos,
        amount: amount,
        target_profit: targetProfit,
        target_profit_percent: targetProfitPercentPayload,
        profit_mode: config.profit_mode,
        start_date: config.start_date,
        end_date: config.end_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const winCount = response.data.positions?.filter(p => p.profit > 0).length || 0;
      const loseCount = response.data.positions?.filter(p => p.profit < 0).length || 0;
      
      toast.success(`${response.data.positions?.length || 0} positions générées !`, {
        description: `${winCount} gagnantes, ${loseCount} perdantes - Profit net: ${response.data.total_profit?.toFixed(2)}€`
      });

      setSimulationPreview([]);
      setSimulationTotalProfit(0);
      setSimulationDateRange(null);
      setSimulationParams(null);
      setShowSimulationModal(false);
      
      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de l\'exécution', {
        description: error.response?.data?.detail || 'Une erreur est survenue'
      });
    } finally {
      setExecutingTrade(false);
    }
  };

  // Confirm and save the exact previewed positions (no regeneration)
  const handleConfirmPreviewPositions = async () => {
    if (!simulationPreview || simulationPreview.length === 0) {
      toast.error('Aucune position à valider');
      return;
    }

    setExecutingTrade(true);
    try {
      const response = await axios.post(`${API}/auto-trading/${clientId}/confirm-positions`, {
        positions: simulationPreview
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const winCount = response.data.positions?.filter(p => p.profit > 0).length || 0;
      const loseCount = response.data.positions?.filter(p => p.profit < 0).length || 0;

      toast.success(`${response.data.positions_created || 0} positions créées !`, {
        description: `${winCount} gagnantes, ${loseCount} perdantes - Profit net: ${response.data.total_profit?.toFixed(2)}€`
      });

      setSimulationPreview([]);
      setSimulationTotalProfit(0);
      setSimulationDateRange(null);
      setSimulationParams(null);
      setShowSimulationModal(false);

      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la validation', {
        description: error.response?.data?.detail || 'Une erreur est survenue'
      });
    } finally {
      setExecutingTrade(false);
    }
  };

  const handleSimulateAutoTrading = async () => {
    try {
      const winningPos = parseInt(autoTradingConfig.winning_positions) || 0;
      const losingPos = parseInt(autoTradingConfig.losing_positions) || 0;
    const normalizedAmount = parseFloat(String(autoTradingConfig.amount_input || '').replace(',', '.').replace(/[^0-9.]/g, '')) || 0;
    const amount = parseFloat(autoTradingConfig.amount) || normalizedAmount;
      const targetProfit = parseFloat(autoTradingConfig.target_profit) || 0;
      const targetProfitPercent = parseFloat(autoTradingConfig.target_profit_percent) || 0;
      const targetProfitPercentPayload = autoTradingConfig.profit_mode === 'percent' ? targetProfitPercent : null;

      if (winningPos === 0 && losingPos === 0) {
        toast.error('Veuillez définir le nombre de positions');
        return;
      }

      if (amount === 0) {
        toast.error('Veuillez entrer un montant');
        return;
      }

      if (targetProfit === 0 && autoTradingConfig.profit_mode === 'euro') {
        toast.error('Veuillez entrer un objectif de profit');
        return;
      }
      if (targetProfitPercent === 0 && autoTradingConfig.profit_mode === 'percent') {
        toast.error('Veuillez entrer un objectif de profit en %');
        return;
      }

      setSimulationLoading(true);
      const response = await axios.post(`${API}/auto-trading/${clientId}/execute?simulate=true`, {
        categories: autoTradingConfig.categories?.length > 0 ? autoTradingConfig.categories : [autoTradingConfig.category || 'crypto'],
        winning_positions: winningPos,
        losing_positions: losingPos,
        amount: amount,
        target_profit: targetProfit,
        target_profit_percent: targetProfitPercentPayload,
        profit_mode: autoTradingConfig.profit_mode,
        start_date: autoTradingConfig.start_date,
        end_date: autoTradingConfig.end_date,
        trading_hour_start: autoTradingConfig.trading_hour_start || '08:30',
        trading_hour_end: autoTradingConfig.trading_hour_end || '20:00',
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      setSimulationPreview(response.data.positions_preview || []);
      setSimulationTotalProfit(response.data.total_profit || 0);
      setSimulationDateRange(response.data.date_range || null);
      setSimulationParams({
        categories: autoTradingConfig.categories?.length > 0 ? autoTradingConfig.categories : [autoTradingConfig.category || 'crypto'],
        winning_positions: winningPos,
        losing_positions: losingPos,
        amount: amount,
        target_profit: targetProfit,
        target_profit_percent: targetProfitPercentPayload,
        profit_mode: autoTradingConfig.profit_mode,
        start_date: autoTradingConfig.start_date,
        end_date: autoTradingConfig.end_date
      });
      setShowSimulationModal(true);
      toast.success('Simulation prête (aucune position créée)');
    } catch (error) {
      console.error('Error simulating auto-trading:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur lors de la simulation: ${errorMsg}`);
    } finally {
      setSimulationLoading(false);
    }
  };

  // Method 2: Compound interest simulation
  const handleCompoundSimulate = async (execute = false) => {
    setSimulationLoading(true);
    try {
      const response = await axios.post(
        `${API}/auto-trading/${clientId}/compound?simulate=${!execute}`,
        compoundConfig,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      if (execute) {
        toast.success(`${response.data.total_positions} positions créées !`);
        setCompoundPreview(null);
        fetchTradePositions();
        fetchClientData(true);
      } else {
        setCompoundPreview(response.data);
        toast.success('Simulation prête');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur simulation');
    } finally {
      setSimulationLoading(false);
    }
  };



  const applyQuickRange = (range) => {
    const startDate = new Date();
    let endDate = new Date(startDate);

    if (range.days) {
      endDate = addDays(startDate, range.days);
    }
    if (range.months) {
      endDate = addMonths(startDate, range.months);
    }

    setAutoTradingConfig(prev => ({
      ...prev,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd')
    }));
  };

  // Handle position validation
  const handleValidatePosition = async (positionId) => {
    try {
      await axios.put(`${API}/trade-positions/${positionId}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Position validée et créditée');
      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  // Handle position deletion
  const handleDeletePosition = async (positionId) => {
    if (!window.confirm('Supprimer cette position ? Si elle était créditée, le montant sera débité.')) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/trade-positions/${positionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.was_credited) {
        toast.success(`Position supprimée - ${response.data.debited}€ débités`);
      } else {
        toast.success('Position supprimée');
      }
      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur: ${errorMsg}`);
    }
  };

  const handleMirrorLogin = () => {
    window.open(`/mirror/${clientId}`, '_blank');
  };

  // Handle bulk position deletion
  const handleBulkDeletePositions = async () => {
    if (selectedPositions.length === 0) {
      toast.error('Aucune position sélectionnée');
      return;
    }
    
    if (!window.confirm(`Supprimer ${selectedPositions.length} position(s) ? Les positions créditées seront débitées.`)) {
      return;
    }
    
    setSaving(true);
    try {
      // Delete each position
      const results = await Promise.all(selectedPositions.map(posId => 
        axios.delete(`${API}/trade-positions/${posId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      
      const totalDebited = results.reduce((sum, r) => sum + (r.data.debited || 0), 0);
      if (totalDebited > 0) {
        toast.success(`${selectedPositions.length} position(s) supprimée(s) - ${totalDebited.toFixed(2)}€ débités`);
      } else {
        toast.success(`${selectedPositions.length} position(s) supprimée(s)`);
      }
      setSelectedPositions([]);
      setSelectAll(false);
      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      console.error('Bulk delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle bulk position validation
  const handleBulkValidatePositions = async () => {
    if (selectedPositions.length === 0) {
      toast.error('Aucune position sélectionnée');
      return;
    }
    
    setSaving(true);
    try {
      await Promise.all(selectedPositions.map(posId => 
        axios.put(`${API}/trade-positions/${posId}/validate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      
      toast.success(`${selectedPositions.length} position(s) validée(s) et créditée(s)`);
      setSelectedPositions([]);
      setSelectAll(false);
      fetchTradePositions();
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  // Handle opening placement edit modal
  const openPlacementEditModal = (placement) => {
    setSelectedPlacement(placement);
    const normalizedStatus = normalizePlacementStatus(placement?.status);
    setPlacementEditData({
      description: placement.product?.name || '',
      amount: placement.amount || 0,
      status: normalizedStatus,
      start_date: placement.start_date || '',
      end_date: placement.end_date || '',
      custom_terms: placement.custom_terms || placement.product?.terms_conditions || '',
      apply_to_capital: true
    });
    setShowPlacementEditModal(true);
  };

  const handleCreateCustomSavings = async () => {
    const f = customSavingsForm;
    if (!f.name || !f.amount || !f.rentability_rate) {
      toast.error('Veuillez remplir le nom, montant et taux');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/placements/subscriptions/custom`, {
        client_id: clientId,
        name: f.name,
        amount: parseFloat(f.amount),
        rentability_rate: parseFloat(f.rentability_rate),
        rentability_variable: parseFloat(f.rentability_variable) || 0,
        start_date: f.start_date,
        end_date: f.end_date,
        duration_months: f.duration_months,
        rentability_period: f.rentability_period,
        interest_period: f.interest_period,
        fund_availability: f.fund_availability,
        fund_guarantee: f.fund_guarantee,
        interest_compound: f.interest_compound,
        interest_payout_target: f.interest_payout_target || 'available',
        custom_terms: f.terms_conditions,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Épargne sur mesure créée — en attente de signature client');
      setShowCustomSavingsModal(false);
      fetchClientData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRetroactiveSavings = async () => {
    const f = retroactiveForm;
    if (!f.amount || !f.rentability_rate || !f.start_date) {
      toast.error('Veuillez remplir le montant, taux et date de début');
      return;
    }
    if (new Date(f.start_date) >= new Date()) {
      toast.error('La date de début doit être dans le passé');
      return;
    }
    setSaving(true);
    try {
      let contractFile = null;
      if (f.contract_option === 'upload' && f.contract_file) {
        const reader = new FileReader();
        contractFile = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(f.contract_file);
        });
      }
      const payload = {
        client_id: clientId,
        product_id: f.product_id || 'custom_retroactive',
        name: f.name || 'Epargne retroactive',
        amount: parseFloat(f.amount),
        rentability_rate: parseFloat(f.rentability_rate),
        rentability_variable: parseFloat(f.rentability_variable) || 0,
        start_date: f.start_date,
        duration_months: parseInt(f.duration_months) || 12,
        rentability_period: f.rentability_period,
        interest_period: f.interest_period,
        interest_compound: f.interest_compound,
        interest_payout_target: f.interest_payout_target,
        fund_availability: f.fund_availability,
        fund_guarantee: f.fund_guarantee,
        contract_option: f.contract_option,
        contract_file: contractFile,
        movements: f.movements.filter(m => m.amount && m.date).map(m => ({
          date: m.date,
          amount: parseFloat(m.amount),
          type: m.type || 'ajout_livret'
        }))
      };
      const res = await axios.post(`${API}/placements/subscriptions/retroactive`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Epargne retroactive creee ! ${res.data.interest_payments_generated} mensualite(s) generee(s)`);
      setShowRetroactiveModal(false);
      setRetroactiveForm({
        product_id: '', name: '', amount: '', rentability_rate: '', rentability_variable: '',
        start_date: '', duration_months: 12, rentability_period: 'Mensuel',
        interest_period: 'Mensuel', interest_compound: false,
        interest_payout_target: 'livret', fund_availability: 'immediate',
        fund_guarantee: 'total', contract_option: 'none', contract_file: null,
        movements: []
      });
      fetchClientData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };


  // Handle saving placement edit
  const handleSavePlacementEdit = async () => {
    if (!selectedPlacement) return;
    
    setSaving(true);
    try {
      const previousStatus = selectedPlacement.status;
      const newStatus = placementEditData.status;

      // Update subscription status
      await axios.put(`${API}/placements/subscriptions/${selectedPlacement.id}`, {
        status: newStatus,
        amount: placementEditData.amount,
        start_date: placementEditData.start_date,
        end_date: placementEditData.end_date,
        apply_to_capital: placementEditData.apply_to_capital,
        custom_terms: placementEditData.custom_terms || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Placement mis à jour');
      setShowPlacementEditModal(false);
      fetchClientData(true);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } finally {
      setSaving(false);
    }
  };

  // Toggle position selection
  const togglePositionSelection = (posId) => {
    setSelectedPositions(prev => 
      prev.includes(posId) ? prev.filter(id => id !== posId) : [...prev, posId]
    );
  };

  // Toggle select all
  const handleSelectAllPositions = () => {
    if (selectAll) {
      setSelectedPositions([]);
      setSelectAll(false);
    } else {
      setSelectedPositions(tradePositions.map(p => p.id));
      setSelectAll(true);
    }
  };

  const handleCreateTransaction = async () => {
    if (!newTransaction.description || !newTransaction.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/transactions/${clientId}`, newTransaction, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction créée avec succès');
      setShowTransactionModal(false);
      setNewTransaction({
        description: '',
        transaction_date: new Date().toISOString().split('T')[0],
        transaction_type: 'depot',
        amount: 0,
        currency: 'EUR',
        status: 'en_attente',
        notes: '',
        visible_to_client: true
      });
      fetchClientData(true); // Refresh all data
    } catch (error) {
      toast.error('Erreur lors de la création de la transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTransactionStatus = async (transactionId, newStatus) => {
    try {
      await axios.put(`${API}/transactions/${clientId}/${transactionId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };


  // Delete transaction with balance adjustment
  const handleDeleteTransaction = async (transactionId, description, amount) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer cette transaction ?\n\n"${description}" - ${amount}€\n\nLe solde du client sera ajusté en conséquence.`)) {
      return;
    }
    
    try {
      // Optimistic UI update - remove transaction immediately
      setTransactions(prev => prev.filter(tx => tx.id !== transactionId));
      
      await axios.delete(`${API}/transactions/${clientId}/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transaction supprimée');
      // Refresh to get updated balance
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la suppression de la transaction');
      // Restore on error
      fetchClientData(true);
    }
  };

  // Generate provisional password
  const handleGeneratePassword = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/clients/${clientId}/generate-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedPassword(response.data.provisional_password);
      toast.success('Mot de passe provisoire généré');
    } catch (error) {
      toast.error('Erreur lors de la génération du mot de passe');
    } finally {
      setSaving(false);
    }
  };

  // Generate provisional PIN
  const handleGeneratePin = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/clients/${clientId}/generate-pin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedPin(response.data.provisional_pin);
      toast.success('Code PIN provisoire généré');
    } catch (error) {
      toast.error('Erreur lors de la génération du PIN');
    } finally {
      setSaving(false);
    }
  };

  // Update client status (activate/deactivate)
  const handleUpdateClientStatus = async (newStatus) => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Client ${newStatus === 'active' ? 'activé' : newStatus === 'suspended' ? 'suspendu' : 'bloqué'}`);
      setShowStatusDialog(false);
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setSaving(false);
    }
  };

  // Assign analyst
  const handleAssignAnalyst = async (analystId) => {
    try {
      await axios.put(`${API}/clients/${clientId}/assign-analyst`, { analyst_id: analystId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Analyste assigné');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  // Assign office/bureau
  const handleAssignOffice = async (officeId) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, { office_id: officeId || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(officeId ? 'Bureau assigné' : 'Bureau retiré');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Veuillez saisir une note');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/notes`, { content: newNote.content }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note ajoutée');
      setShowNoteDialog(false);
      setNewNote({ content: '', is_private: false });
    } catch (error) {
      console.error('Note error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de la note');
    } finally {
      setSaving(false);
    }
    // Refresh data separately to avoid catching refresh errors
    try { await fetchClientData(true); } catch {}
  };

  // Add appointment
  const handleAddAppointment = async () => {
    if (!newAppointment.title || !newAppointment.date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/appointments`, newAppointment, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rendez-vous ajouté');
      setShowAppointmentDialog(false);
      setNewAppointment({ title: '', date: '', time: '', notes: '' });
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du rendez-vous');
    } finally {
      setSaving(false);
    }
  };

  // Review profile changes
  const handleReviewChanges = async (action) => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/review-changes`, { action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'approve' ? 'Modifications approuvées' : 'Modifications refusées');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Review KYC document
  const handleReviewKyc = async (docId, status, rejectionReason = '', identitySubtype = '') => {
    try {
      const payload = { status, rejection_reason: rejectionReason };
      if (identitySubtype) payload.identity_subtype = identitySubtype;
      const res = await axios.put(`${API}/kyc/${docId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.auto_approved_verso) {
        toast.success('Passeport validé — verso auto-validé');
      } else {
        toast.success(status === 'approved' ? 'Document validé' : 'Document refusé');
      }
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleDeleteKyc = async (docId, docName) => {
    if (!window.confirm(`Supprimer "${docName}" ? Le client devra le téléverser à nouveau.`)) return;
    try {
      await axios.delete(`${API}/kyc/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé — le client sera notifié');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLivretTransfer = async () => {
    const { type, sub, amount } = livretTransferModal;
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) { toast.error('Montant invalide'); return; }
    if (type === 'withdraw' && val > (sub?.amount || 0)) { toast.error(`Montant supérieur au solde (${sub?.amount}€)`); return; }
    setLivretTransferModal(prev => ({ ...prev, loading: true }));
    try {
      const endpoint = type === 'withdraw' ? 'withdraw-to-available' : 'deposit-from-available';
      const res = await axios.post(`${API}/placements/subscriptions/${sub.id}/${endpoint}`, { amount: val }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setLivretTransferModal({ open: false, type: '', sub: null, amount: '', loading: false });
      fetchClientData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
      setLivretTransferModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ==================== CRYPTO WALLET FUNCTIONS ====================
  const handleCreateWallet = async () => {
    if (!newWallet.currency_code || !newWallet.wallet_address) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/crypto/wallets/${clientId}`, newWallet, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet créé avec succès');
      setShowWalletModal(false);
      setNewWallet({ currency_code: '', wallet_address: '', qr_code_url: '' });
      fetchClientData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du wallet');
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce wallet ?')) return;
    try {
      await axios.delete(`${API}/crypto/wallets/${walletId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet supprimé');
      fetchClientData(true);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleProcessWalletRequest = async (action) => {
    if (!selectedWalletRequest) return;
    
    if (action === 'approve' && !walletApprovalData.wallet_address) {
      toast.error("L'adresse du wallet est obligatoire");
      return;
    }
    
    try {
      await axios.put(`${API}/crypto/requests/${selectedWalletRequest.id}`, {
        action,
        wallet_address: walletApprovalData.wallet_address,
        qr_code_url: walletApprovalData.qr_code_url,
        rejection_reason: action === 'reject' ? 'Demande refusée' : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'approve' ? 'Demande approuvée et wallet créé' : 'Demande refusée');
      setShowProcessRequestModal(false);
      setSelectedWalletRequest(null);
      setWalletApprovalData({ wallet_address: '', qr_code_url: '' });
      fetchClientData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    }
  };

  const handleQrCodeUpload = async (e, isForRequest = false) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isForRequest) {
        setWalletApprovalData(prev => ({ ...prev, qr_code_url: reader.result }));
      } else {
        setNewWallet(prev => ({ ...prev, qr_code_url: reader.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  const getCryptoIcon = (code) => {
    const icons = {
      'BTC': '₿', 'ETH': 'Ξ', 'USDT_ERC20': '₮', 'USDT_TRC20': '₮', 'USDC': '$',
      'SOL': '◎', 'BNB': 'BNB', 'XRP': 'XRP', 'ADA': '₳', 'DOGE': 'Ð',
      'DOT': '●', 'MATIC': 'M', 'LTC': 'Ł', 'AVAX': 'A', 'LINK': 'L', 'TRX': 'T'
    };
    return icons[code] || code;
  };

  const getCryptoColor = (code) => {
    const colors = {
      'BTC': 'bg-orange-500/20 text-orange-500',
      'ETH': 'bg-purple-500/20 text-purple-500',
      'USDT_ERC20': 'bg-green-500/20 text-green-500',
      'USDT_TRC20': 'bg-green-500/20 text-green-500',
      'USDC': 'bg-blue-500/20 text-blue-500',
      'SOL': 'bg-purple-400/20 text-purple-400',
      'BNB': 'bg-yellow-500/20 text-yellow-500'
    };
    return colors[code] || 'bg-gray-500/20 text-gray-400';
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  // Notify workspace of client name once loaded
  const clientNameRef = useRef('');
  useEffect(() => {
    if (client?.user && onClientNameResolved) {
      const name = `${client.user.first_name || ''} ${client.user.last_name || ''}`.trim();
      if (name && name !== clientNameRef.current) {
        clientNameRef.current = name;
        onClientNameResolved(name);
      }
    }
  }, [client?.user?.first_name, client?.user?.last_name]);

  if (loading) {
    const loader = <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
    return inWorkspace ? loader : <DashboardLayout>{loader}</DashboardLayout>;
  }

  if (!client) return null;

  const clientStatus = client?.user?.status || 'active';
  const hasPendingChanges = client?.pending_changes_status === 'pending';

  const handleBack = () => {
    if (onBack) { onBack(); } else { navigate('/admin/clients'); }
  };

  const content = (
    <>
      <div className="space-y-6" onInput={() => onDirtyChange && onDirtyChange(true)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold">{client.user?.first_name} {client.user?.last_name}</h2>
              <div className="flex items-center gap-2">
                <input 
                  className="text-sm text-muted-foreground font-mono bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-0 py-0.5 w-auto"
                  value={client.client_code || ''}
                  onChange={(e) => setClient(prev => ({ ...prev, client_code: e.target.value }))}
                  placeholder="N° de compte"
                  data-testid="client-code-input"
                  style={{ minWidth: '120px', width: `${Math.max(120, (client.client_code?.length || 10) * 9)}px` }}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleMirrorLogin} data-testid="mirror-login-btn">
              <Eye className="w-4 h-4 mr-2" />{t('login_as_client')}
            </Button>
            <Button onClick={handleSaveProfile} disabled={saving} style={{ backgroundColor: primaryColor }} data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? t('loading') : t('save')}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur py-2">
            <TabsList className="bg-secondary/50 flex flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="profile" className="text-xs"><User className="w-3.5 h-3.5 mr-1" />Profil</TabsTrigger>
              <TabsTrigger value="portfolios" className="text-xs"><Wallet className="w-3.5 h-3.5 mr-1" />Portefeuilles</TabsTrigger>
              <TabsTrigger value="wallets" data-testid="tab-wallets" className="text-xs"><Bitcoin className="w-3.5 h-3.5 mr-1" />Wallets</TabsTrigger>
              <TabsTrigger value="crypto-portfolio" data-testid="tab-crypto-portfolio" onClick={() => fetchClientCrypto()} className="text-xs"><TrendingUp className="w-3.5 h-3.5 mr-1" />Crypto</TabsTrigger>
              <TabsTrigger value="iban" data-testid="tab-iban" onClick={fetchIbanRequests} className="text-xs"><CreditCard className="w-3.5 h-3.5 mr-1" />IBAN</TabsTrigger>
              <TabsTrigger value="transactions" data-testid="tab-transactions" className="text-xs"><DollarSign className="w-3.5 h-3.5 mr-1" />Transactions</TabsTrigger>
              <TabsTrigger value="trading" data-testid="tab-trading" className="text-xs"><TrendingUp className="w-3.5 h-3.5 mr-1" />Trading</TabsTrigger>
              <TabsTrigger value="kyc" className="text-xs"><FileCheck className="w-3.5 h-3.5 mr-1" />KYC</TabsTrigger>
              <TabsTrigger value="comments" data-testid="tab-comments" className="text-xs"><StickyNote className="w-3.5 h-3.5 mr-1" />Notes</TabsTrigger>
              <TabsTrigger value="connections" data-testid="tab-connections" onClick={fetchSessionsAndNavigation} className="text-xs">
                {isClientOnline ? <Wifi className="w-3.5 h-3.5 mr-1 text-green-500" /> : <WifiOff className="w-3.5 h-3.5 mr-1 text-gray-400" />}
                Connexions
              </TabsTrigger>
              <TabsTrigger value="history" className="text-xs"><History className="w-3.5 h-3.5 mr-1" />Historique</TabsTrigger>
              <TabsTrigger value="statement" data-testid="tab-statement" onClick={fetchStatements} className="text-xs"><FileText className="w-3.5 h-3.5 mr-1" />Relevé</TabsTrigger>
              <TabsTrigger value="placement-access" data-testid="tab-placement-access" onClick={fetchPlacementAccess} className="text-xs"><Briefcase className="w-3.5 h-3.5 mr-1" />Placements</TabsTrigger>
              <TabsTrigger value="advances" data-testid="tab-advances" onClick={fetchAdvances} className="text-xs"><Banknote className="w-3.5 h-3.5 mr-1" />Avances</TabsTrigger>
              <TabsTrigger value="mandates" data-testid="tab-mandates" onClick={fetchMandates} className="text-xs"><FileText className="w-3.5 h-3.5 mr-1" />Mandats</TabsTrigger>
              <TabsTrigger value="stock-portfolio" data-testid="tab-stock-portfolio" onClick={() => fetchStockPortfolio()} className="text-xs"><BarChart3 className="w-3.5 h-3.5 mr-1" />Actions</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="profile">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main info column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Pending changes alert */}
                {hasPendingChanges && (
                  <Card className="border-yellow-500/50 bg-yellow-500/5">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertCircle className="w-5 h-5 text-yellow-500" />
                          <div>
                            <p className="font-medium text-yellow-500">Modifications en attente d'approbation</p>
                            <p className="text-sm text-muted-foreground">Le client a soumis des modifications de profil</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleReviewChanges('approve')} disabled={saving} style={{ backgroundColor: accentColor }}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approuver
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleReviewChanges('reject')} disabled={saving}>
                            <XCircle className="w-4 h-4 mr-1" /> Refuser
                          </Button>
                        </div>
                      </div>
                      {client.pending_changes && (
                        <div className="mt-3 p-3 bg-black/20 rounded text-sm">
                          <p className="font-medium mb-2">Modifications demandées:</p>
                          {Object.entries(client.pending_changes).map(([key, value]) => (
                            <p key={key} className="text-muted-foreground">{key}: <span className="text-white">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}</span></p>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Account Type Selector */}
                <Card>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Type de compte</Label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setClient(prev => ({ ...prev, account_type: 'particulier' }))}
                          className={`px-4 py-1.5 rounded text-xs font-semibold transition-all ${
                            (client.account_type || 'particulier') === 'particulier' 
                              ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                          data-testid="client-account-type-particulier"
                        >Particulier</button>
                        <button
                          onClick={() => setClient(prev => ({ ...prev, account_type: 'professionnel' }))}
                          className={`px-4 py-1.5 rounded text-xs font-semibold transition-all ${
                            client.account_type === 'professionnel' 
                              ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                          data-testid="client-account-type-professionnel"
                        >Professionnel</button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Client Test Toggle */}
                <Card>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-semibold">Client Test</Label>
                        <p className="text-[10px] text-muted-foreground">Exclu des statistiques et calculs</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newVal = !client.is_test;
                          try {
                            await axios.put(`${API}/clients/${clientId}`, { is_test: newVal }, { headers: { Authorization: `Bearer ${token}` } });
                            setClient(prev => ({ ...prev, is_test: newVal }));
                            toast.success(newVal ? 'Client marqué comme Test' : 'Client Test désactivé');
                          } catch { toast.error('Erreur'); }
                        }}
                        className={`w-11 h-6 rounded-full transition-colors ${client.is_test ? 'bg-amber-500' : 'bg-gray-600'}`}
                        data-testid="client-test-toggle"
                      >
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform ${client.is_test ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Company Info (Professional only) */}
                {client.account_type === 'professionnel' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Informations société
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Raison sociale</Label>
                          <Input value={client.company_name || ''} onChange={(e) => setClient(prev => ({ ...prev, company_name: e.target.value }))} placeholder="Ma Société SAS" data-testid="client-company-name" />
                        </div>
                        <div className="space-y-2">
                          <Label>SIRET</Label>
                          <Input value={client.company_siret || ''} onChange={(e) => setClient(prev => ({ ...prev, company_siret: e.target.value }))} placeholder="123 456 789 00012" data-testid="client-company-siret" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Forme juridique</Label>
                          <Select value={client.company_legal_form || ''} onValueChange={(v) => setClient(prev => ({ ...prev, company_legal_form: v }))}>
                            <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SAS">SAS</SelectItem>
                              <SelectItem value="SARL">SARL</SelectItem>
                              <SelectItem value="SA">SA</SelectItem>
                              <SelectItem value="SCI">SCI</SelectItem>
                              <SelectItem value="EURL">EURL</SelectItem>
                              <SelectItem value="SASU">SASU</SelectItem>
                              <SelectItem value="Auto-entrepreneur">Auto-entrepreneur</SelectItem>
                              <SelectItem value="Autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Activité</Label>
                          <Input value={client.company_activity || ''} onChange={(e) => setClient(prev => ({ ...prev, company_activity: e.target.value }))} placeholder="Conseil, Commerce..." />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Adresse société</Label>
                        <Input value={client.company_address || ''} onChange={(e) => setClient(prev => ({ ...prev, company_address: e.target.value }))} placeholder="12 rue de la Paix" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Ville</Label>
                          <Input value={client.company_city || ''} onChange={(e) => setClient(prev => ({ ...prev, company_city: e.target.value }))} placeholder="Paris" />
                        </div>
                        <div className="space-y-2">
                          <Label>Code postal</Label>
                          <Input value={client.company_postal_code || ''} onChange={(e) => setClient(prev => ({ ...prev, company_postal_code: e.target.value }))} placeholder="75001" />
                        </div>
                        <div className="space-y-2">
                          <Label>Pays</Label>
                          <Input value={client.company_country || ''} onChange={(e) => setClient(prev => ({ ...prev, company_country: e.target.value }))} placeholder="France" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Basic Info Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Informations personnelles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Prénom</Label>
                        <Input 
                          value={client.user?.first_name || ''} 
                          onChange={(e) => setClient(prev => ({ ...prev, user: { ...prev.user, first_name: e.target.value } }))}
                          data-testid="admin-client-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nom</Label>
                        <Input 
                          value={client.user?.last_name || ''} 
                          onChange={(e) => setClient(prev => ({ ...prev, user: { ...prev.user, last_name: e.target.value } }))}
                          data-testid="admin-client-last-name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nom d'utilisateur</Label>
                        <Input value={client.user?.email?.split('@')[0] || ''} disabled className="bg-muted font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> E-mail</Label>
                        <Input 
                          value={client.user?.email || ''} 
                          onChange={(e) => setClient(prev => ({ ...prev, user: { ...prev.user, email: e.target.value } }))}
                          data-testid="admin-client-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone principal</Label>
                        <Input 
                          value={client.phone_primary || ''} 
                          onChange={(e) => setClient(prev => ({ ...prev, phone_primary: e.target.value }))}
                          placeholder="+33 6 00 00 00 00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone secondaire</Label>
                        <Input 
                          value={client.phone_secondary || ''} 
                          onChange={(e) => setClient(prev => ({ ...prev, phone_secondary: e.target.value }))}
                          placeholder="+33 1 00 00 00 00"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Date de naissance</Label>
                        <Input type="date" value={client.date_of_birth || ''} onChange={(e) => setClient(prev => ({ ...prev, date_of_birth: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Lieu de naissance</Label>
                        <Input value={client.place_of_birth || ''} onChange={(e) => setClient(prev => ({ ...prev, place_of_birth: e.target.value }))} placeholder="Paris" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Flag className="w-4 h-4" /> Nationalité</Label>
                      <Input value={client.nationality || ''} onChange={(e) => setClient(prev => ({ ...prev, nationality: e.target.value }))} placeholder="Française" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Adresse</Label>
                      <Input value={client.address || ''} onChange={(e) => setClient(prev => ({ ...prev, address: e.target.value }))} placeholder="123 Rue Exemple" />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Code postal</Label>
                        <Input value={client.postal_code || ''} onChange={(e) => setClient(prev => ({ ...prev, postal_code: e.target.value }))} placeholder="75001" />
                      </div>
                      <div className="space-y-2">
                        <Label>Ville</Label>
                        <Input value={client.city || ''} onChange={(e) => setClient(prev => ({ ...prev, city: e.target.value }))} placeholder="Paris" />
                      </div>
                      <div className="space-y-2">
                        <Label>Pays</Label>
                        <Input value={client.country || ''} onChange={(e) => setClient(prev => ({ ...prev, country: e.target.value }))} placeholder="France" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Beneficiaries Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Successeurs / Bénéficiaires
                      </CardTitle>
                      <Badge variant="outline" className="font-mono">
                        {(client.beneficiaries || []).reduce((s, b) => s + (b.percentage || 0), 0)}% / 100%
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(client.beneficiaries || []).map((b, idx) => {
                      // Ensure b is an object with string fields (not rendered directly)
                      const ben = typeof b === 'object' ? b : {};
                      return (
                      <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">Bénéficiaire {idx + 1}</span>
                          <div className="flex items-center gap-2">
                            {ben.id_status && (
                              <Badge variant="outline" className={ben.id_status === 'approved' ? 'text-green-500 border-green-500' : ben.id_status === 'rejected' ? 'text-red-500 border-red-500' : 'text-yellow-500 border-yellow-500'}>
                                {ben.id_status === 'approved' ? 'Validé' : ben.id_status === 'rejected' ? 'Refusé' : 'En attente'}
                              </Badge>
                            )}
                            <Button variant="ghost" size="sm" className="text-red-500 h-6 px-2 text-xs" onClick={() => {
                              const updated = [...(client.beneficiaries || [])];
                              updated.splice(idx, 1);
                              setClient(prev => ({...prev, beneficiaries: updated}));
                            }}>Retirer</Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Nom" value={ben.last_name || ''} className="h-8 text-sm" onChange={(e) => {
                            const updated = [...(client.beneficiaries || [])];
                            updated[idx] = {...updated[idx], last_name: e.target.value};
                            setClient(prev => ({...prev, beneficiaries: updated}));
                          }} />
                          <Input placeholder="Prénom" value={ben.first_name || ''} className="h-8 text-sm" onChange={(e) => {
                            const updated = [...(client.beneficiaries || [])];
                            updated[idx] = {...updated[idx], first_name: e.target.value};
                            setClient(prev => ({...prev, beneficiaries: updated}));
                          }} />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input type="date" value={ben.date_of_birth || ''} className="h-8 text-sm" onChange={(e) => {
                            const updated = [...(client.beneficiaries || [])];
                            updated[idx] = {...updated[idx], date_of_birth: e.target.value};
                            setClient(prev => ({...prev, beneficiaries: updated}));
                          }} />
                          <Input placeholder="Relation" value={ben.relation || ''} className="h-8 text-sm" onChange={(e) => {
                            const updated = [...(client.beneficiaries || [])];
                            updated[idx] = {...updated[idx], relation: e.target.value};
                            setClient(prev => ({...prev, beneficiaries: updated}));
                          }} />
                          <div className="flex items-center gap-1">
                            <Input type="number" min={0} max={100} value={ben.percentage || ''} placeholder="%" className="h-8 text-sm" onChange={(e) => {
                              const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                              const updated = [...(client.beneficiaries || [])];
                              updated[idx] = {...updated[idx], percentage: val};
                              if (updated.reduce((s, x) => s + (x.percentage || 0), 0) > 100) return;
                              setClient(prev => ({...prev, beneficiaries: updated}));
                            }} />
                            <span className="text-xs">%</span>
                          </div>
                        </div>
                        {ben.phone && (
                          <div className="text-xs text-muted-foreground">Tel: {ben.phone}</div>
                        )}
                        {ben.id_type && (
                          <div className="space-y-2 pt-1 border-t border-border/50">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Pièce d'identité :</span>
                              <Badge variant="outline" className="text-xs">{ben.id_type === 'passport' ? 'Passeport' : 'Carte d\'identité'}</Badge>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              {ben.id_front_url && (
                                <a href={ben.id_front_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-1 rounded border text-xs hover:bg-secondary/30 transition-colors" data-testid={`ben-${idx}-front-link`}>
                                  <FileText className="w-3 h-3" /> Recto
                                </a>
                              )}
                              {ben.id_back_url && (
                                <a href={ben.id_back_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-1 rounded border text-xs hover:bg-secondary/30 transition-colors" data-testid={`ben-${idx}-back-link`}>
                                  <FileText className="w-3 h-3" /> Verso
                                </a>
                              )}
                              {ben.passport_url && (
                                <a href={ben.passport_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-1 rounded border text-xs hover:bg-secondary/30 transition-colors" data-testid={`ben-${idx}-passport-link`}>
                                  <FileText className="w-3 h-3" /> Passeport
                                </a>
                              )}
                              {!ben.id_front_url && !ben.passport_url && (
                                <span className="text-xs text-muted-foreground italic">Aucun document envoyé</span>
                              )}
                            </div>
                            {ben.id_status === 'pending' && (
                              <div className="flex gap-2 pt-1">
                                <Button size="sm" className="h-6 px-3 text-xs bg-green-600 hover:bg-green-700" onClick={async () => {
                                  const updated = [...(client.beneficiaries || [])];
                                  updated[idx] = {...updated[idx], id_status: 'approved'};
                                  try {
                                    await axios.put(`${API}/clients/${clientId}`, { beneficiaries: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                    setClient(prev => ({...prev, beneficiaries: updated}));
                                    toast.success('Documents validés');
                                  } catch { toast.error('Erreur'); }
                                }}>Valider</Button>
                                <Button size="sm" variant="outline" className="h-6 px-3 text-xs text-red-500 hover:bg-red-500/10" onClick={async () => {
                                  const updated = [...(client.beneficiaries || [])];
                                  updated[idx] = {...updated[idx], id_status: 'rejected', id_front_url: '', id_back_url: '', passport_url: ''};
                                  try {
                                    await axios.put(`${API}/clients/${clientId}`, { beneficiaries: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                    setClient(prev => ({...prev, beneficiaries: updated}));
                                    toast.success('Documents refusés — le client peut renvoyer de nouveaux documents');
                                  } catch { toast.error('Erreur'); }
                                }}>Refuser</Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )})}
                    <Button variant="outline" size="sm" className="w-full" onClick={() => {
                      setClient(prev => ({
                        ...prev,
                        beneficiaries: [...(prev.beneficiaries || []), { first_name: '', last_name: '', date_of_birth: '', relation: '', percentage: 0 }]
                      }));
                    }}>
                      + Ajouter un bénéficiaire
                    </Button>
                  </CardContent>
                </Card>

                {/* Credentials Card */}
                <Card className="border-orange-500/30 bg-orange-500/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-orange-500" />
                      Identifiants du client
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Key className="w-4 h-4" /> Mot de passe
                        </Label>
                        <div className="flex gap-2">
                          <Input 
                            value={customPassword}
                            onChange={(e) => setCustomPassword(e.target.value)}
                            placeholder="Entrez le nouveau mot de passe"
                            className="font-mono"
                            data-testid="client-password-input"
                          />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => {
                              if (customPassword) {
                                navigator.clipboard.writeText(customPassword);
                                toast.success('Copié');
                              }
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={forcePasswordChange} 
                            onCheckedChange={setForcePasswordChange}
                            data-testid="force-password-change"
                          />
                          <Label className="text-xs text-muted-foreground">Forcer le changement au prochain login</Label>
                        </div>
                        <Button size="sm" onClick={async () => {
                          if (!customPassword || customPassword.length < 6) {
                            toast.error('Mot de passe trop court (min 6 caractères)');
                            return;
                          }
                          try {
                            await axios.put(`${API}/clients/${clientId}/set-password`, {
                              password: customPassword,
                              force_change: forcePasswordChange
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Mot de passe mis à jour');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                        }} data-testid="save-password-btn">
                          Enregistrer le mot de passe
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Lock className="w-4 h-4" /> Code PIN
                        </Label>
                        <div className="flex gap-2">
                          <Input 
                            value={customPin}
                            onChange={(e) => setCustomPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="Code PIN (6 chiffres)"
                            className="font-mono tracking-widest"
                            maxLength={6}
                            data-testid="client-pin-input"
                          />
                          <Button variant="outline" size="icon" onClick={() => {
                            if (customPin) { navigator.clipboard.writeText(customPin); toast.success('PIN copié'); }
                          }}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button size="sm" onClick={async () => {
                          if (!customPin || customPin.length < 4) {
                            toast.error('PIN trop court (min 4 chiffres)');
                            return;
                          }
                          try {
                            await axios.put(`${API}/clients/${clientId}/set-pin`, { pin: customPin }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Code PIN mis à jour');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                        }} data-testid="save-pin-btn">
                          Enregistrer le PIN
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes & Appointments */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Notes */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <StickyNote className="w-5 h-5" />
                          Notes
                        </CardTitle>
                        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Plus className="w-4 h-4 mr-1" /> Ajouter
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Ajouter une note</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <Textarea 
                                placeholder="Contenu de la note..." 
                                value={newNote.content}
                                onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                                rows={4}
                              />
                              <Button onClick={handleAddNote} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                                {saving ? 'Ajout...' : 'Ajouter la note'}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(client.notes || []).length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-4">Aucune note</p>
                      ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {(client.notes || []).map((note, idx) => (
                            <div key={idx} className="p-3 bg-secondary/30 rounded-lg">
                              <p className="text-sm">{note.content}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">{note.author_name || 'Admin'}</span>
                                <span>—</span>
                                <span>{new Date(note.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Appointments */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <CalendarPlus className="w-5 h-5" />
                          Rendez-vous
                        </CardTitle>
                        <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Plus className="w-4 h-4 mr-1" /> Ajouter
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Ajouter un rendez-vous</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label>Titre *</Label>
                                <Input 
                                  placeholder="Objet du rendez-vous" 
                                  value={newAppointment.title}
                                  onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Date *</Label>
                                  <Input 
                                    type="date"
                                    value={newAppointment.date}
                                    onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Heure</Label>
                                  <Input 
                                    type="time"
                                    value={newAppointment.time}
                                    onChange={(e) => setNewAppointment(prev => ({ ...prev, time: e.target.value }))}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea 
                                  placeholder="Notes additionnelles..." 
                                  value={newAppointment.notes}
                                  onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                                  rows={2}
                                />
                              </div>
                              <Button onClick={handleAddAppointment} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                                {saving ? 'Ajout...' : 'Ajouter le rendez-vous'}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(client.appointments || []).length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-4">Aucun rendez-vous</p>
                      ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {(client.appointments || []).map((appt, idx) => (
                            <div key={idx} className="p-3 bg-secondary/30 rounded-lg">
                              <p className="font-medium">{appt.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(appt.date).toLocaleDateString('fr-FR')} {appt.time && `à ${appt.time}`}
                              </p>
                              {appt.notes && <p className="text-xs text-muted-foreground mt-1">{appt.notes}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Sidebar - Actions & Status */}
              <div className="space-y-6">
                {/* Client Status Card */}
                <Card className={clientStatus === 'blocked' ? 'border-red-500/50' : clientStatus === 'suspended' ? 'border-yellow-500/50' : 'border-green-500/50'}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {clientStatus === 'active' ? <UserCheck className="w-5 h-5 text-green-500" /> : <UserX className="w-5 h-5 text-red-500" />}
                      Statut du compte
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge className={
                        clientStatus === 'active' ? 'bg-green-500/20 text-green-500' :
                        clientStatus === 'suspended' ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-red-500/20 text-red-500'
                      }>
                        {clientStatus === 'active' ? 'Actif' : clientStatus === 'suspended' ? 'Suspendu' : 'Bloqué'}
                      </Badge>
                    </div>
                    <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <Settings className="w-4 h-4 mr-2" /> Modifier le statut
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Modifier le statut du client</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Button onClick={() => handleUpdateClientStatus('active')} disabled={saving || clientStatus === 'active'} className="w-full bg-green-600 hover:bg-green-700">
                            <UserCheck className="w-4 h-4 mr-2" /> Activer le compte
                          </Button>
                          <Button onClick={() => handleUpdateClientStatus('suspended')} disabled={saving || clientStatus === 'suspended'} className="w-full bg-yellow-600 hover:bg-yellow-700">
                            <Clock className="w-4 h-4 mr-2" /> Suspendre temporairement
                          </Button>
                          <Button onClick={() => handleUpdateClientStatus('blocked')} disabled={saving || clientStatus === 'blocked'} variant="destructive" className="w-full">
                            <UserX className="w-4 h-4 mr-2" /> Bloquer le compte
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>

                {/* Analyst Assignment */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Assignation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Bureau */}
                    {(user?.role === 'super_admin' || (user?.role === 'admin_ops' && !user?.office_id)) && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Bureau</Label>
                      <Select 
                        value={client.office_id || 'none'} 
                        onValueChange={(v) => handleAssignOffice(v === 'none' ? null : v)}
                      >
                        <SelectTrigger data-testid="assign-office-select">
                          <SelectValue placeholder="Sélectionner un bureau" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun bureau</SelectItem>
                          {offices.map(office => (
                            <SelectItem key={office.id} value={office.id}>
                              {office.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                    {/* Analyste */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Gestionnaire / Analyste</Label>
                      <Select 
                        value={client.assigned_analyst_id || 'none'} 
                        onValueChange={(v) => handleAssignAnalyst(v === 'none' ? null : v)}
                        disabled={user?.role === 'analyst'}
                      >
                        <SelectTrigger data-testid="assign-analyst-select">
                          <SelectValue placeholder="Sélectionner un analyste" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun</SelectItem>
                          {analysts
                            .filter(a => !client.office_id || a.office_id === client.office_id || !a.office_id)
                            .map(analyst => {
                              const office = offices.find(o => o.id === analyst.office_id);
                              return (
                                <SelectItem key={analyst.id} value={analyst.id}>
                                  {analyst.first_name} {analyst.last_name}
                                  {office ? ` (${office.name})` : ''}
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Security Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="w-5 h-5" />
                      Sécurité
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Password Dialog */}
                    <Dialog open={showPasswordDialog} onOpenChange={(open) => { setShowPasswordDialog(open); if (!open) setGeneratedPassword(''); }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <Key className="w-4 h-4 mr-2" /> Générer mot de passe
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Mot de passe provisoire</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          {!generatedPassword ? (
                            <>
                              <p className="text-muted-foreground text-sm">
                                Un nouveau mot de passe provisoire sera généré. Le client devra le changer à sa prochaine connexion.
                              </p>
                              <Button onClick={handleGeneratePassword} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                                {saving ? 'Génération...' : 'Générer'}
                              </Button>
                            </>
                          ) : (
                            <div className="space-y-4">
                              <div className="p-4 bg-secondary rounded-lg text-center">
                                <p className="text-sm text-muted-foreground mb-2">Mot de passe provisoire:</p>
                                <p className="text-2xl font-mono font-bold" style={{ color: accentColor }}>{generatedPassword}</p>
                              </div>
                              <Button variant="outline" onClick={() => copyToClipboard(generatedPassword)} className="w-full">
                                <Copy className="w-4 h-4 mr-2" /> Copier
                              </Button>
                              <p className="text-xs text-muted-foreground text-center">
                                Communiquez ce mot de passe au client de manière sécurisée
                              </p>
                            </div>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* PIN Dialog */}
                    <Dialog open={showPinDialog} onOpenChange={(open) => { setShowPinDialog(open); if (!open) setGeneratedPin(''); }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <Lock className="w-4 h-4 mr-2" /> Générer code PIN
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Code PIN provisoire</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          {!generatedPin ? (
                            <>
                              <p className="text-muted-foreground text-sm">
                                Un nouveau code PIN provisoire sera généré. Le client devra le changer à sa prochaine connexion.
                              </p>
                              <Button onClick={handleGeneratePin} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                                {saving ? 'Génération...' : 'Générer'}
                              </Button>
                            </>
                          ) : (
                            <div className="space-y-4">
                              <div className="p-4 bg-secondary rounded-lg text-center">
                                <p className="text-sm text-muted-foreground mb-2">Code PIN provisoire:</p>
                                <p className="text-3xl font-mono font-bold tracking-widest" style={{ color: accentColor }}>{generatedPin}</p>
                              </div>
                              <Button variant="outline" onClick={() => copyToClipboard(generatedPin)} className="w-full">
                                <Copy className="w-4 h-4 mr-2" /> Copier
                              </Button>
                              <p className="text-xs text-muted-foreground text-center">
                                Communiquez ce PIN au client de manière sécurisée
                              </p>
                            </div>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portfolios">
            {/* Funds Summary Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Synthèse des fonds du client
                </CardTitle>
                <CardDescription>
                  Les fonds sont calculés automatiquement en fonction des transactions, gains trading et intérêts livrets
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Main Funds Display - 4 columns including Trading */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {/* Fonds disponibles */}
                  <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className="text-xs text-muted-foreground">Fonds Disponibles</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-500">
                      {((client.portfolios || []).find(p => p.portfolio_type === 'available')?.available || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>

                  {/* Capital investi */}
                  <div className="p-5 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                      </div>
                      <span className="text-xs text-muted-foreground">Capital Investi</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-500">
                      {((client.portfolios || []).find(p => p.portfolio_type === 'available')?.capital || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  
                  {/* Bonus */}
                  <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Star className="w-4 h-4 text-amber-500" />
                      </div>
                      <span className="text-xs text-muted-foreground">Bonus</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-500">
                      {((client.portfolios || []).find(p => p.portfolio_type === 'available')?.bonus || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  
                  {/* Portefeuille Trading - Always show but with toggle state */}
                  <div className={`p-5 rounded-xl border ${tradingPortfolioEnabled.enabled ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30' : 'bg-secondary/30 border-dashed'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tradingPortfolioEnabled.enabled ? 'bg-purple-500/20' : 'bg-secondary/50'}`}>
                        <ArrowRightLeft className={`w-4 h-4 ${tradingPortfolioEnabled.enabled ? 'text-purple-500' : 'text-muted-foreground'}`} />
                      </div>
                      <span className="text-xs text-muted-foreground">Portefeuille Trading</span>
                    </div>
                    {tradingPortfolioEnabled.enabled ? (
                      <p className="text-2xl font-bold text-purple-500">
                        {(((client.portfolios || []).find(p => p.portfolio_type === 'trading')
                          || (client.portfolios || []).find(p => p.portfolio_type === 'trade'))?.available
                          || ((client.portfolios || []).find(p => p.portfolio_type === 'trading')
                            || (client.portfolios || []).find(p => p.portfolio_type === 'trade'))?.balance
                          || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Désactivé</p>
                    )}
                  </div>
                </div>

                {/* Info Box */}
                <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Comment sont calculés les fonds ?</span>
                  </p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                    <li><span className="font-medium">Fonds Disponibles</span> = Dépôts - Retraits + Gains Trading validés + Intérêts Livrets</li>
                    <li><span className="font-medium">Capital Investi</span> = Somme des dépôts effectués</li>
                    <li><span className="font-medium">Bonus</span> = Somme des bonus crédités via l'onglet Transactions</li>
                  </ul>
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground">
                      Pour modifier les fonds, utilisez l'onglet <span className="font-medium">Transactions</span> (Dépôt, Retrait, Bonus).
                    </p>
                  </div>
                </div>

                {/* Quick Transfer Buttons */}
                <div className="mt-4 p-4 rounded-lg bg-secondary/30 border border-border space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4" style={{ color: primaryColor }} />
                    Transfert de fonds
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-border/50 bg-background/50 space-y-2">
                      <p className="text-xs text-muted-foreground">Disponible → Trading</p>
                      <Input type="text" inputMode="decimal" placeholder="Montant" className="h-8 text-sm" id="transfer-to-trading-amount" data-testid="transfer-to-trading-input" />
                      <select className="w-full h-8 px-2 rounded border border-input bg-background text-xs" id="transfer-to-trading-status" data-testid="transfer-to-trading-status">
                        <option value="en_attente">En attente</option>
                        <option value="validee">Validée (exécuter)</option>
                        <option value="en_verification">En vérification</option>
                        <option value="refuse">Refusée</option>
                      </select>
                      <Button size="sm" className="h-8 w-full" style={{ backgroundColor: primaryColor }}
                        data-testid="transfer-to-trading-btn"
                        onClick={async () => {
                          const val = parseFloat(document.getElementById('transfer-to-trading-amount').value?.replace(',', '.'));
                          const status = document.getElementById('transfer-to-trading-status').value;
                          if (!val || val <= 0) { toast.error('Montant invalide'); return; }
                          try {
                            await axios.post(`${API}/portfolio/transfer`, {
                              client_id: clientId, amount: val, direction: 'available_to_trading', force_status: status
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(`Transfert ${val.toLocaleString('fr-FR')}€ → Trading (${status})`);
                            document.getElementById('transfer-to-trading-amount').value = '';
                            fetchClientData(true);
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                        }}
                      >
                        Transférer →
                      </Button>
                    </div>
                    <div className="p-3 rounded-lg border border-border/50 bg-background/50 space-y-2">
                      <p className="text-xs text-muted-foreground">Trading → Disponible</p>
                      <Input type="text" inputMode="decimal" placeholder="Montant" className="h-8 text-sm" id="transfer-to-available-amount" data-testid="transfer-to-available-input" />
                      <select className="w-full h-8 px-2 rounded border border-input bg-background text-xs" id="transfer-to-available-status" data-testid="transfer-to-available-status">
                        <option value="en_attente">En attente</option>
                        <option value="validee">Validée (exécuter)</option>
                        <option value="en_verification">En vérification</option>
                        <option value="refuse">Refusée</option>
                      </select>
                      <Button size="sm" className="h-8 w-full" style={{ backgroundColor: primaryColor }}
                        data-testid="transfer-to-available-btn"
                        onClick={async () => {
                          const val = parseFloat(document.getElementById('transfer-to-available-amount').value?.replace(',', '.'));
                          const status = document.getElementById('transfer-to-available-status').value;
                          if (!val || val <= 0) { toast.error('Montant invalide'); return; }
                          try {
                            await axios.post(`${API}/portfolio/transfer`, {
                              client_id: clientId, amount: val, direction: 'trading_to_available', force_status: status
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(`Transfert ${val.toLocaleString('fr-FR')}€ → Disponible (${status})`);
                            document.getElementById('transfer-to-available-amount').value = '';
                            fetchClientData(true);
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                        }}
                      >
                        ← Transférer
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Trading Portfolio Toggle & Transfers */}
                <div className="mt-6 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <ArrowRightLeft className="w-4 h-4" style={{ color: primaryColor }} />
                      Portefeuille Trading
                    </h3>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={tradingPortfolioEnabled.enabled}
                        onCheckedChange={async (checked) => {
                          try {
                            await axios.put(`${API}/portfolio/trading-enabled/${clientId}`, 
                              { enabled: checked },
                              { headers: { Authorization: `Bearer ${token}` }}
                            );
                            setTradingPortfolioEnabled(prev => ({ ...prev, enabled: checked, source: 'client' }));
                            toast.success(checked ? 'Portefeuille Trading activé' : 'Portefeuille Trading désactivé');
                            fetchClientData(true);
                          } catch (error) {
                            toast.error('Erreur');
                          }
                        }}
                        className="data-[state=checked]:bg-purple-500"
                      />
                      <span className={`text-sm ${tradingPortfolioEnabled.enabled ? 'text-purple-500 font-medium' : 'text-muted-foreground'}`}>
                        {tradingPortfolioEnabled.enabled ? 'Activé' : 'Désactivé'}
                      </span>
                      {tradingPortfolioEnabled.source === 'client' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/portfolio/trading-enabled/${clientId}`, 
                                { reset_to_global: true },
                                { headers: { Authorization: `Bearer ${token}` }}
                              );
                              fetchTradingPortfolioStatus();
                              toast.success('Réinitialisé aux paramètres globaux');
                            } catch (error) {
                              toast.error('Erreur');
                            }
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Réinitialiser au global
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {tradingPortfolioEnabled.source === 'client' && (
                    <p className="text-xs text-yellow-500 mb-3">
                      ⚠️ Paramètre personnalisé - le paramètre global ne s'applique pas.
                    </p>
                  )}
                  
                  {tradingPortfolioEnabled.enabled ? (
                    <>
                      {portfolioTransfers.filter(t => t.status === 'pending').length > 0 && (
                        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                          <h4 className="font-medium mb-2 flex items-center gap-2 text-yellow-600 text-sm">
                            <Clock className="w-4 h-4" />
                            Demandes de transfert en attente ({portfolioTransfers.filter(t => t.status === 'pending').length})
                          </h4>
                          <div className="space-y-2">
                            {portfolioTransfers.filter(t => t.status === 'pending').map(transfer => (
                              <div key={transfer.id} className="rounded bg-secondary/30 p-2">
                                <p className="font-medium text-sm">
                                  {transfer.source_type} → {transfer.dest_type}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {transfer.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € • 
                                  Demandé le {new Date(transfer.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2 italic">
                            → Valider/Refuser depuis l'onglet Transactions
                          </p>
                        </div>
                      )}
                      {portfolioTransfers.filter(t => t.status !== 'pending').length > 0 && (
                        <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <h4 className="font-medium mb-2 flex items-center gap-2 text-emerald-400 text-sm">
                            <Clock className="w-4 h-4" />
                            Historique des transferts ({portfolioTransfers.filter(t => t.status !== 'pending').length})
                          </h4>
                          <div className="space-y-2">
                            {portfolioTransfers.filter(t => t.status !== 'pending').map(transfer => (
                              <div key={transfer.id} className="rounded bg-secondary/30 p-2" data-testid={`transfer-history-${transfer.id}`}>
                                <p className="font-medium text-sm">
                                  {transfer.source_type} → {transfer.dest_type}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {transfer.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € •
                                  {['approved', 'validated', 'valide'].includes(transfer.status) ? ' Validé' : ' Refusé'} le {new Date(transfer.processed_at || transfer.updated_at || transfer.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {portfolioTransfers.filter(t => t.status === 'pending').length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Le client peut transférer des fonds depuis son espace vers le portefeuille trading.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Activez pour permettre au client de transférer des fonds vers le trading.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          {/* WALLETS TAB */}
          <TabsContent value="wallets">
            <div className="space-y-6">
              {/* Pending Wallet Requests */}
              {walletRequests.filter(r => r.status === 'pending').length > 0 && (
                <Card className="border-yellow-500/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-yellow-500">
                      <Clock className="w-5 h-5" />
                      Demandes de Wallet en Attente ({walletRequests.filter(r => r.status === 'pending').length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {walletRequests.filter(r => r.status === 'pending').map((request) => (
                        <div 
                          key={request.id}
                          className="flex items-center justify-between p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${getCryptoColor(request.currency_code)}`}>
                              {getCryptoIcon(request.currency_code)}
                            </div>
                            <div>
                              <p className="font-medium">{request.currency_name}</p>
                              <p className="text-sm text-muted-foreground">
                                Réseau: {request.network} • Demandé le {new Date(request.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          <Button 
                            onClick={() => {
                              setSelectedWalletRequest(request);
                              setShowProcessRequestModal(true);
                            }}
                            style={{ backgroundColor: primaryColor }}
                          >
                            Traiter
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Existing Wallets */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Bitcoin className="w-5 h-5" />
                        Wallets Crypto du Client
                      </CardTitle>
                      <CardDescription>Gérez les adresses de dépôt crypto</CardDescription>
                    </div>
                    <Button 
                      onClick={() => setShowWalletModal(true)}
                      style={{ backgroundColor: primaryColor }}
                      data-testid="add-wallet-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un Wallet
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {cryptoWallets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun wallet crypto pour ce client</p>
                      <p className="text-sm">Cliquez sur "Ajouter un Wallet" pour en créer un</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {cryptoWallets.map((wallet) => (
                        <div 
                          key={wallet.id}
                          className="p-4 rounded-lg border border-border bg-background/50"
                          data-testid={`wallet-card-${wallet.currency_code}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${getCryptoColor(wallet.currency_code)}`}>
                                {getCryptoIcon(wallet.currency_code)}
                              </div>
                              <div>
                                <h3 className="font-semibold">{wallet.currency_name}</h3>
                                <p className="text-xs text-muted-foreground">{wallet.network}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {wallet.qr_code_url && (
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <QrCode className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>{wallet.currency_name} - QR Code</DialogTitle>
                                    </DialogHeader>
                                    <div className="flex justify-center py-4">
                                      <img src={wallet.qr_code_url} alt="QR Code" className="w-48 h-48 object-contain bg-white p-2 rounded" />
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                              <label>
                                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onloadend = async () => {
                                    try {
                                      await axios.put(`${API}/crypto/wallets/${wallet.id}`, { qr_code_url: reader.result }, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('QR Code ajouté');
                                      fetchClientData(true);
                                    } catch { toast.error('Erreur upload QR'); }
                                  };
                                  reader.readAsDataURL(file);
                                }} />
                                <Button variant="ghost" size="sm" asChild title="Ajouter/modifier QR Code">
                                  <span><Upload className="w-4 h-4" /></span>
                                </Button>
                              </label>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteWallet(wallet.id)}
                                className="text-red-500 hover:text-red-400"
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="bg-secondary/50 rounded p-2">
                            <p className="text-xs text-muted-foreground mb-1">Adresse de dépôt</p>
                            <div className="flex items-center gap-2">
                              <code className="text-xs font-mono break-all flex-1">
                                {wallet.wallet_address}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(wallet.wallet_address)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <p className="text-xs text-muted-foreground mt-2">
                            Créé le {new Date(wallet.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Request History */}
              {walletRequests.filter(r => r.status !== 'pending').length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Historique des Demandes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {walletRequests.filter(r => r.status !== 'pending').map((request) => (
                        <div 
                          key={request.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-border bg-background/50"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${getCryptoColor(request.currency_code)}`}>
                              {getCryptoIcon(request.currency_code)}
                            </div>
                            <div>
                              <p className="font-medium">{request.currency_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              request.status === 'approved' 
                                ? 'bg-green-500/20 text-green-500' 
                                : 'bg-red-500/20 text-red-500'
                            }`}>
                              {request.status === 'approved' ? 'Approuvée' : 'Refusée'}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-400 h-7 w-7 p-0"
                              onClick={async () => {
                                if (!window.confirm('Supprimer cet historique ?')) return;
                                try {
                                  await axios.delete(`${API}/crypto/requests/${request.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Historique supprimé');
                                  fetchClientData(true);
                                } catch (err) {
                                  toast.error('Erreur lors de la suppression');
                                }
                              }}
                              data-testid={`delete-wallet-history-${request.id}`}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* CRYPTO PORTFOLIO TAB */}
          <TabsContent value="crypto-portfolio">
            <div className="space-y-6">
              {loadingCrypto ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : !clientCrypto ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">Cliquez sur l'onglet Crypto pour charger les données</CardContent></Card>
              ) : (
                <>
                  {/* Overview */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Portefeuille Crypto</h3>
                    <Button onClick={openCreditCryptoDialog} style={{ backgroundColor: primaryColor }} data-testid="credit-crypto-btn">
                      <DollarSign className="w-4 h-4 mr-2" />Créditer Crypto
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-card/80"><CardContent className="p-4">
                      <p className="text-xs text-muted-foreground uppercase">Valeur totale crypto</p>
                      <p className="text-2xl font-bold">{clientCrypto.total_value?.toLocaleString('fr-FR')} €</p>
                    </CardContent></Card>
                    <Card className="bg-card/80"><CardContent className="p-4">
                      <p className="text-xs text-muted-foreground uppercase">Investi</p>
                      <p className="text-2xl font-bold">{clientCrypto.total_invested?.toLocaleString('fr-FR')} €</p>
                    </CardContent></Card>
                    <Card className="bg-card/80"><CardContent className="p-4">
                      <p className="text-xs text-muted-foreground uppercase">P&L</p>
                      <p className={`text-2xl font-bold ${clientCrypto.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {clientCrypto.total_pnl >= 0 ? '+' : ''}{clientCrypto.total_pnl?.toLocaleString('fr-FR')} €
                      </p>
                    </CardContent></Card>
                  </div>

                  {/* Holdings */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Portefeuille Crypto</CardTitle></CardHeader>
                    <CardContent>
                      {(clientCrypto.holdings || []).length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">Aucune crypto détenue</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-border/50">
                            <th className="text-left py-2 px-2">Crypto</th>
                            <th className="text-right py-2 px-2">Quantité</th>
                            <th className="text-right py-2 px-2">Prix actuel</th>
                            <th className="text-right py-2 px-2">Valeur</th>
                            <th className="text-right py-2 px-2">Investi</th>
                            <th className="text-right py-2 px-2">P&L</th>
                          </tr></thead>
                          <tbody>
                            {clientCrypto.holdings.map(h => (
                              <tr key={h.symbol} className="border-b border-border/20">
                                <td className="py-2 px-2 flex items-center gap-2">
                                  {h.image && <img src={h.image} alt="" className="w-6 h-6 rounded-full" />}
                                  <span className="font-semibold">{h.symbol}</span>
                                  <span className="text-xs text-muted-foreground">{h.name}</span>
                                </td>
                                <td className="text-right py-2 px-2 font-mono">{h.quantity < 1 ? h.quantity?.toFixed(8) : h.quantity?.toFixed(4)}</td>
                                <td className="text-right py-2 px-2 font-mono">{h.current_price?.toLocaleString('fr-FR')} €</td>
                                <td className="text-right py-2 px-2 font-semibold">{h.value?.toLocaleString('fr-FR')} €</td>
                                <td className="text-right py-2 px-2 text-muted-foreground">{h.total_cost?.toLocaleString('fr-FR')} €</td>
                                <td className={`text-right py-2 px-2 font-semibold ${h.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {h.pnl >= 0 ? '+' : ''}{h.pnl?.toLocaleString('fr-FR')} €
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Wallet Requests & Wallets */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Wallets Crypto</CardTitle></CardHeader>
                    <CardContent>
                      {/* Pending requests */}
                      {(clientCrypto.wallet_requests || []).filter(r => r.status === 'pending').length > 0 && (
                        <div className="space-y-3 mb-4">
                          <p className="text-xs font-semibold text-yellow-400 uppercase">Demandes en attente</p>
                          {clientCrypto.wallet_requests.filter(r => r.status === 'pending').map(r => (
                            <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
                              <div>
                                <p className="font-semibold">{r.currency_name || r.currency_code}</p>
                                <p className="text-xs text-muted-foreground">Réseau: {r.network} · Demandé le {new Date(r.created_at).toLocaleDateString('fr-FR')}</p>
                              </div>
                              <Button size="sm" onClick={() => { setAssignWalletData({ id: r.id, address: '', qr_code_url: '' }); setAssignWalletDialog(true); }} style={{ backgroundColor: primaryColor }} data-testid={`assign-request-${r.id}`}>
                                Traiter
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Assigned wallets */}
                      {(clientCrypto.wallets || []).length > 0 ? (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-emerald-400 uppercase">Wallets actifs</p>
                          {clientCrypto.wallets.map(w => (
                            <div key={w.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                              <div>
                                <p className="font-semibold">{w.currency_name || w.currency_code}</p>
                                <p className="font-mono text-xs text-muted-foreground">{w.wallet_address}</p>
                                <p className="text-xs text-muted-foreground">Réseau: {w.network}</p>
                              </div>
                              <Badge className="bg-emerald-500/20 text-emerald-400">Actif</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (clientCrypto.wallet_requests || []).filter(r => r.status === 'pending').length === 0 && (
                        <p className="text-center text-muted-foreground py-4">Aucun wallet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Crypto Transactions */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Transactions Crypto</CardTitle></CardHeader>
                    <CardContent>
                      {(clientCrypto.transactions || []).length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">Aucune transaction crypto</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-border/50">
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Description</th>
                            <th className="text-right py-2 px-2">Montant</th>
                            <th className="py-2 px-2 w-8"></th>
                          </tr></thead>
                          <tbody>
                            {clientCrypto.transactions.map(tx => {
                              const typeLabels = { achat_crypto: 'Achat', vente_crypto: 'Vente', swap_crypto: 'Swap', envoi_crypto: 'Envoi', reception_crypto: 'Réception' };
                              const typeColors = { achat_crypto: 'text-blue-400', vente_crypto: 'text-emerald-400', swap_crypto: 'text-purple-400', envoi_crypto: 'text-red-400', reception_crypto: 'text-cyan-400' };
                              return (
                                <tr key={tx.id} className="border-b border-border/20">
                                  <td className="py-2 px-2">{new Date(tx.transaction_date).toLocaleDateString('fr-FR')}</td>
                                  <td className={`py-2 px-2 font-semibold ${typeColors[tx.transaction_type] || ''}`}>{typeLabels[tx.transaction_type] || tx.transaction_type}</td>
                                  <td className="py-2 px-2 text-muted-foreground text-xs">{tx.description}</td>
                                  <td className={`text-right py-2 px-2 font-mono ${tx.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString('fr-FR')} €
                                  </td>
                                  <td className="py-2 px-2">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-300" onClick={async () => {
                                      if (!window.confirm('Supprimer cette transaction ? Les effets seront annulés.')) return;
                                      try { await axios.delete(`${API}/crypto/admin/transaction/${tx.id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Transaction supprimée'); fetchClientCrypto(); fetchClientData(true); }
                                      catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                                    }}><Trash className="w-3 h-3" /></Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Assign Wallet Dialog */}
              <Dialog open={assignWalletDialog} onOpenChange={setAssignWalletDialog}>
                <DialogContent className="max-w-md" data-testid="assign-wallet-dialog">
                  <DialogHeader><DialogTitle>Traiter la demande de wallet</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Adresse du wallet *</Label>
                      <Input value={assignWalletData.address} onChange={e => setAssignWalletData(p => ({ ...p, address: e.target.value }))} placeholder="0x..." className="font-mono" data-testid="assign-wallet-address" />
                    </div>
                    <div>
                      <Label>QR Code du wallet</Label>
                      <div className="space-y-2">
                        <Input value={assignWalletData.qr_code_url || ''} onChange={e => setAssignWalletData(p => ({ ...p, qr_code_url: e.target.value }))} placeholder="URL du QR code..." data-testid="assign-wallet-qr" />
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                          <Upload className="w-4 h-4" />
                          Importer une image QR
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            e.target.value = '';
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setAssignWalletData(p => ({ ...p, qr_code_url: ev.target.result }));
                              toast.success('QR Code importé');
                            };
                            reader.readAsDataURL(file);
                          }} data-testid="qr-upload-input" />
                        </label>
                        {assignWalletData.qr_code_url && assignWalletData.qr_code_url.startsWith('data:') && (
                          <div className="flex items-center gap-3 p-2 bg-secondary/30 rounded-lg">
                            <img src={assignWalletData.qr_code_url} alt="QR" className="w-16 h-16 object-contain bg-white rounded p-1" />
                            <span className="text-xs text-emerald-400">QR Code importé</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAssignWalletDialog(false)}>Annuler</Button>
                    <Button onClick={handleAssignWallet} style={{ backgroundColor: primaryColor }} data-testid="confirm-assign-wallet">Approuver et assigner</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Credit Crypto Dialog */}
              <Dialog open={creditCryptoDialog} onOpenChange={setCreditCryptoDialog}>
                <DialogContent className="max-w-lg" data-testid="credit-crypto-dialog">
                  <DialogHeader><DialogTitle>Transaction Crypto — Client</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant={creditCryptoForm.use_funds ? 'default' : 'outline'} onClick={() => setCreditCryptoForm(p => ({ ...p, use_funds: true }))} className="flex-1 text-xs">Depuis fonds disponibles</Button>
                      <Button size="sm" variant={!creditCryptoForm.use_funds ? 'default' : 'outline'} onClick={() => setCreditCryptoForm(p => ({ ...p, use_funds: false }))} className="flex-1 text-xs">Réception externe (crypto)</Button>
                    </div>
                    {creditCryptoForm.use_funds && (
                      <div className="p-3 bg-secondary/30 rounded-lg text-sm">
                        <span className="text-muted-foreground">Fonds disponibles du client : </span>
                        <span className="font-bold">{clientFundsAvailable.toLocaleString('fr-FR')} €</span>
                      </div>
                    )}
                    {!creditCryptoForm.use_funds && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-400">
                        Le client a envoyé des cryptos depuis un portefeuille externe. Ses fonds disponibles ne seront pas impactés.
                      </div>
                    )}
                    <div>
                      <Label>Cryptomonnaie *</Label>
                      <Select value={creditCryptoForm.symbol} onValueChange={v => { setCreditCryptoForm(p => ({ ...p, symbol: v })); checkWalletNeeded(v); }}>
                        <SelectTrigger data-testid="credit-crypto-symbol">
                          <SelectValue placeholder="Sélectionner une crypto" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {cryptoPrices.slice(0, 50).map(c => (
                            <SelectItem key={c.symbol} value={c.symbol}>
                              <span className="flex items-center gap-2">
                                {c.image && <img src={c.image} alt="" className="w-4 h-4 rounded-full inline" />}
                                {c.symbol} — {c.name} — {c.current_price?.toLocaleString('fr-FR')} €
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Montant en EUR *</Label>
                      <Input type="number" step="0.01" autoComplete="off" value={creditCryptoForm.amount_eur} onChange={e => setCreditCryptoForm(p => ({ ...p, amount_eur: e.target.value }))} placeholder="450" data-testid="credit-crypto-amount" />
                      {creditCryptoForm.use_funds && creditCryptoForm.amount_eur && parseFloat(creditCryptoForm.amount_eur) > clientFundsAvailable && (
                        <p className="text-xs text-red-400 mt-1 font-semibold">Fonds insuffisants !</p>
                      )}
                    </div>
                    {/* Rate preview */}
                    {creditCryptoForm.symbol && creditCryptoForm.amount_eur && parseFloat(creditCryptoForm.amount_eur) > 0 && (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Cours actuel</span>
                          <span className="font-mono">{getCryptoPrice(creditCryptoForm.symbol).toLocaleString('fr-FR')} € / {creditCryptoForm.symbol}</span>
                        </div>
                        <div className="flex justify-between font-bold text-emerald-400">
                          <span>Le client recevra</span>
                          <span className="font-mono">{(getCryptoPrice(creditCryptoForm.symbol) > 0 ? parseFloat(creditCryptoForm.amount_eur) / getCryptoPrice(creditCryptoForm.symbol) : 0).toFixed(8)} {creditCryptoForm.symbol}</span>
                        </div>
                      </div>
                    )}
                    {/* Wallet creation if needed */}
                    {needsWallet && creditCryptoForm.symbol && (
                      <div className="p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 space-y-3">
                        <p className="text-sm text-yellow-400 font-semibold">Le client n'a pas de wallet {creditCryptoForm.symbol}</p>
                        <div>
                          <Label>Adresse wallet *</Label>
                          <Input value={inlineWalletAddr} onChange={e => setInlineWalletAddr(e.target.value)} placeholder="0x..." className="font-mono text-xs" />
                        </div>
                        <div>
                          <Label>QR Code</Label>
                          <div className="flex gap-2">
                            <Input value={inlineWalletQr} onChange={e => setInlineWalletQr(e.target.value)} placeholder="URL..." className="flex-1" />
                            <label className="cursor-pointer px-2 py-1 rounded bg-primary/10 text-primary text-xs flex items-center gap-1">
                              <Upload className="w-3 h-3" />Import
                              <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (!f) return; e.target.value=''; const r = new FileReader(); r.onload = ev => { setInlineWalletQr(ev.target.result); toast.success('QR importé'); }; r.readAsDataURL(f); }} />
                            </label>
                          </div>
                          {inlineWalletQr && inlineWalletQr.startsWith('data:') && <img src={inlineWalletQr} alt="QR" className="w-16 h-16 bg-white rounded p-1 mt-1" />}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreditCryptoDialog(false)}>Annuler</Button>
                    <Button onClick={handleCreditCrypto} disabled={!creditCryptoForm.symbol || !creditCryptoForm.amount_eur || (creditCryptoForm.use_funds && parseFloat(creditCryptoForm.amount_eur) > clientFundsAvailable) || (needsWallet && !inlineWalletAddr)} style={{ backgroundColor: primaryColor }} data-testid="confirm-credit-crypto">
                      {needsWallet ? 'Créer wallet & créditer' : 'Créditer'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* IBAN TAB */}
          <TabsContent value="iban">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Gestion des IBAN
                      </CardTitle>
                      <CardDescription>
                        Validez ou gérez les demandes d'IBAN du client
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setShowCreateIbanForm(!showCreateIbanForm)}
                      variant={showCreateIbanForm ? "outline" : "default"}
                      style={!showCreateIbanForm ? { backgroundColor: primaryColor } : {}}
                    >
                      {showCreateIbanForm ? <XCircle className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      {showCreateIbanForm ? 'Annuler' : 'Créer un IBAN'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Pool IBAN Section — always visible for admins, for analysts only in pool mode */}
                  {(user?.role !== 'analyst' || ibanMode === 'pool') && (
                    <div className="mb-6 border rounded-lg p-4 bg-blue-500/5 border-blue-500/20" data-testid="client-pool-iban-section">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2 text-blue-400">
                          <CreditCard className="w-4 h-4" /> RIB du Pool Centralisé
                        </h4>
                      </div>
                      {/* Currently assigned pool IBANs */}
                      {clientPoolIbans.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-muted-foreground mb-2">RIB attribués depuis le pool :</p>
                          <div className="space-y-2">
                            {clientPoolIbans.map(iban => (
                              <div key={iban.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                                <div>
                                  <p className="font-mono text-xs">{iban.iban}</p>
                                  <p className="text-xs text-muted-foreground">{iban.account_holder} — {iban.bank_name || 'N/A'}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">Réf. virement : <span className="font-mono font-medium text-foreground">{client?.client_code || '-'}</span></p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={
                                    iban.status === 'valid' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                                    iban.status === 'verification' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                                    'bg-red-500/15 text-red-400 border-red-500/30'
                                  }>
                                    {iban.status === 'valid' ? 'Valide' : iban.status === 'verification' ? 'Vérification' : 'Expiré'}
                                  </Badge>
                                  <Button variant="ghost" size="sm" className="text-destructive h-7 text-xs"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/iban-pool/${iban.id}/unassign-client`, { client_id: clientId }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
                                        toast.success('RIB retiré du client');
                                        fetchIbanRequests();
                                      } catch (e) { toast.error('Erreur'); }
                                    }}>
                                    Retirer
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Available pool IBANs to assign */}
                      {poolIbans.length > 0 ? (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">RIB disponibles à attribuer :</p>
                          <div className="space-y-2">
                            {poolIbans.map(iban => (
                              <div key={iban.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/10 transition-colors">
                                <div>
                                  <p className="font-mono text-xs">{iban.iban}</p>
                                  <p className="text-xs text-muted-foreground">{iban.account_holder} — {iban.bank_name || 'N/A'}</p>
                                </div>
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/iban-pool/${iban.id}/assign-client`, { client_id: clientId }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
                                      toast.success('RIB attribué au client');
                                      fetchIbanRequests();
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                                  }}>
                                  <Plus className="w-3 h-3 mr-1" /> Attribuer
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : clientPoolIbans.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Aucun RIB disponible. Créez des RIB depuis Admin {'>'} Gestion RIB.</p>
                      ) : null}
                    </div>
                  )}
                  {/* Create IBAN Form */}
                  {showCreateIbanForm && (
                    <div className="border rounded-lg p-4 mb-6 bg-secondary/10">
                      <h4 className="font-medium mb-4">{editingIbanId ? 'Modifier l\'IBAN' : 'Créer un nouvel IBAN'}</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nom du bénéficiaire *</Label>
                            <Input
                              placeholder="Nom complet"
                              value={ibanValidationData.beneficiary_name}
                              onChange={(e) => setIbanValidationData(prev => ({ ...prev, beneficiary_name: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Banque</Label>
                            <Input
                              placeholder="Nom de la banque"
                              value={ibanValidationData.bank_name}
                              onChange={(e) => setIbanValidationData(prev => ({ ...prev, bank_name: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>IBAN *</Label>
                          <Input
                            placeholder="FR76 1234 5678 9012 3456 7890 123"
                            value={ibanValidationData.iban}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\s/g, '').toUpperCase();
                              const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
                              setIbanValidationData(prev => ({ ...prev, iban: formatted }));
                            }}
                            className="font-mono tracking-wider"
                            maxLength={42}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>BIC/SWIFT *</Label>
                            <Input
                              placeholder="BNPAFRPP"
                              value={ibanValidationData.swift}
                              onChange={(e) => setIbanValidationData(prev => ({ ...prev, swift: e.target.value.toUpperCase() }))}
                              className="font-mono"
                            />
                          </div>
                          <div>
                            <Label>Référence de virement</Label>
                            <Input
                              placeholder="REF-123456"
                              value={ibanValidationData.transfer_reference}
                              onChange={(e) => setIbanValidationData(prev => ({ ...prev, transfer_reference: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="border-t pt-3 mt-3">
                          <p className="text-sm text-muted-foreground mb-2">Adresse (facultatif)</p>
                          <div className="space-y-2">
                            <Input
                              placeholder="Adresse"
                              value={ibanValidationData.address}
                              onChange={(e) => setIbanValidationData(prev => ({ ...prev, address: e.target.value }))}
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Code postal"
                                value={ibanValidationData.postal_code}
                                onChange={(e) => setIbanValidationData(prev => ({ ...prev, postal_code: e.target.value }))}
                              />
                              <Input
                                placeholder="Ville"
                                value={ibanValidationData.city}
                                onChange={(e) => setIbanValidationData(prev => ({ ...prev, city: e.target.value }))}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="border-t pt-3 mt-3">
                          <Label>Logo (facultatif - apparaîtra sur le PDF)</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="mt-1"
                          />
                          {ibanValidationData.logo_base64 && (
                            <div className="mt-2">
                              <img src={ibanValidationData.logo_base64} alt="Logo preview" className="h-12 object-contain" />
                            </div>
                          )}
                        </div>
                        <Button 
                          onClick={handleCreateIban}
                          className="w-full"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {editingIbanId ? 'Enregistrer les modifications' : 'Créer et valider l\'IBAN'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {loadingIban ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : ibanRequests.length === 0 && !showCreateIbanForm ? (
                    <p className="text-muted-foreground text-center py-8">
                      Aucune demande d'IBAN
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {ibanRequests.map(iban => (
                        <div key={iban.id} className="border rounded-lg p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CreditCard className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium">Demande IBAN</p>
                                <p className="text-xs text-muted-foreground">
                                  Créée le {new Date(iban.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <Badge variant={
                              iban.status === 'validated' ? 'default' :
                              iban.status === 'pending' ? 'secondary' :
                              iban.status === 'expired' ? 'outline' : 'destructive'
                            }>
                              {iban.status === 'validated' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {iban.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {iban.status === 'expired' && <AlertCircle className="w-3 h-3 mr-1" />}
                              {iban.status === 'deleted' && <XCircle className="w-3 h-3 mr-1" />}
                              {iban.status === 'validated' ? 'Validé' :
                               iban.status === 'pending' ? 'En attente' :
                               iban.status === 'expired' ? 'Expiré' : 'Supprimé'}
                            </Badge>
                            {iban.status === 'validated' && (
                              <Button size="sm" variant={iban.visible === false ? 'destructive' : 'outline'} className="text-xs" onClick={async () => {
                                try { await axios.put(`${API}/iban/${iban.id}/visibility`, { visible: iban.visible === false }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(iban.visible === false ? 'IBAN visible' : 'IBAN masqué'); fetchIbanRequests(); } catch { toast.error('Erreur'); }
                              }}>{iban.visible === false ? <><EyeOff className="w-3 h-3 mr-1" />Masqué</> : <><Eye className="w-3 h-3 mr-1" />Visible</>}</Button>
                            )}
                          </div>
                          
                          {iban.status === 'validated' ? (
                            <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
                              {iban.logo_base64 && (
                                <div className="flex justify-center pb-2 border-b border-border/30">
                                  <img src={iban.logo_base64} alt="Logo banque" className="h-10 object-contain" />
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Bénéficiaire</p>
                                  <p className="font-medium">{iban.beneficiary_name}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Banque</p>
                                  <p className="font-medium">{iban.bank_name || '-'}</p>
                                </div>
                              </div>
                              {iban.transfer_reference && (
                                <div>
                                  <p className="text-muted-foreground text-sm">Référence de virement</p>
                                  <p className="font-medium text-blue-500">{iban.transfer_reference}</p>
                                </div>
                              )}
                              {(iban.address || iban.postal_code || iban.city) && (
                                <div>
                                  <p className="text-muted-foreground text-sm">Adresse</p>
                                  <p className="font-medium">
                                    {iban.address && <span>{iban.address}<br/></span>}
                                    {iban.postal_code} {iban.city}
                                  </p>
                                </div>
                              )}
                              <div>
                                <p className="text-muted-foreground text-sm">IBAN</p>
                                <p className="font-mono font-bold text-lg">
                                  {iban.iban?.replace(/(.{4})/g, '$1 ').trim()}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-sm">BIC/SWIFT</p>
                                <p className="font-mono font-medium">{iban.swift}</p>
                              </div>
                            </div>
                          ) : iban.status === 'pending' && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Nom du bénéficiaire *</Label>
                                  <Input
                                    placeholder="Nom complet"
                                    value={selectedIbanRequest === iban.id ? ibanValidationData.beneficiary_name : ''}
                                    onChange={(e) => {
                                      setSelectedIbanRequest(iban.id);
                                      setIbanValidationData(prev => ({ ...prev, beneficiary_name: e.target.value }));
                                    }}
                                    onFocus={() => setSelectedIbanRequest(iban.id)}
                                  />
                                </div>
                                <div>
                                  <Label>Banque</Label>
                                  <Input
                                    placeholder="Nom de la banque"
                                    value={selectedIbanRequest === iban.id ? ibanValidationData.bank_name : ''}
                                    onChange={(e) => {
                                      setSelectedIbanRequest(iban.id);
                                      setIbanValidationData(prev => ({ ...prev, bank_name: e.target.value }));
                                    }}
                                    onFocus={() => setSelectedIbanRequest(iban.id)}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>IBAN *</Label>
                                <Input
                                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                                  value={selectedIbanRequest === iban.id ? ibanValidationData.iban : ''}
                                  onChange={(e) => {
                                    setSelectedIbanRequest(iban.id);
                                    setIbanValidationData(prev => ({ ...prev, iban: e.target.value.toUpperCase() }));
                                  }}
                                  onFocus={() => setSelectedIbanRequest(iban.id)}
                                  className="font-mono"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>BIC/SWIFT *</Label>
                                  <Input
                                    placeholder="BNPAFRPP"
                                    value={selectedIbanRequest === iban.id ? ibanValidationData.swift : ''}
                                    onChange={(e) => {
                                      setSelectedIbanRequest(iban.id);
                                      setIbanValidationData(prev => ({ ...prev, swift: e.target.value.toUpperCase() }));
                                    }}
                                    onFocus={() => setSelectedIbanRequest(iban.id)}
                                    className="font-mono"
                                  />
                                </div>
                                <div>
                                  <Label>Référence de virement</Label>
                                  <Input
                                    placeholder="REF-123456"
                                    value={selectedIbanRequest === iban.id ? ibanValidationData.transfer_reference : ''}
                                    onChange={(e) => {
                                      setSelectedIbanRequest(iban.id);
                                      setIbanValidationData(prev => ({ ...prev, transfer_reference: e.target.value }));
                                    }}
                                    onFocus={() => setSelectedIbanRequest(iban.id)}
                                  />
                                </div>
                              </div>
                              <div className="border-t pt-3 mt-3">
                                <p className="text-sm text-muted-foreground mb-2">Adresse (facultatif)</p>
                                <div className="space-y-2">
                                  <Input
                                    placeholder="Adresse"
                                    value={selectedIbanRequest === iban.id ? ibanValidationData.address : ''}
                                    onChange={(e) => {
                                      setSelectedIbanRequest(iban.id);
                                      setIbanValidationData(prev => ({ ...prev, address: e.target.value }));
                                    }}
                                    onFocus={() => setSelectedIbanRequest(iban.id)}
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="Code postal"
                                      value={selectedIbanRequest === iban.id ? ibanValidationData.postal_code : ''}
                                      onChange={(e) => {
                                        setSelectedIbanRequest(iban.id);
                                        setIbanValidationData(prev => ({ ...prev, postal_code: e.target.value }));
                                      }}
                                      onFocus={() => setSelectedIbanRequest(iban.id)}
                                    />
                                    <Input
                                      placeholder="Ville"
                                      value={selectedIbanRequest === iban.id ? ibanValidationData.city : ''}
                                      onChange={(e) => {
                                        setSelectedIbanRequest(iban.id);
                                        setIbanValidationData(prev => ({ ...prev, city: e.target.value }));
                                      }}
                                      onFocus={() => setSelectedIbanRequest(iban.id)}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="border-t pt-3 mt-3">
                                <Label>Logo (facultatif - apparaîtra sur le PDF)</Label>
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    setSelectedIbanRequest(iban.id);
                                    handleLogoUpload(e);
                                  }}
                                  className="mt-1"
                                />
                                {selectedIbanRequest === iban.id && ibanValidationData.logo_base64 && (
                                  <div className="mt-2">
                                    <img src={ibanValidationData.logo_base64} alt="Logo preview" className="h-12 object-contain" />
                                  </div>
                                )}
                              </div>
                              <Button 
                                onClick={() => handleValidateIban(iban.id)}
                                className="w-full"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Valider l'IBAN
                              </Button>
                            </div>
                          )}
                          
                          <div className="flex justify-end gap-2 pt-2 border-t">
                            {iban.status === 'validated' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateIbanStatus(iban.id, 'expired')}
                              >
                                <AlertCircle className="w-4 h-4 mr-1" />
                                Marquer expiré
                              </Button>
                            )}
                            {iban.status === 'expired' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateIbanStatus(iban.id, 'validated')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Réactiver
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIbanValidationData({
                                  beneficiary_name: iban.beneficiary_name || '',
                                  iban: iban.iban || '',
                                  swift: iban.swift || '',
                                  bank_name: iban.bank_name || '',
                                  address: iban.address || '',
                                  postal_code: iban.postal_code || '',
                                  city: iban.city || '',
                                  transfer_reference: iban.transfer_reference || '',
                                  logo_base64: iban.logo_base64 || '',
                                });
                                setEditingIbanId(iban.id);
                                setShowCreateIbanForm(true);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteIban(iban.id)}
                            >
                              <Trash className="w-4 h-4 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TRANSACTIONS TAB */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Gestion des Transactions</CardTitle>
                    <CardDescription>Créez et gérez les transactions du client</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowTransactionModal(true)} 
                    style={{ backgroundColor: primaryColor }}
                    data-testid="new-transaction-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Nouvelle Transaction
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Transaction Creation Modal/Form */}
                {showTransactionModal && (
                  <Card className="mb-6 border-2 border-primary/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Créer une transaction</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Description *</Label>
                          <Input
                            value={newTransaction.description}
                            onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Ex: Dépôt initial, Bonus bienvenue..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={newTransaction.transaction_date}
                            onChange={(e) => setNewTransaction(prev => ({ ...prev, transaction_date: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Montant (€) *</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={newTransaction.amount_input ?? newTransaction.amount}
                            onChange={(e) => {
                              const val = e.target.value.replace(',', '.');
                              setNewTransaction(prev => ({ 
                                ...prev, 
                                amount_input: e.target.value,
                                amount: parseFloat(val) || prev.amount
                              }));
                            }}
                            onBlur={(e) => {
                              const val = parseNumberOnBlur(e.target.value);
                              setNewTransaction(prev => ({ ...prev, amount: val, amount_input: undefined }));
                            }}
                            onFocus={handleInputFocus}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select
                            value={newTransaction.transaction_type}
                            onValueChange={(v) => setNewTransaction(prev => ({ ...prev, transaction_type: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {transactionTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.isCredit ? '↑ ' : '↓ '}{type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Statut</Label>
                          <Select
                            value={newTransaction.status}
                            onValueChange={(v) => setNewTransaction(prev => ({ ...prev, status: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {transactionStatuses.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Devise</Label>
                          <Select
                            value={newTransaction.currency}
                            onValueChange={(v) => setNewTransaction(prev => ({ ...prev, currency: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="EUR">EUR (€)</SelectItem>
                              <SelectItem value="USD">USD ($)</SelectItem>
                              <SelectItem value="GBP">GBP (£)</SelectItem>
                              <SelectItem value="CHF">CHF</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Notes internes (non visibles par le client)</Label>
                        <Input
                          value={newTransaction.notes}
                          onChange={(e) => setNewTransaction(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Notes pour l'équipe..."
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={newTransaction.visible_to_client}
                          onCheckedChange={(checked) => setNewTransaction(prev => ({ ...prev, visible_to_client: checked }))}
                        />
                        <Label>Visible par le client</Label>
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button onClick={handleCreateTransaction} disabled={saving} style={{ backgroundColor: accentColor }}>
                          {saving ? 'Création...' : 'Créer la transaction'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowTransactionModal(false)}>
                          Annuler
                        </Button>
                      </div>
                      <div className="p-3 rounded-lg bg-secondary/30 text-sm text-muted-foreground">
                        <strong>Note:</strong> Si le statut est "Validé", "Confirmé", "Payée" ou "Terminé", les fonds seront automatiquement crédités/débités du compte client.
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Transactions List */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Description</th>
                        <th className="text-left py-3 px-2">Type</th>
                        <th className="text-right py-3 px-2">Montant</th>
                        <th className="text-center py-3 px-2">Statut</th>
                        <th className="text-center py-3 px-2">Visible</th>
                        <th className="text-center py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-8 text-muted-foreground">
                            Aucune transaction
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx) => {
                          const customTypeInfo = tx.transaction_type === 'transfert_credit'
                            ? { label: 'Crédit transfert' }
                            : null;
                          const typeInfo = transactionTypes.find(t => t.value === tx.transaction_type) || customTypeInfo;
                          const statusInfo = transactionStatuses.find(s => s.value === tx.status);
                          // Format date properly
                          const formatDate = (dateStr) => {
                            if (!dateStr) return '-';
                            try {
                              const date = new Date(dateStr);
                              return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            } catch {
                              return dateStr;
                            }
                          };
                          return (
                            <tr key={tx.id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-3 px-2 text-muted-foreground">{formatDate(tx.transaction_date || tx.created_at)}</td>
                              <td className="py-3 px-2">
                                <span className="font-medium">{tx.description}</span>
                                {tx.notes && <p className="text-xs text-muted-foreground">{tx.notes}</p>}
                              </td>
                              <td className="py-3 px-2">
                                <Badge variant={typeInfo?.isCredit ? 'default' : 'destructive'} className={typeInfo?.isCredit ? 'bg-green-600' : 'bg-red-600'}>
                                  {typeInfo?.label || tx.transaction_type}
                                </Badge>
                              </td>
                              <td className="py-3 px-2 text-right font-mono" style={{ color: typeInfo?.isCredit ? accentColor : '#ef5350' }}>
                                {typeInfo?.isCredit ? '+' : '-'}{tx.amount?.toLocaleString()} {tx.currency}
                              </td>
                              <td className="py-3 px-2 text-center">
                                {['souscription', 'subscription', 'retrait_livret', 'ajout_livret'].includes(tx.transaction_type) ? (
                                  <Badge variant="outline" className="text-muted-foreground text-xs">
                                    {statusInfo?.label || tx.status}
                                  </Badge>
                                ) : (
                                <Select
                                  value={tx.status}
                                  onValueChange={(v) => handleUpdateTransactionStatus(tx.id, v)}
                                >
                                  <SelectTrigger className="w-36">
                                    <SelectValue>{statusInfo?.label}</SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {transactionStatuses.map(status => (
                                      <SelectItem key={status.value} value={status.value}>
                                        {status.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                )}
                              </td>
                              {/* Colonne Visible */}
                              <td className="py-3 px-2 text-center">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className={tx.visible_to_client !== false ? 'text-green-500 hover:text-green-700' : 'text-muted-foreground hover:text-white'}
                                  onClick={async () => {
                                    try {
                                      const newVisible = tx.visible_to_client === false ? true : false;
                                      await axios.put(`${API}/transactions/${clientId}/${tx.id}`, 
                                        { visible_to_client: newVisible },
                                        { headers: { Authorization: `Bearer ${token}` }}
                                      );
                                      toast.success(newVisible ? 'Transaction visible' : 'Transaction masquée');
                                      fetchClientData(true);
                                    } catch (error) {
                                      toast.error('Erreur lors de la modification');
                                    }
                                  }}
                                  title={tx.visible_to_client !== false ? 'Cliquer pour masquer au client' : 'Cliquer pour rendre visible au client'}
                                >
                                  {tx.visible_to_client !== false ? (
                                    <Eye className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4 opacity-30" />
                                  )}
                                </Button>
                              </td>
                              {/* Colonne Actions */}
                              <td className="py-3 px-2 text-center flex gap-1">
                                <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700"
                                  onClick={() => setEditingTx({...tx})} data-testid={`edit-tx-${tx.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-100"
                                  onClick={() => handleDeleteTransaction(tx.id, tx.description, tx.amount)} data-testid={`delete-tx-${tx.id}`}>
                                  <Trash className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Placement Subscriptions Section */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Souscriptions Placements</CardTitle>
                    <CardDescription>Gérez les souscriptions aux produits d&apos;investissement</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowSavingsGuide(true)}
                      data-testid="savings-guide-btn"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Guide
                    </Button>
                    <Button
                      onClick={async () => {
                        setRetroactiveForm({
                          product_id: '', name: '', amount: '', rentability_rate: '', rentability_variable: '',
                          start_date: '', duration_months: 12, rentability_period: 'Mensuel',
                          interest_period: 'Mensuel', interest_compound: false,
                          interest_payout_target: 'livret', fund_availability: 'immediate',
                          fund_guarantee: 'total', contract_option: 'none', contract_file: null,
                          movements: []
                        });
                        // Load products if not already loaded
                        if (!allProducts || allProducts.length === 0) {
                          try {
                            const res = await axios.get(`${API}/placements/products`, { headers: { Authorization: `Bearer ${token}` } });
                            setAllProducts(res.data || []);
                          } catch (e) { console.error('Failed to load products:', e); }
                        }
                        setShowRetroactiveModal(true);
                      }}
                      variant="outline"
                      style={{ borderColor: primaryColor, color: primaryColor }}
                      data-testid="create-retroactive-savings-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Epargne retroactive
                    </Button>
                    <Button
                      onClick={() => {
                        setCustomSavingsForm({
                          name: '', amount: '', rentability_rate: '', rentability_variable: '',
                          start_date: new Date().toISOString().slice(0, 10), end_date: '',
                          rentability_period: 'Mensuel', interest_period: 'Mensuel',
                          fund_availability: 'immediate', fund_guarantee: 'total',
                          terms_conditions: '', interest_compound: false, interest_payout_target: 'available',
                          duration_months: 12
                        });
                        setShowCustomSavingsModal(true);
                      }}
                      style={{ backgroundColor: primaryColor }}
                      data-testid="create-custom-savings-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Épargne sur mesure
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {placementSubscriptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucune souscription de placement pour ce client
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Produit</th>
                          <th className="text-left py-3 px-2">Montant</th>
                          <th className="text-left py-3 px-2">Durée</th>
                          <th className="text-left py-3 px-2">Rentabilité</th>
                          <th className="text-left py-3 px-2">Date</th>
                          <th className="text-left py-3 px-2">Prochain versement</th>
                          <th className="text-left py-3 px-2">Statut</th>
                          <th className="text-right py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {placementSubscriptions.map((sub) => (
                          <tr key={sub.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2 font-medium">{sub.product_name || sub.product?.name || 'Produit'}</td>
                            <td className="py-3 px-2">{sub.amount?.toLocaleString('fr-FR')} €</td>
                            <td className="py-3 px-2">{sub.duration_months} mois</td>
                            <td className="py-3 px-2">
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                {sub.rentability_rate}%
                              </Badge>
                            </td>
                            <td className="py-3 px-2">
                              <span className="text-xs">{new Date(sub.created_at).toLocaleDateString('fr-FR')}</span>
                              {sub.activated_at && (
                                <p className="text-[10px] text-green-500 mt-0.5">Validé le {new Date(sub.activated_at).toLocaleDateString('fr-FR')}</p>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              {sub.next_interest_date && sub.status === 'active' ? (
                                <span className="text-blue-500 text-xs font-medium">
                                  {new Date(sub.next_interest_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <Badge 
                                variant={sub.status === 'active' ? 'default' : sub.status === 'pending' ? 'secondary' : 'outline'}
                                className={
                                  sub.status === 'active' ? 'bg-green-500' : 
                                  sub.status === 'pending' ? 'bg-orange-500' : 
                                  sub.status === 'pending_signature' ? 'bg-amber-500' : ''
                                }
                              >
                                {sub.status === 'pending_signature' ? 'En attente de signature' : 
                                 sub.status === 'pending' ? 'Signé — à valider' : 
                                 sub.status === 'active' ? 'Actif' : 
                                 sub.status === 'cancelled' ? 'Annulé' : sub.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex items-center justify-end gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openPlacementEditModal(sub)}
                                  data-testid={`edit-placement-${sub.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Editer
                                </Button>
                                {sub.status === 'active' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-orange-600 border-orange-600 hover:bg-orange-50"
                                      onClick={() => setLivretTransferModal({ open: true, type: 'withdraw', sub, amount: '', loading: false })}
                                      data-testid={`withdraw-from-livret-${sub.id}`}
                                    >
                                      <ArrowUpCircle className="w-4 h-4 mr-1" />
                                      Retrait
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                      onClick={() => setLivretTransferModal({ open: true, type: 'deposit', sub, amount: '', loading: false })}
                                      data-testid={`deposit-to-livret-${sub.id}`}
                                    >
                                      <ArrowDownCircle className="w-4 h-4 mr-1" />
                                      Ajout fonds
                                    </Button>
                                  </>
                                )}
                                {(sub.status === 'pending' || sub.status === 'pending_signature') && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-600 border-green-600 hover:bg-green-50"
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API}/placements/subscriptions/${sub.id}`, { status: 'active', apply_to_capital: true }, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          toast.success('Souscription activée');
                                          fetchClientData(true);
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Fonds insuffisants pour activer ce placement.');
                                        }
                                      }}
                                      data-testid={`activate-placement-${sub.id}`}
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-1" />
                                      Valider
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-600 hover:bg-red-50"
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API}/placements/subscriptions/${sub.id}`, { status: 'cancelled' }, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          toast.success('Souscription annulée');
                                          fetchClientData(true);
                                        } catch (err) {
                                          toast.error('Erreur lors de l\'annulation');
                                        }
                                      }}
                                      data-testid={`cancel-placement-${sub.id}`}
                                    >
                                      <XCircle className="w-4 h-4 mr-1" />
                                      Refuser
                                    </Button>
                                  </>
                                )}
                                {(sub.status === 'active' || sub.status === 'pending') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        const res = await axios.get(`${API}/placements/subscriptions/${sub.id}/contract`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob'
                                        });
                                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                        window.open(url, '_blank');
                                      } catch { toast.error('Erreur téléchargement contrat'); }
                                    }}
                                    data-testid={`download-contract-${sub.id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    Contrat
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={async () => {
                                    if (!window.confirm('Supprimer définitivement ce contrat de placement ?')) return;
                                    try {
                                      await axios.delete(`${API}/placements/subscriptions/${sub.id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('Contrat supprimé');
                                      fetchClientData(true);
                                    } catch (err) {
                                      console.error('Delete error:', err);
                                      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
                                    }
                                  }}
                                  data-testid={`delete-placement-${sub.id}`}
                                >
                                  <Trash className="w-4 h-4 mr-1" />
                                  Supprimer
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUTO TRADING TAB - FOR ANALYST ONLY */}
          <TabsContent value="trading">
            {/* Funds Reminder Banner */}
            <Card className="mb-6 border-2" style={{ borderColor: primaryColor + '40' }}>
              <CardContent className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor + '20' }}>
                      <Wallet className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Fonds Disponibles</p>
                      <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                        {((client.portfolios || []).find(p => p.portfolio_type === 'available')?.available || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-purple-500/20">
                      <TrendingUp className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-sm text-purple-400">Portefeuille Trading</p>
                      <p className="text-2xl font-bold text-purple-500">
                        {(((client.portfolios || []).find(p => p.portfolio_type === 'trading')
                          || (client.portfolios || []).find(p => p.portfolio_type === 'trade'))?.available
                          || ((client.portfolios || []).find(p => p.portfolio_type === 'trading')
                            || (client.portfolios || []).find(p => p.portfolio_type === 'trade'))?.balance
                          || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex flex-col justify-center">
                    <p className="text-sm text-muted-foreground">Capital investi</p>
                    <p className="text-lg font-semibold">
                      {((client.portfolios || []).find(p => p.portfolio_type === 'available')?.capital || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center border-t pt-2">
                  Le trading utilise les fonds du <span className="text-purple-500 font-medium">Portefeuille Trading</span>. Le client doit d'abord transférer ses fonds depuis son espace.
                </p>
              </CardContent>
            </Card>

            {/* Method selector */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={tradingMethod === 'method1' ? 'default' : 'outline'}
                onClick={() => setTradingMethod('method1')}
                style={tradingMethod === 'method1' ? { backgroundColor: primaryColor } : {}}
                data-testid="trading-method1-btn"
              >
                Méthode 1 — Montant fixe
              </Button>
              <Button
                variant={tradingMethod === 'method2' ? 'default' : 'outline'}
                onClick={() => setTradingMethod('method2')}
                style={tradingMethod === 'method2' ? { backgroundColor: primaryColor } : {}}
                data-testid="trading-method2-btn"
              >
                Méthode 2 — Intérêts composés
              </Button>
              <Button
                variant={tradingMethod === 'method3' ? 'default' : 'outline'}
                onClick={() => setTradingMethod('method3')}
                style={tradingMethod === 'method3' ? { backgroundColor: primaryColor } : {}}
                data-testid="trading-method3-btn"
              >
                Méthode 3 — Pilotage
              </Button>
              <Button
                variant={tradingMethod === 'method4' ? 'default' : 'outline'}
                onClick={() => setTradingMethod('method4')}
                style={tradingMethod === 'method4' ? { backgroundColor: primaryColor } : {}}
                data-testid="trading-method4-btn"
              >
                Méthode 4 — Ratio
              </Button>
            </div>

            {tradingMethod === 'method4' ? (
              <Method4Bias clientId={clientId} token={token} primaryColor={primaryColor} />
            ) : tradingMethod === 'method3' ? (
              <Method3Piloting clientId={clientId} token={token} primaryColor={primaryColor} />
            ) : tradingMethod === 'method2' ? (
              /* ====== METHOD 2: COMPOUND INTEREST ====== */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Intérêts composés
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Rendement journalier cible (%)</Label>
                      <Input type="number" step="0.01" value={compoundConfig.daily_rate}
                        onChange={(e) => setCompoundConfig(p => ({...p, daily_rate: e.target.value}))}
                        data-testid="compound-daily-rate" />
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div className="p-2 rounded bg-muted/30 text-center">
                          <p>Semaine estimée</p>
                          <p className="text-emerald-400 font-bold">{((parseFloat(compoundConfig.daily_rate) || 0) * 7).toFixed(2)}%</p>
                        </div>
                        <div className="p-2 rounded bg-muted/30 text-center">
                          <p>Mois estimé</p>
                          <p className="text-emerald-400 font-bold">{((parseFloat(compoundConfig.daily_rate) || 0) * 30).toFixed(2)}%</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Positions min/jour</Label>
                        <Input type="number" value={compoundConfig.min_positions} min={1} max={10}
                          onChange={(e) => setCompoundConfig(p => ({...p, min_positions: parseInt(e.target.value) || 1}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Positions max/jour</Label>
                        <Input type="number" value={compoundConfig.max_positions} min={1} max={10}
                          onChange={(e) => setCompoundConfig(p => ({...p, max_positions: parseInt(e.target.value) || 4}))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Heure début</Label>
                        <Input type="time" value={compoundConfig.trading_hour_start}
                          onChange={(e) => setCompoundConfig(p => ({...p, trading_hour_start: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Heure fin</Label>
                        <Input type="time" value={compoundConfig.trading_hour_end}
                          onChange={(e) => setCompoundConfig(p => ({...p, trading_hour_end: e.target.value}))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Date début</Label>
                        <Input type="date" value={compoundConfig.start_date}
                          onChange={(e) => setCompoundConfig(p => ({...p, start_date: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Date fin</Label>
                        <Input type="date" value={compoundConfig.end_date}
                          onChange={(e) => setCompoundConfig(p => ({...p, end_date: e.target.value}))} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Catégories</Label>
                      <div className="flex flex-wrap gap-2">
                        {['crypto', 'bourse', 'matiere', 'devise', 'biotech'].map(cat => (
                          <label key={cat} className="flex items-center gap-1">
                            <Checkbox
                              checked={compoundConfig.categories.includes(cat)}
                              onCheckedChange={(checked) => {
                                setCompoundConfig(p => ({
                                  ...p,
                                  categories: checked ? [...p.categories, cat] : p.categories.filter(c => c !== cat)
                                }));
                              }}
                            />
                            <span className="text-xs capitalize">{cat === 'matiere' ? 'Matières' : cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2 pt-2">
                      <Button className="w-full" onClick={() => handleCompoundSimulate(false)} disabled={simulationLoading}
                        style={{ backgroundColor: primaryColor }} data-testid="compound-simulate-btn">
                        {simulationLoading ? 'Simulation...' : 'Prévisualiser'}
                      </Button>
                      {compoundPreview && (
                        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" 
                          onClick={() => handleCompoundSimulate(true)} disabled={simulationLoading}
                          data-testid="compound-execute-btn">
                          GÉNÉRER {compoundPreview.total_positions} positions
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Preview panel */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Aperçu intérêts composés</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {compoundPreview ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="p-3 rounded bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">Capital initial</p>
                            <p className="font-bold">{compoundPreview.starting_balance?.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div className="p-3 rounded bg-emerald-500/10 text-center">
                            <p className="text-xs text-muted-foreground">Capital final</p>
                            <p className="font-bold text-emerald-500">{compoundPreview.ending_balance?.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div className="p-3 rounded bg-emerald-500/10 text-center">
                            <p className="text-xs text-muted-foreground">Profit total</p>
                            <p className="font-bold text-emerald-500">+{compoundPreview.total_profit?.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div className="p-3 rounded bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">Rendement</p>
                            <p className="font-bold">+{compoundPreview.total_return_pct}%</p>
                          </div>
                        </div>

                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-card">
                              <tr className="border-b text-muted-foreground">
                                <th className="text-left py-2 px-2">Date</th>
                                <th className="text-center py-2 px-2">Pos.</th>
                                <th className="text-right py-2 px-2">Solde début</th>
                                <th className="text-right py-2 px-2">Profit</th>
                                <th className="text-right py-2 px-2">Solde fin</th>
                                <th className="text-right py-2 px-2">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {compoundPreview.daily_summary?.map((day, idx) => {
                                const dayPositions = compoundPreview.positions_preview?.filter(p => p.trade_date === day.date) || [];
                                return (
                                  <React.Fragment key={idx}>
                                    <tr className="border-b border-border/20">
                                      <td className="py-1.5 px-2 font-semibold">{day.date}</td>
                                      <td className="py-1.5 px-2 text-center">{day.positions}</td>
                                      <td className="py-1.5 px-2 text-right font-mono">{day.balance_start?.toLocaleString('fr-FR')} €</td>
                                      <td className={`py-1.5 px-2 text-right font-mono font-bold ${day.profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {day.profit >= 0 ? '+' : ''}{day.profit?.toFixed(2)} €
                                      </td>
                                      <td className="py-1.5 px-2 text-right font-mono">{day.balance_end?.toLocaleString('fr-FR')} €</td>
                                      <td className={`py-1.5 px-2 text-right font-mono ${day.return_pct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {day.return_pct >= 0 ? '+' : ''}{day.return_pct}%
                                      </td>
                                    </tr>
                                    {dayPositions.map((pos, pidx) => (
                                      <tr key={`${idx}-${pidx}`} className="border-b border-border/10 text-muted-foreground">
                                        <td className="py-1 px-2 pl-6 text-[10px]">{pos.open_time} → {pos.close_time}</td>
                                        <td className="py-1 px-2 text-center text-[10px]">{pos.symbol}</td>
                                        <td className="py-1 px-2 text-right text-[10px] font-mono">{pos.trade_type}</td>
                                        <td className={`py-1 px-2 text-right text-[10px] font-mono ${pos.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          {pos.profit >= 0 ? '+' : ''}{pos.profit?.toFixed(2)} €
                                        </td>
                                        <td className="py-1 px-2 text-right text-[10px] font-mono">{pos.open_price?.toFixed(2)} → {pos.close_price?.toFixed(2)}</td>
                                        <td className={`py-1 px-2 text-right text-[10px] font-mono ${pos.profit_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          {pos.profit_percentage >= 0 ? '+' : ''}{pos.profit_percentage}%
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p>Configurez les paramètres et cliquez &quot;Prévisualiser&quot;</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
            /* ====== METHOD 1: EXISTING ====== */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Configuration Panel */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Assignation de positions
                  </CardTitle>
                  <CardDescription>
                    Générez des positions sur une période. Le client les verra au fur et à mesure.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Enable/Disable - Controls visibility for client */}
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <Label>Afficher au client</Label>
                      <p className="text-xs text-muted-foreground">Si désactivé, le client ne voit aucune position</p>
                    </div>
                    <Switch
                      checked={autoTradingConfig.enabled}
                      onCheckedChange={(checked) => setAutoTradingConfig(prev => ({ ...prev, enabled: checked }))}
                      data-testid="auto-trading-switch"
                    />
                  </div>

                  {/* Amount */}
                  <div className="space-y-2">
                    <Label>Montant des positions (€) *</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="Ex: 5000"
                      value={autoTradingConfig.amount_input ?? (autoTradingConfig.amount === '' ? '' : autoTradingConfig.amount)}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const normalized = String(rawValue).replace(',', '.').replace(/[^0-9.]/g, '');
                        setAutoTradingConfig(prev => ({ 
                          ...prev, 
                          amount_input: rawValue,
                          amount: rawValue === '' ? '' : (parseFloat(normalized) || '')
                        }));
                      }}
                      onBlur={(e) => {
                        const val = e.target.value === '' ? '' : parseNumberOnBlur(e.target.value);
                        setAutoTradingConfig(prev => ({ ...prev, amount: val, amount_input: undefined }));
                      }}
                      onFocus={handleInputFocus}
                      data-testid="auto-amount-input"
                    />
                  </div>

                  {/* Positions Count */}
                  <div className="space-y-2">
                    <Label>Positions gagnantes *</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex: 40"
                      value={autoTradingConfig.winning_positions === '' ? '' : autoTradingConfig.winning_positions}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                        setAutoTradingConfig(prev => ({ 
                          ...prev, 
                          winning_positions: val,
                          positions_count: (parseInt(val) || 0) + (parseInt(prev.losing_positions) || 0)
                        }));
                      }}
                      onFocus={handleInputFocus}
                      data-testid="auto-winning-positions-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Positions perdantes</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex: 10"
                      value={autoTradingConfig.losing_positions === '' ? '' : autoTradingConfig.losing_positions}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                        setAutoTradingConfig(prev => ({ 
                          ...prev, 
                          losing_positions: val,
                          positions_count: (parseInt(prev.winning_positions) || 0) + (parseInt(val) || 0)
                        }));
                      }}
                      onFocus={handleInputFocus}
                      data-testid="auto-losing-positions-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Total: {(parseInt(autoTradingConfig.winning_positions) || 0) + (parseInt(autoTradingConfig.losing_positions) || 0)} positions
                    </p>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Début période *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal bg-card/50"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {autoTradingConfig.start_date 
                              ? format(new Date(autoTradingConfig.start_date), 'dd/MM/yyyy', { locale: fr })
                              : 'Sélectionner...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={autoTradingConfig.start_date ? new Date(autoTradingConfig.start_date) : undefined}
                            onSelect={(date) => setAutoTradingConfig(prev => ({ 
                              ...prev, 
                              start_date: date ? format(date, 'yyyy-MM-dd') : '' 
                            }))}
                            locale={fr}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Fin de période</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal bg-card/50"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {autoTradingConfig.end_date 
                              ? format(new Date(autoTradingConfig.end_date), 'dd/MM/yyyy', { locale: fr })
                              : 'Sélectionner...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={autoTradingConfig.end_date ? new Date(autoTradingConfig.end_date) : undefined}
                            onSelect={(date) => setAutoTradingConfig(prev => ({ 
                              ...prev, 
                              end_date: date ? format(date, 'yyyy-MM-dd') : '' 
                            }))}
                            locale={fr}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Trading Hours */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Heure début</Label>
                      <Input type="time" value={autoTradingConfig.trading_hour_start || '08:30'}
                        onChange={(e) => setAutoTradingConfig(prev => ({...prev, trading_hour_start: e.target.value}))}
                        data-testid="m1-hour-start" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Heure fin</Label>
                      <Input type="time" value={autoTradingConfig.trading_hour_end || '20:00'}
                        onChange={(e) => setAutoTradingConfig(prev => ({...prev, trading_hour_end: e.target.value}))}
                        data-testid="m1-hour-end" />
                    </div>
                  </div>



                <div className="flex flex-wrap gap-2">
                  {quickRangeOptions.map(option => (
                    <Button
                      key={option.label}
                      variant="outline"
                      size="sm"
                      onClick={() => applyQuickRange(option)}
                      data-testid={`quick-range-${option.label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>

                  {/* Category - Multiple Selection */}
                  <div className="space-y-2">
                    <Label>Catégories (sélection multiple)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'crypto', label: 'Crypto' },
                        { value: 'bourse', label: 'Bourse' },
                        { value: 'matiere', label: 'Matières premières' },
                        { value: 'devise', label: 'Devises' },
                      ].map(cat => {
                        const categories = autoTradingConfig.categories || [autoTradingConfig.category || 'crypto'];
                        const isSelected = categories.includes(cat.value);
                        return (
                          <Button
                            key={cat.value}
                            variant={isSelected ? 'default' : 'outline'}
                            size="sm"
                            className={isSelected ? '' : 'opacity-60'}
                            onClick={() => {
                              const current = autoTradingConfig.categories || [autoTradingConfig.category || 'crypto'];
                              const newCats = isSelected 
                                ? current.filter(c => c !== cat.value)
                                : [...current, cat.value];
                              setAutoTradingConfig(prev => ({ 
                                ...prev, 
                                categories: newCats.length > 0 ? newCats : ['crypto']
                              }));
                            }}
                            style={isSelected ? { backgroundColor: primaryColor } : {}}
                          >
                            {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                            {cat.label}
                          </Button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {(autoTradingConfig.categories || [autoTradingConfig.category]).join(', ')}
                    </p>
                  </div>

                  {/* Target Profit - Choice between Euro and Percentage */}
                  <div className="space-y-3">
                    <Label>Objectif de profit</Label>
                    
                    {/* Toggle between Euro and Percent */}
                    <div className="flex gap-2 p-1 bg-secondary/30 rounded-lg">
                      <Button
                        type="button"
                        variant={autoTradingConfig.profit_mode === 'euro' ? 'default' : 'ghost'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setAutoTradingConfig(prev => ({ ...prev, profit_mode: 'euro' }))}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        En Euros (€)
                      </Button>
                      <Button
                        type="button"
                        variant={autoTradingConfig.profit_mode === 'percent' ? 'default' : 'ghost'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setAutoTradingConfig(prev => ({ ...prev, profit_mode: 'percent' }))}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        En % du montant
                      </Button>
                    </div>

                    {/* Euro Input */}
                    {autoTradingConfig.profit_mode === 'euro' && (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="Ex: 272.72"
                          value={autoTradingConfig.profit_euro_input ?? (autoTradingConfig.target_profit === '' ? '' : autoTradingConfig.target_profit)}
                          onChange={(e) => {
                            const val = e.target.value.replace(',', '.');
                            const numVal = e.target.value === '' ? '' : (parseFloat(val) || 0);
                            const amount = parseFloat(autoTradingConfig.amount) || 0;
                            const percent = amount > 0 && numVal !== '' ? (numVal / amount) * 100 : '';
                            setAutoTradingConfig(prev => ({ 
                              ...prev, 
                              profit_euro_input: e.target.value,
                              target_profit: numVal,
                              target_profit_percent: percent === '' ? '' : percent.toFixed(2)
                            }));
                          }}
                          onBlur={(e) => {
                            const val = e.target.value === '' ? '' : parseNumberOnBlur(e.target.value);
                            setAutoTradingConfig(prev => ({ ...prev, target_profit: val, profit_euro_input: undefined }));
                          }}
                          onFocus={handleInputFocus}
                          data-testid="auto-profit-input"
                        />
                        {autoTradingConfig.target_profit !== '' && autoTradingConfig.target_profit !== 0 && (
                          <p className="text-xs text-muted-foreground">
                            {autoTradingConfig.target_profit >= 0 
                              ? <span className="text-green-500">↑ Profit: +{autoTradingConfig.target_profit}€ ({parseFloat(autoTradingConfig.amount) > 0 ? ((autoTradingConfig.target_profit / parseFloat(autoTradingConfig.amount)) * 100).toFixed(2) : 0}%)</span>
                              : <span className="text-red-500">↓ Perte: {autoTradingConfig.target_profit}€</span>
                            }
                          </p>
                        )}
                      </div>
                    )}

                    {/* Percentage Input */}
                    {autoTradingConfig.profit_mode === 'percent' && (
                      <div className="space-y-2">
                        <div className="relative">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="Ex: 2.26"
                            value={autoTradingConfig.profit_percent_input ?? (autoTradingConfig.target_profit_percent === '' ? '' : autoTradingConfig.target_profit_percent)}
                            onChange={(e) => {
                              const val = e.target.value.replace(',', '.');
                              const numVal = e.target.value === '' ? '' : (parseFloat(val) || 0);
                              const amount = parseFloat(autoTradingConfig.amount) || 0;
                              const euroVal = numVal !== '' ? (numVal * amount) / 100 : '';
                              setAutoTradingConfig(prev => ({ 
                                ...prev, 
                                profit_percent_input: e.target.value,
                                target_profit_percent: numVal,
                                target_profit: euroVal === '' ? '' : parseFloat(euroVal.toFixed(2))
                              }));
                            }}
                            onBlur={(e) => {
                              const val = e.target.value === '' ? '' : parseNumberOnBlur(e.target.value);
                              setAutoTradingConfig(prev => ({ ...prev, target_profit_percent: val, profit_percent_input: undefined }));
                            }}
                            onFocus={handleInputFocus}
                            data-testid="auto-profit-percent-input"
                            className="pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                        </div>
                        {autoTradingConfig.target_profit_percent !== '' && autoTradingConfig.target_profit_percent !== 0 && (
                          <p className="text-xs text-muted-foreground">
                            {autoTradingConfig.target_profit_percent >= 0 
                              ? <span className="text-green-500">↑ Profit: +{autoTradingConfig.target_profit_percent}% = {((parseFloat(autoTradingConfig.target_profit_percent) * parseFloat(autoTradingConfig.amount)) / 100).toFixed(2)}€</span>
                              : <span className="text-red-500">↓ Perte: {autoTradingConfig.target_profit_percent}%</span>
                            }
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Profit Info */}
                  <div className="p-3 rounded-lg bg-secondary/30 text-sm text-muted-foreground">
                    <p>Les gains seront crédités aux fonds disponibles du client au fur et à mesure que les positions arrivent à leur date de clôture.</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-4">
                    <Button 
                      className="w-full" 
                      onClick={() => setShowTradingGuide(true)}
                      variant="outline"
                      data-testid="trading-guide-btn"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Guide du trading automatique
                    </Button>
                    <Button 
                      className="w-full" 
                      onClick={handleSaveAutoTrading}
                      disabled={saving}
                      variant="outline"
                      data-testid="save-auto-trading-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                    <Button 
                      className="w-full" 
                      onClick={handleSimulateAutoTrading}
                      disabled={simulationLoading || !autoTradingConfig.start_date}
                      variant="outline"
                      data-testid="simulate-auto-trading-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {simulationLoading ? 'Simulation...' : 'Prévisualiser'}
                    </Button>
                    <Button 
                      className="w-full" 
                      onClick={handleExecuteAutoTrading}
                      disabled={executingTrade || !autoTradingConfig.start_date}
                      style={{ backgroundColor: primaryColor }}
                      data-testid="execute-auto-trading-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {executingTrade ? 'Génération...' : 'GÉNÉRER'}
                    </Button>
                  </div>

                  {!autoTradingConfig.start_date && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <AlertCircle className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-yellow-500">Sélectionnez une date de début</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Positions List - Shows all positions including future ones for admin */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        Positions générées
                        {!autoTradingConfig.enabled && (
                          <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                            Masqué au client
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        {autoTradingConfig.enabled 
                          ? "Le client voit les positions dont la date est passée"
                          : "Le trading est désactivé - le client ne voit rien"
                        }
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Profit total (filtré)</p>
                      <p className="text-2xl font-bold" style={{ color: positionsTotalProfit >= 0 ? accentColor : '#ef5350' }}>
                        {positionsTotalProfit >= 0 ? '+' : ''}{positionsTotalProfit.toFixed(2)} €
                      </p>
                    </div>
                  </div>
                  
                  {/* Filter tabs and summary */}
                  <div className="mt-4 flex flex-wrap items-center gap-4">
                    <div className="flex gap-2">
                      <Button
                        variant={positionsStatusFilter === '' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setPositionsStatusFilter(''); setPositionsPage(1); }}
                        data-testid="filter-all"
                      >
                        <Filter className="w-4 h-4 mr-1" />
                        Toutes ({positionsSummary.all})
                        <span className={`ml-2 text-xs ${positionsTotalProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {positionsTotalProfit >= 0 ? '+' : ''}{positionsTotalProfit?.toFixed(2)}€
                        </span>
                      </Button>
                      <Button
                        variant={positionsStatusFilter === 'closed' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setPositionsStatusFilter('closed'); setPositionsPage(1); }}
                        className={positionsStatusFilter === 'closed' ? '' : 'text-green-500 border-green-500/50'}
                        data-testid="filter-closed"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Fermées ({positionsSummary.closed || positionsSummary.visible || 0})
                        <span className={`ml-2 text-xs ${(positionsSummary.closed_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(positionsSummary.closed_profit || 0) >= 0 ? '+' : ''}{(positionsSummary.closed_profit || 0).toFixed(2)}€
                        </span>
                      </Button>
                      <Button
                        variant={positionsStatusFilter === 'pending' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setPositionsStatusFilter('pending'); setPositionsPage(1); }}
                        className={positionsStatusFilter === 'pending' ? '' : 'text-yellow-500 border-yellow-500/50'}
                        data-testid="filter-pending"
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        En attente ({positionsSummary.pending})
                        <span className={`ml-2 text-xs ${(positionsSummary.pending_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(positionsSummary.pending_profit || 0) >= 0 ? '+' : ''}{(positionsSummary.pending_profit || 0).toFixed(2)}€
                        </span>
                      </Button>
                    </div>
                    
                    {/* Bulk Actions */}
                    {selectedPositions.length > 0 && (
                      <div className="flex gap-2 mt-4 p-3 rounded-lg bg-secondary/50">
                        <span className="text-sm text-muted-foreground flex items-center mr-2">
                          {selectedPositions.length} sélectionnée(s)
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-500 border-green-500/50"
                          onClick={handleBulkValidatePositions}
                          disabled={saving}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider tout
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-500/50"
                          onClick={handleBulkDeletePositions}
                          disabled={saving}
                        >
                          <Trash className="w-4 h-4 mr-1" />
                          Supprimer tout
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedPositions([]); setSelectAll(false); }}
                        >
                          Désélectionner
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {tradePositions.length === 0 ? (
                    <div className="text-center py-12">
                      <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Aucune position {positionsStatusFilter ? 'pour ce filtre' : 'générée'}</p>
                      {!positionsStatusFilter && <p className="text-sm text-muted-foreground">Configurez une période et cliquez sur GÉNÉRER</p>}
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="py-2 px-2 w-10">
                                <input 
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAllPositions}
                                  className="w-4 h-4 rounded cursor-pointer"
                                />
                              </th>
                              <th className="text-left py-2 px-2">Symbol</th>
                              <th className="text-center py-2 px-2">Type</th>
                              <th className="text-left py-2 px-2">Date prévue</th>
                              <th className="text-right py-2 px-2">Capital initial</th>
                              <th className="text-right py-2 px-2">Montant</th>
                              <th className="text-right py-2 px-2">Open</th>
                              <th className="text-right py-2 px-2">Close</th>
                              <th className="text-right py-2 px-2">Profit</th>
                              <th className="text-center py-2 px-2">Statut</th>
                              <th className="text-center py-2 px-2">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tradePositions.map((pos) => {
                              const isSelected = selectedPositions.includes(pos.id);
                              return (
                                <tr key={pos.id} className={`border-b border-border/50 hover:bg-secondary/30 ${isSelected ? 'bg-primary/5' : ''}`}>
                                  <td className="py-2 px-2">
                                    <input 
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => togglePositionSelection(pos.id)}
                                      className="w-4 h-4 rounded cursor-pointer"
                                    />
                                  </td>
                                  <td className="py-2 px-2">
                                    <span className="font-semibold">{pos.symbol}</span>
                                    <br />
                                    <span className="text-xs text-muted-foreground">{pos.asset_name}</span>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${pos.trade_type === 'BUY' ? 'bg-green-600/20 text-green-500' : 'bg-red-600/20 text-red-500'}`}>
                                      {pos.trade_type === 'BUY' ? 'A' : 'V'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-muted-foreground">
                                    {pos.trade_date}
                                    <br />
                                    <span className="text-xs">{pos.open_time} → {pos.close_time}</span>
                                  </td>
                                  <td className="py-2 px-2 text-right font-mono">{pos.capital_initial ? `${pos.capital_initial.toLocaleString('fr-FR')} €` : '—'}</td>
                                  <td className="py-2 px-2 text-right font-mono">{(pos.lot * pos.open_price).toFixed(2)}€</td>
                                  <td className="py-2 px-2 text-right font-mono">{pos.open_price}</td>
                                  <td className="py-2 px-2 text-right font-mono">{pos.close_price || '-'}</td>
                                  <td className="py-2 px-2 text-right font-mono" style={{ color: pos.profit >= 0 ? accentColor : '#ef5350' }}>
                                    {pos.profit >= 0 ? '+' : ''}{pos.profit?.toFixed(2)} €
                                    {pos.credited && <span className="text-xs text-green-500 ml-1">✓</span>}
                                    <br />
                                    <span className="text-xs">{pos.profit_percentage?.toFixed(2)}%</span>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {pos.status === 'closed' ? (
                                      <Badge variant="outline" className="text-green-500 border-green-500">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Validée
                                      </Badge>
                                    ) : pos.status === 'visible' ? (
                                      <Badge className="bg-green-500/20 text-green-400 border border-green-500/50">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Fermée
                                      </Badge>
                                    ) : pos.status === 'pending' ? (
                                      <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                                        <Clock className="w-3 h-3 mr-1" />
                                        En attente
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-blue-500 border-blue-500">
                                        <Play className="w-3 h-3 mr-1" />
                                        Ouvert
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      {pos.status === 'pending' && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-green-500 hover:bg-green-500/10"
                                          onClick={() => handleValidatePosition(pos.id)}
                                          title="Valider et créditer"
                                        >
                                          <CheckCircle className="w-3 h-3" />
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-red-500 hover:bg-red-500/10"
                                        onClick={() => handleDeletePosition(pos.id)}
                                        title="Supprimer"
                                      >
                                        <Trash className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination */}
                      {positionsTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPositionsPage(p => Math.max(1, p - 1))}
                            disabled={positionsPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            Page {positionsPage} sur {positionsTotalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPositionsPage(p => Math.min(positionsTotalPages, p + 1))}
                            disabled={positionsPage === positionsTotalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>

          <TabsContent value="kyc">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents KYC
                </CardTitle>
                <CardDescription>Validez ou refusez les documents soumis par le client</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Client-specific KYC Requirements */}
                <div className="mb-6 p-4 rounded-lg bg-secondary/30 border border-border">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Documents requis pour ce client
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Personnalisez les documents demandés à ce client (par défaut, utilise les paramètres globaux du template)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { type: 'identity', label: "Pièce d'identité (Recto + Verso)", subTypes: ['identity_front', 'identity_back'] },
                      { type: 'proof_address', label: "Justificatif de domicile" },
                      { type: 'iban', label: "IBAN / RIB" },
                      { type: 'proof_income', label: "Justificatif de revenus" },
                      { type: 'bank_statement', label: "Relevé bancaire" },
                      { type: 'tax_return', label: "Avis d'imposition" },
                      { type: 'other', label: "Autre document" }
                    ].map(doc => {
                      const isRequired = doc.subTypes
                        ? doc.subTypes.every(st => (clientKycRequirements || []).some(d => d.type === st))
                        : (clientKycRequirements || []).some(d => d.type === doc.type);
                      return (
                        <label 
                          key={doc.type} 
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${isRequired ? 'bg-green-500/20' : 'hover:bg-secondary'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isRequired}
                            onChange={async (e) => {
                              const currentDocs = clientKycRequirements || [];
                              let newDocs;
                              if (e.target.checked) {
                                if (doc.subTypes) {
                                  const toAdd = doc.subTypes
                                    .filter(st => !currentDocs.some(d => d.type === st))
                                    .map(st => ({ type: st, label: st === 'identity_front' ? "Pièce d'identité — Recto" : "Pièce d'identité — Verso", required: true }));
                                  newDocs = [...currentDocs, ...toAdd];
                                } else {
                                  newDocs = [...currentDocs, { type: doc.type, label: doc.label, required: true }];
                                }
                              } else {
                                if (doc.subTypes) {
                                  newDocs = currentDocs.filter(d => !doc.subTypes.includes(d.type));
                                } else {
                                  newDocs = currentDocs.filter(d => d.type !== doc.type);
                                }
                              }
                              try {
                                await axios.put(`${API}/kyc-requirements/${clientId}`, { documents: newDocs }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setClientKycRequirements(newDocs);
                                toast.success('Documents requis mis à jour');
                              } catch (error) {
                                toast.error('Erreur lors de la mise à jour');
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{doc.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {clientKycRequirements && clientKycRequirements.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      <strong>{clientKycRequirements.length}</strong> document(s) requis : {clientKycRequirements.map(d => d.label).join(', ')}
                    </p>
                  )}
                </div>

                {/* Submitted Documents */}
                <h4 className="font-semibold mb-3">Documents soumis par le client</h4>
                {kycDocs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
                    <p className="text-muted-foreground">Aucun document KYC téléversé</p>
                    <p className="text-sm text-muted-foreground">Le client n'a pas encore soumis de documents</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {kycDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/50">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="w-6 h-6" style={{ color: primaryColor }} />
                          </div>
                          <div>
                            <p className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ')}{doc.identity_subtype === 'passport' ? ' (Passeport)' : doc.identity_subtype === 'ci' ? ' (CI)' : ''}</p>
                            <p className="text-sm text-muted-foreground">{doc.file_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Téléversé le {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                            </p>
                            {doc.file_url && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <button 
                                    className="text-xs underline mt-1 block cursor-pointer"
                                    style={{ color: primaryColor }}
                                  >
                                    Voir le document
                                  </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-4xl max-h-[90vh]">
                                  <DialogHeader>
                                    <DialogTitle>{doc.document_type?.replace(/_/g, ' ')} - {doc.file_name}</DialogTitle>
                                  </DialogHeader>
                                  <div className="mt-4 max-h-[70vh] overflow-auto">
                                    {doc.file_url.startsWith('data:image') ? (
                                      <img 
                                        src={doc.file_url} 
                                        alt={doc.file_name}
                                        className="max-w-full h-auto rounded-lg"
                                      />
                                    ) : doc.file_url.startsWith('data:application/pdf') ? (
                                      <iframe 
                                        src={doc.file_url}
                                        className="w-full h-[70vh] rounded-lg"
                                        title={doc.file_name}
                                      />
                                    ) : (
                                      <div className="text-center py-8">
                                        <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                                        <p className="text-muted-foreground">Aperçu non disponible</p>
                                        <a 
                                          href={doc.file_url}
                                          download={doc.file_name}
                                          className="text-sm underline mt-2 block"
                                          style={{ color: primaryColor }}
                                        >
                                          Télécharger le fichier
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge 
                            variant="outline"
                            className={
                              doc.status === 'approved' ? 'bg-green-500/20 text-green-500 border-green-500/50' :
                              doc.status === 'rejected' ? 'bg-red-500/20 text-red-500 border-red-500/50' :
                              'bg-yellow-500/20 text-yellow-500 border-yellow-500/50'
                            }
                          >
                            {doc.status === 'approved' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" />Validé</>
                            ) : doc.status === 'rejected' ? (
                              <><XCircle className="w-3 h-3 mr-1" />Refusé</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" />En attente</>
                            )}
                          </Badge>
                          {doc.status === 'pending' && (
                            <div className="flex gap-2">
                              {doc.document_type === 'identity_front' ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" style={{ backgroundColor: accentColor }} data-testid={`kyc-approve-identity-${doc.id}`}>
                                      <CheckCircle className="w-4 h-4 mr-1" /> Valider
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleReviewKyc(doc.id, 'approved', '', 'passport')} data-testid="kyc-approve-passport">
                                      Passeport (verso non requis)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleReviewKyc(doc.id, 'approved', '', 'ci')} data-testid="kyc-approve-ci">
                                      Carte d'identité (verso requis)
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleReviewKyc(doc.id, 'approved')}
                                  style={{ backgroundColor: accentColor }}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => {
                                  const reason = prompt('Raison du refus:');
                                  if (reason) handleReviewKyc(doc.id, 'rejected', reason);
                                }}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
                            onClick={() => handleDeleteKyc(doc.id, doc.file_name || doc.document_type)}
                            data-testid={`kyc-delete-${doc.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Connections Tab */}
          <TabsContent value="connections">
            <div className="space-y-6">
              {/* Online Status Card */}
              <Card className={isClientOnline ? "border-green-500/50 bg-green-500/5" : "border-gray-500/30"}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isClientOnline ? (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                          <Wifi className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-green-600">En ligne</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-400" />
                          <WifiOff className="w-5 h-5 text-gray-400" />
                          <span className="font-medium text-muted-foreground">Hors ligne</span>
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {lastSeen && (
                        <span>Dernière activité: {new Date(lastSeen).toLocaleString('fr-FR')}</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sessions History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    Historique des Sessions
                  </CardTitle>
                  <CardDescription>
                    Toutes les connexions du client avec adresse IP et appareil
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingSessions ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : userSessions.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">Aucune session enregistrée</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Statut</th>
                            <th className="text-left p-2">Date/Heure</th>
                            <th className="text-left p-2">Adresse IP</th>
                            <th className="text-left p-2">Appareil</th>
                            <th className="text-left p-2">Navigateur</th>
                            <th className="text-left p-2">Fin de session</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userSessions.map((session) => (
                            <tr key={session.id} className="border-b hover:bg-secondary/30">
                              <td className="p-2">
                                <Badge variant={session.status === 'active' ? 'default' : 'secondary'}>
                                  {session.status === 'active' ? 'Active' : 'Terminée'}
                                </Badge>
                              </td>
                              <td className="p-2 font-mono text-xs">
                                {new Date(session.login_at).toLocaleString('fr-FR')}
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  <Globe className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-mono">{session.ip_address}</span>
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  {session.device_type === 'mobile' ? (
                                    <Smartphone className="w-4 h-4" />
                                  ) : (
                                    <Monitor className="w-4 h-4" />
                                  )}
                                  <span className="capitalize">{session.device_type}</span>
                                </div>
                              </td>
                              <td className="p-2">{session.browser || 'Inconnu'}</td>
                              <td className="p-2 text-muted-foreground text-xs">
                                {session.logout_at ? new Date(session.logout_at).toLocaleString('fr-FR') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Navigation History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Historique de Navigation
                  </CardTitle>
                  <CardDescription>
                    Pages visitées par le client avec date, heure et adresse IP
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingSessions ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : navigationHistory.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">Aucune navigation enregistrée</p>
                  ) : (
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background">
                          <tr className="border-b">
                            <th className="text-left p-2">Date/Heure</th>
                            <th className="text-left p-2">Page visitée</th>
                            <th className="text-left p-2">Adresse IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {navigationHistory.map((view) => (
                            <tr key={view.id} className="border-b hover:bg-secondary/30">
                              <td className="p-2 font-mono text-xs whitespace-nowrap">
                                {new Date(view.timestamp).toLocaleString('fr-FR')}
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-mono text-xs">{view.page_url}</span>
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  <Globe className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-mono text-xs">{view.ip_address}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader><CardTitle>Journal d'activité</CardTitle></CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">{t('no_data')}</p>
                ) : (
                  <div className="space-y-2">
                    {auditLogs.map((log, index) => (
                      <div key={log.id || index} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/30">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                        <div className="flex-1">
                          <p className="text-sm font-medium capitalize">{log.action?.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{log.details}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Statement Tab */}
          <TabsContent value="statement">
            <div className="space-y-6" data-testid="statement-tab">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Relevé de compte
                  </CardTitle>
                  <CardDescription>
                    Générez un relevé PDF complet avec transactions, trading et placements sur une période donnée.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Auto-generate toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                    <div>
                      <p className="text-sm font-semibold">Génération automatique mensuelle</p>
                      <p className="text-xs text-muted-foreground">Un relevé du mois précédent sera généré et partagé avec le client le 1er de chaque mois</p>
                    </div>
                    <button
                      onClick={async () => {
                        const newVal = !client.auto_statement;
                        try {
                          await axios.put(`${API}/clients/${clientId}`, { auto_statement: newVal }, { headers: { Authorization: `Bearer ${token}` } });
                          setClient(prev => ({ ...prev, auto_statement: newVal }));
                          toast.success(newVal ? 'Relevé automatique activé' : 'Relevé automatique désactivé');
                        } catch { toast.error('Erreur'); }
                      }}
                      className={`w-11 h-6 rounded-full transition-colors ${client.auto_statement ? 'bg-green-500' : 'bg-gray-600'}`}
                      data-testid="auto-statement-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${client.auto_statement ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Date de début</Label>
                      <Input type="date" value={statementStartDate || ''} onChange={(e) => setStatementStartDate(e.target.value)} data-testid="statement-start-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <Input type="date" value={statementEndDate || ''} onChange={(e) => setStatementEndDate(e.target.value)} data-testid="statement-end-date" />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          if (!statementStartDate || !statementEndDate) { toast.error('Sélectionnez les dates'); return; }
                          setStatementLoading(true);
                          try {
                            const res = await axios.post(`${API}/exports/account-statement/${clientId}`, {
                              start_date: statementStartDate, end_date: statementEndDate
                            }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 30000 });
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.style.display = 'none';
                            a.href = url;
                            a.download = `Releve-${statementStartDate}-${statementEndDate}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
                            toast.success('Relevé téléchargé');
                          } catch (err) {
                            let msg = 'Erreur génération';
                            try { if (err.response?.data instanceof Blob) { msg = await err.response.data.text(); const j = JSON.parse(msg); msg = j.detail || msg; } else { msg = err.response?.data?.detail || msg; } } catch {}
                            toast.error(msg);
                          }
                          finally { setStatementLoading(false); }
                        }}
                        disabled={statementLoading}
                        style={{ backgroundColor: primaryColor }}
                        data-testid="generate-statement-btn"
                      >
                        {statementLoading ? 'Génération...' : 'Télécharger PDF'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!statementStartDate || !statementEndDate) { toast.error('Sélectionnez les dates'); return; }
                          setStatementLoading(true);
                          try {
                            await axios.post(`${API}/exports/account-statement/${clientId}/share`, {
                              start_date: statementStartDate, end_date: statementEndDate
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Relevé partagé avec le client ! Il recevra une notification.');
                            fetchStatements();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur partage'); }
                          finally { setStatementLoading(false); }
                        }}
                        disabled={statementLoading}
                        data-testid="share-statement-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Partager avec le client
                      </Button>
                    </div>
                  </div>
                  {/* Quick period buttons */}
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return [
                        { label: 'Ce mois', fn: () => { const now = new Date(); setStatementStartDate(fmt(new Date(now.getFullYear(), now.getMonth(), 1))); setStatementEndDate(fmt(now)); }},
                        { label: 'Mois dernier', fn: () => { const now = new Date(); setStatementStartDate(fmt(new Date(now.getFullYear(), now.getMonth()-1, 1))); setStatementEndDate(fmt(new Date(now.getFullYear(), now.getMonth(), 0))); }},
                        { label: '3 derniers mois', fn: () => { const now = new Date(); setStatementStartDate(fmt(new Date(now.getFullYear(), now.getMonth()-3, 1))); setStatementEndDate(fmt(now)); }},
                        { label: '6 derniers mois', fn: () => { const now = new Date(); setStatementStartDate(fmt(new Date(now.getFullYear(), now.getMonth()-6, 1))); setStatementEndDate(fmt(now)); }},
                        { label: 'Cette année', fn: () => { const now = new Date(); setStatementStartDate(`${now.getFullYear()}-01-01`); setStatementEndDate(fmt(now)); }},
                        { label: 'Année dernière', fn: () => { const now = new Date(); setStatementStartDate(`${now.getFullYear()-1}-01-01`); setStatementEndDate(`${now.getFullYear()-1}-12-31`); }},
                      ].map(p => (
                        <Button key={p.label} variant="outline" size="sm" className="text-xs" onClick={p.fn}>{p.label}</Button>
                      ));
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Generated Statements List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Relevés générés ({statementList.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {statementList.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">Aucun relevé partagé avec ce client</p>
                  ) : (
                    <div className="space-y-2">
                      {statementList.map(stmt => (
                        <div key={stmt.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-secondary/30 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded bg-primary/10 flex items-center justify-center">
                              <FileText className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{stmt.label}</p>
                              <p className="text-xs text-muted-foreground">Partagé le {stmt.uploaded_at ? new Date(stmt.uploaded_at).toLocaleDateString('fr-FR') : ''}</p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:bg-red-500/10"
                            onClick={async () => {
                              if (!window.confirm('Supprimer ce relevé ? Le client ne pourra plus le télécharger.')) return;
                              try {
                                await axios.delete(`${API}/kyc/${stmt.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                setStatementList(prev => prev.filter(s => s.id !== stmt.id));
                                toast.success('Relevé supprimé');
                              } catch { toast.error('Erreur suppression'); }
                            }}
                            data-testid={`delete-statement-${stmt.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Placement Access Tab */}
          <TabsContent value="placement-access">
            <div className="space-y-6" data-testid="placement-access-tab">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Accès aux placements
                      </CardTitle>
                      <CardDescription>Masquer des placements ou définir un code d&apos;accès pour ce client</CardDescription>
                    </div>
                    <Button onClick={savePlacementAccess} style={{ backgroundColor: primaryColor }} data-testid="save-placement-access-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {allProducts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>Aucun produit placement disponible</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={fetchPlacementAccess}>Charger</Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allProducts.map(product => {
                        const isHidden = hiddenPlacements.includes(product.id);
                        const hasPassword = !!placementPasswords[product.id];
                        return (
                          <div key={product.id} className={`p-4 rounded-lg border ${isHidden ? 'bg-red-500/5 border-red-500/20' : 'bg-card'}`} data-testid={`placement-access-${product.id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {product.images?.[0] ? (
                                  <img src={product.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                                    <Briefcase className="w-4 h-4 text-primary" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-semibold">{product.name}</p>
                                  <p className="text-xs text-muted-foreground">{product.rentability_rate}% {product.rentability_period} — {product.duration_months} mois</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                {/* Password */}
                                <div className="flex items-center gap-2">
                                  <Lock className={`w-4 h-4 ${hasPassword ? 'text-amber-500' : 'text-muted-foreground/30'}`} />
                                  <Input
                                    type="text"
                                    value={placementPasswords[product.id] || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setPlacementPasswords(prev => {
                                        const next = { ...prev };
                                        if (val) next[product.id] = val;
                                        else delete next[product.id];
                                        return next;
                                      });
                                    }}
                                    placeholder="Code d'accès"
                                    className="w-32 h-8 text-xs"
                                    disabled={isHidden}
                                    data-testid={`placement-password-${product.id}`}
                                  />
                                </div>
                                {/* Hide toggle */}
                                <Button
                                  variant={isHidden ? 'destructive' : 'outline'}
                                  size="sm"
                                  onClick={() => {
                                    setHiddenPlacements(prev =>
                                      prev.includes(product.id)
                                        ? prev.filter(id => id !== product.id)
                                        : [...prev, product.id]
                                    );
                                  }}
                                  data-testid={`placement-hide-${product.id}`}
                                >
                                  {isHidden ? <><EyeOff className="w-4 h-4 mr-1" />Masqué</> : <><Eye className="w-4 h-4 mr-1" />Visible</>}
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Categories Access */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Accès aux catégories
                  </CardTitle>
                  <CardDescription>Masquer des catégories entières ou les protéger par code</CardDescription>
                </CardHeader>
                <CardContent>
                  {allCategories.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">Aucune catégorie</p>
                  ) : (
                    <div className="space-y-3">
                      {allCategories.map(cat => {
                        const isHidden = hiddenCategories.includes(cat.id);
                        const hasPassword = !!categoryPasswords[cat.id];
                        return (
                          <div key={cat.id} className={`p-4 rounded-lg border ${isHidden ? 'bg-red-500/5 border-red-500/20' : 'bg-card'}`} data-testid={`category-access-${cat.id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{cat.title}</p>
                                <p className="text-xs text-muted-foreground">{cat.description || 'Catégorie'}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  <Lock className={`w-4 h-4 ${hasPassword ? 'text-amber-500' : 'text-muted-foreground/30'}`} />
                                  <Input
                                    type="text"
                                    value={categoryPasswords[cat.id] || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setCategoryPasswords(prev => {
                                        const next = { ...prev };
                                        if (val) next[cat.id] = val;
                                        else delete next[cat.id];
                                        return next;
                                      });
                                    }}
                                    placeholder="Code"
                                    className="w-32 h-8 text-xs"
                                    disabled={isHidden}
                                    data-testid={`category-password-${cat.id}`}
                                  />
                                </div>
                                <Button
                                  variant={isHidden ? 'destructive' : 'outline'}
                                  size="sm"
                                  onClick={() => setHiddenCategories(prev => prev.includes(cat.id) ? prev.filter(id => id !== cat.id) : [...prev, cat.id])}
                                  data-testid={`category-hide-${cat.id}`}
                                >
                                  {isHidden ? <><EyeOff className="w-4 h-4 mr-1" />Masqué</> : <><Eye className="w-4 h-4 mr-1" />Visible</>}
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments">
            <Card data-testid="comments-tab">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <StickyNote className="w-5 h-5" />
                      Commentaires
                    </CardTitle>
                    <CardDescription>Historique des commentaires sur ce client</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add comment form */}
                <div className="p-4 rounded-lg border bg-muted/20 space-y-3">
                  <Textarea
                    placeholder="Écrire un commentaire..."
                    value={newNote.content}
                    onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                    rows={3}
                    data-testid="comment-input"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={handleAddNote}
                      disabled={saving || !newNote.content?.trim()}
                      size="sm"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="submit-comment-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </div>

                {/* Comments list */}
                {(client.notes || []).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <StickyNote className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Aucun commentaire</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...(client.notes || [])].reverse().map((note) => {
                      const roleLabel = {
                        'super_admin': 'Super Admin',
                        'admin_ops': 'Admin Ops',
                        'bureau': 'Bureau',
                        'analyst': 'Analyste'
                      }[note.author_role] || note.author_role || 'Admin';
                      const roleColor = {
                        'super_admin': 'bg-red-500/20 text-red-400',
                        'admin_ops': 'bg-blue-500/20 text-blue-400',
                        'analyst': 'bg-purple-500/20 text-purple-400'
                      }[note.author_role] || 'bg-muted text-muted-foreground';
                      
                      return (
                        <div key={note.id} className="p-4 rounded-lg border bg-card" data-testid={`comment-${note.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                                {(note.author_name || 'A').split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm">{note.author_name || 'Admin'}</span>
                                  <Badge variant="outline" className={`text-[10px] ${roleColor}`}>{roleLabel}</Badge>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(note.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-400 h-7 w-7 p-0"
                              onClick={async () => {
                                if (!window.confirm('Supprimer ce commentaire ?')) return;
                                try {
                                  await axios.delete(`${API}/clients/${clientId}/notes/${note.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Commentaire supprimé');
                                  fetchClientData(true);
                                } catch { toast.error('Erreur'); }
                              }}
                              data-testid={`delete-comment-${note.id}`}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          <p className="text-sm mt-3 whitespace-pre-wrap">{note.content}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ADVANCES TAB */}
          <TabsContent value="advances">
            <div className="space-y-6" data-testid="advances-tab">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5" />Avances de Fonds</CardTitle>
                    <Button onClick={() => { setEditAdvanceId(null); setAdvanceForm({ amount: '', date: new Date().toISOString().slice(0, 10), due_date: '', penalty_rate: '', penalty_period: 'month' }); setAdvanceDialog(true); }} style={{ backgroundColor: primaryColor }} data-testid="create-advance-btn">
                      <Plus className="w-4 h-4 mr-2" />Nouvelle avance
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {advances.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8" onClick={fetchAdvances}>Aucune avance de fonds. Cliquez pour charger.</p>
                  ) : (
                    <div className="space-y-4">
                      {advances.map(adv => {
                        const pct = adv.amount > 0 ? ((adv.amount - adv.remaining) / adv.amount * 100) : 0;
                        const statusColors = { pending_signature: 'bg-yellow-500/20 text-yellow-400', signed: 'bg-blue-500/20 text-blue-400', active: 'bg-emerald-500/20 text-emerald-400', repaid: 'bg-gray-500/20 text-gray-400', cancelled: 'bg-red-500/20 text-red-400' };
                        const statusLabels = { pending_signature: 'En attente signature', signed: 'Signée', active: 'Active', repaid: 'Remboursée', cancelled: 'Annulée' };
                        return (
                          <Card key={adv.id} className="bg-card/50">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <p className="text-lg font-bold">{adv.amount?.toLocaleString('fr-FR')} €</p>
                                  <p className="text-xs text-muted-foreground">Créée le {adv.date} · Ref: AVF-{adv.id?.slice(0, 8).toUpperCase()}</p>
                                </div>
                                <Badge className={statusColors[adv.status] || ''}>{statusLabels[adv.status] || adv.status}</Badge>
                              </div>
                              {adv.status === 'active' && (
                                <div className="mb-3">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span>Remboursé: {(adv.amount - adv.remaining)?.toLocaleString('fr-FR')} €</span>
                                    <span>Reste: {adv.remaining?.toLocaleString('fr-FR')} €</span>
                                  </div>
                                  <div className="w-full h-2 bg-secondary rounded-full"><div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
                                </div>
                              )}
                              {(adv.repayments || []).length > 0 && (
                                <div className="text-xs text-muted-foreground mb-2">
                                  {adv.repayments.map((r, i) => (<span key={i} className="mr-3">Remb. {r.amount?.toLocaleString('fr-FR')} € ({new Date(r.date).toLocaleDateString('fr-FR')})</span>))}
                                </div>
                              )}
                              <div className="flex gap-2">
                                {adv.status !== 'repaid' && adv.status !== 'cancelled' && (
                                  <Button size="sm" variant="outline" onClick={() => { setEditAdvanceId(adv.id); setAdvanceForm({ amount: String(adv.amount), date: adv.date, due_date: adv.due_date || '', penalty_rate: String(adv.penalty_rate || ''), penalty_period: adv.penalty_period || 'month' }); setAdvanceDialog(true); }}>Modifier</Button>
                                )}
                                {adv.status === 'signed' && <Button size="sm" onClick={() => handleApproveAdvance(adv.id)} style={{ backgroundColor: primaryColor }}>Valider & créditer</Button>}
                                {adv.status === 'active' && <Button size="sm" variant="outline" onClick={() => { setRepayAdvance(adv); setRepayAmount(''); setRepayDialog(true); }}>Remboursement</Button>}
                                <Button size="sm" variant="outline" onClick={() => window.open(`${API}/advances/${adv.id}/contract?token=${token}`, '_blank')}>Contrat PDF</Button>
                                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteAdvance(adv.id)}><Trash className="w-4 h-4" /></Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Create advance dialog */}
              <Dialog open={advanceDialog} onOpenChange={setAdvanceDialog}>
                <DialogContent className="max-w-sm">
                  <DialogHeader><DialogTitle>{editAdvanceId ? 'Modifier l\'avance' : 'Nouvelle avance de fonds'}</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Montant (€) *</Label><Input type="number" step="0.01" autoComplete="off" value={advanceForm.amount} onChange={e => setAdvanceForm(p => ({ ...p, amount: e.target.value }))} placeholder="5000" data-testid="advance-amount" /></div>
                    <div><Label>Date de l'avance</Label><Input type="date" autoComplete="off" value={advanceForm.date} onChange={e => setAdvanceForm(p => ({ ...p, date: e.target.value }))} data-testid="advance-date" /></div>
                    <div><Label>Date limite de remboursement *</Label><Input type="date" autoComplete="off" value={advanceForm.due_date} onChange={e => setAdvanceForm(p => ({ ...p, due_date: e.target.value }))} data-testid="advance-due-date" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Pénalités de retard (%)</Label><Input type="number" step="0.1" min="0" autoComplete="off" value={advanceForm.penalty_rate} onChange={e => setAdvanceForm(p => ({ ...p, penalty_rate: e.target.value }))} placeholder="Ex: 5" /></div>
                      <div><Label>Par</Label>
                        <Select value={advanceForm.penalty_period} onValueChange={v => setAdvanceForm(p => ({ ...p, penalty_period: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="day">Jour</SelectItem>
                            <SelectItem value="week">Semaine</SelectItem>
                            <SelectItem value="month">Mois</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => { setAdvanceDialog(false); setEditAdvanceId(null); }}>Annuler</Button><Button onClick={handleCreateAdvance} style={{ backgroundColor: primaryColor }}>{editAdvanceId ? 'Enregistrer' : 'Créer'}</Button></DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Repayment dialog */}
              <Dialog open={repayDialog} onOpenChange={setRepayDialog}>
                <DialogContent className="max-w-sm">
                  <DialogHeader><DialogTitle>Remboursement</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="p-3 bg-secondary/30 rounded-lg text-sm">
                      <p>Avance: {repayAdvance?.amount?.toLocaleString('fr-FR')} €</p>
                      <p>Reste à rembourser: <span className="font-bold">{repayAdvance?.remaining?.toLocaleString('fr-FR')} €</span></p>
                    </div>
                    <div><Label>Montant du remboursement (€)</Label><Input type="number" step="0.01" value={repayAmount} onChange={e => setRepayAmount(e.target.value)} placeholder="1000" /></div>
                    {repayAmount && parseFloat(repayAmount) > (repayAdvance?.remaining || 0) && <p className="text-xs text-red-400">Dépasse le reste à rembourser</p>}
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setRepayDialog(false)}>Annuler</Button><Button onClick={handleRepayAdvance} disabled={!repayAmount || parseFloat(repayAmount) > (repayAdvance?.remaining || 0)}>Rembourser</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* MANDATES TAB */}
          <TabsContent value="mandates">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Mandats de Gestion</CardTitle>
                    <Button onClick={() => setMandateDialog(true)} style={{ backgroundColor: primaryColor }}><Plus className="w-4 h-4 mr-2" />Nouveau mandat</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {mandates.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Aucun mandat de gestion</p>
                  ) : (
                    <div className="space-y-3">
                      {mandates.map(m => {
                        const statusColors = { pending_signature: 'bg-yellow-500/20 text-yellow-400', signed: 'bg-blue-500/20 text-blue-400', active: 'bg-emerald-500/20 text-emerald-400', cancelled: 'bg-red-500/20 text-red-400' };
                        const statusLabels = { pending_signature: 'En attente signature', signed: 'Signée', active: 'Actif', cancelled: 'Annulé' };
                        return (
                          <Card key={m.id} className="bg-card/50">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold">Mandat de Gestion</p>
                                  <p className="text-xs text-muted-foreground">Ref: MDG-{m.id?.slice(0,8).toUpperCase()} · Créé le {m.date}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={statusColors[m.status] || ''}>{statusLabels[m.status] || m.status}</Badge>
                                  {m.status === 'signed' && <Button size="sm" onClick={() => handleApproveMandate(m.id)} style={{ backgroundColor: primaryColor }}>Valider</Button>}
                                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/mandates/${m.id}/contract?token=${token}`, '_blank')}>PDF</Button>
                                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteMandate(m.id)}><Trash className="w-4 h-4" /></Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Dialog open={mandateDialog} onOpenChange={setMandateDialog}>
                <DialogContent className="max-w-sm">
                  <DialogHeader><DialogTitle>Créer un mandat de gestion</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Un mandat de gestion sera créé pour ce client. Il devra le signer électroniquement.</p>
                  <DialogFooter><Button variant="outline" onClick={() => setMandateDialog(false)}>Annuler</Button><Button onClick={handleCreateMandate} style={{ backgroundColor: primaryColor }}>Créer</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* ==================== STOCK PORTFOLIO TAB ==================== */}
          <TabsContent value="stock-portfolio">
            <div className="space-y-6">
              {loadingStocks ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Portefeuille d'Actions</h3>
                    <div className="flex gap-2">
                      <Button onClick={() => { setCreditStockForm({ symbol: '', name: '', quantity: '', buy_price: '', use_current: false, use_funds: true, date: '' }); setStockCategory('bourse'); setCreditStockDialog(true); fetchStockPortfolio(); }} style={{ backgroundColor: primaryColor }} data-testid="credit-stock-btn">
                        <Plus className="w-4 h-4 mr-2" />Créditer Actions
                      </Button>
                    </div>
                  </div>

                  {/* Summary */}
                  {stockHoldings.length > 0 && (() => {
                    const totalMarketValue = stockHoldings.reduce((s, h) => {
                      const cp = getStockCurrentPrice(h.symbol, h.category);
                      return s + (cp > 0 ? h.quantity * cp : 0);
                    }, 0);
                    const totalInvested = stockHoldings.reduce((s, h) => s + h.quantity * h.buy_price, 0);
                    const totalPnl = totalMarketValue - totalInvested;
                    const hasPrices = stockHoldings.some(h => getStockCurrentPrice(h.symbol, h.category) > 0);
                    return (
                    <div className="grid grid-cols-4 gap-4">
                      <Card className="bg-card/80"><CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase">Valeur actuelle</p>
                        <p className="text-2xl font-bold">
                          {hasPrices ? totalMarketValue.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : totalInvested.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </p>
                        {hasPrices && (
                          <p className={`text-xs font-semibold mt-1 ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                          </p>
                        )}
                      </CardContent></Card>
                      <Card className="bg-card/80"><CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase">Total investi</p>
                        <p className="text-2xl font-bold">
                          {totalInvested.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </p>
                      </CardContent></Card>
                      <Card className="bg-card/80"><CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase">Fonds disponibles</p>
                        <p className="text-2xl font-bold">{stockClientFunds.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                      </CardContent></Card>
                      <Card className="bg-card/80"><CardContent className="p-4">
                        <p className="text-xs text-muted-foreground uppercase">Actions détenues</p>
                        <p className="text-2xl font-bold">{stockHoldings.length}</p>
                      </CardContent></Card>
                    </div>
                    );
                  })()}

                  {/* Holdings Table */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Actions détenues</CardTitle></CardHeader>
                    <CardContent>
                      {stockHoldings.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">Aucune action dans le portefeuille</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-border/50">
                            <th className="text-left py-2 px-2">Action</th>
                            <th className="text-left py-2 px-2">Marché</th>
                            <th className="text-right py-2 px-2">Quantité</th>
                            <th className="text-right py-2 px-2">Prix d'achat</th>
                            <th className="text-right py-2 px-2">Cours actuel</th>
                            <th className="text-right py-2 px-2">Valeur</th>
                            <th className="text-right py-2 px-2">P&L</th>
                            <th className="text-right py-2 px-2">Actions</th>
                          </tr></thead>
                          <tbody>
                            {stockHoldings.map(h => {
                              const cp = getStockCurrentPrice(h.symbol, h.category);
                              const val = cp > 0 ? h.quantity * cp : h.quantity * h.buy_price;
                              const invested = h.quantity * h.buy_price;
                              const pnl = cp > 0 ? val - invested : 0;
                              const pnlPct = invested > 0 && cp > 0 ? (pnl / invested * 100) : 0;
                              return (
                                <tr key={h.id} className="border-b border-border/20" data-testid={`admin-stock-${h.symbol}`}>
                                  <td className="py-2 px-2">
                                    <span className="font-semibold">{h.symbol}</span>
                                    <span className="text-xs text-muted-foreground ml-1">{h.name}</span>
                                  </td>
                                  <td className="py-2 px-2">
                                    <Badge variant="outline" className="text-[9px]">{h.category === 'euronext' ? 'EURONEXT' : 'US'}</Badge>
                                  </td>
                                  <td className="text-right py-2 px-2 font-mono">{h.quantity}</td>
                                  <td className="text-right py-2 px-2 font-mono">{h.buy_price?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                  <td className="text-right py-2 px-2 font-mono">{cp > 0 ? cp.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '-'}</td>
                                  <td className="text-right py-2 px-2 font-semibold">{val.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                  <td className={`text-right py-2 px-2 font-semibold ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {cp > 0 ? `${pnl >= 0 ? '+' : ''}${pnl.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (${pnlPct.toFixed(1)}%)` : '-'}
                                  </td>
                                  <td className="text-right py-2 px-2">
                                    <div className="flex gap-1 justify-end">
                                      <Button size="sm" variant="outline" className="text-xs h-7" style={{ color: primaryColor, borderColor: primaryColor + '40' }} onClick={() => { setSellStockForm({ symbol: h.symbol, name: h.name, category: h.category, quantity: '', max_qty: h.quantity }); setSellStockDialog(true); }} data-testid={`sell-stock-${h.symbol}`}>
                                        Vendre
                                      </Button>
                                      <Button size="sm" variant="outline" className="text-xs h-7 text-red-400" onClick={() => { setDebitStockForm({ symbol: h.symbol, quantity: '', sell_price: '', date: '' }); setDebitStockDialog(true); }} data-testid={`debit-stock-${h.symbol}`}>
                                        Retirer
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Transaction History */}
                  {stockTransactions.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base">Historique des transactions</CardTitle></CardHeader>
                      <CardContent>
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-border/50">
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Action</th>
                            <th className="text-right py-2 px-2">Quantité</th>
                            <th className="text-right py-2 px-2">Prix</th>
                            <th className="text-right py-2 px-2">Total</th>
                            <th className="text-left py-2 px-2">Par</th>
                            <th className="text-right py-2 px-2"></th>
                          </tr></thead>
                          <tbody>
                            {stockTransactions.map(t => (
                              <tr key={t.id} className="border-b border-border/20">
                                <td className="py-2 px-2 text-muted-foreground text-xs">{new Date(t.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                <td className="py-2 px-2">
                                  <Badge className={t.type === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                                    {t.type === 'buy' ? 'Achat' : 'Vente'}
                                  </Badge>
                                </td>
                                <td className="py-2 px-2 font-semibold">{t.symbol}</td>
                                <td className="text-right py-2 px-2 font-mono">{t.quantity}</td>
                                <td className="text-right py-2 px-2 font-mono">{t.price?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                <td className="text-right py-2 px-2 font-semibold">{t.total?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                <td className="py-2 px-2 text-xs text-muted-foreground">{t.admin_name}</td>
                                <td className="text-right py-2 px-2">
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setEditStockTxn({ ...t }); setEditStockTxnDialog(true); }}><Edit className="w-3 h-3" /></Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Credit Stock Dialog */}
              <Dialog open={creditStockDialog} onOpenChange={setCreditStockDialog}>
                <DialogContent className="max-w-lg" data-testid="credit-stock-dialog">
                  <DialogHeader><DialogTitle>Créditer des Actions</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    {/* Funds available */}
                    <div className="p-2 bg-secondary/20 rounded text-sm text-center">
                      Fonds disponibles client : <span className="font-bold">{stockClientFunds.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                    </div>

                    {/* Category selector */}
                    <div>
                      <Label className="text-xs font-semibold">Marché</Label>
                      <div className="flex gap-2 mt-1">
                        {['bourse', 'euronext'].map(cat => (
                          <Button key={cat} size="sm" variant={stockCategory === cat ? 'default' : 'outline'} onClick={() => { setStockCategory(cat); setCreditStockForm(f => ({ ...f, symbol: '', name: '' })); }} style={stockCategory === cat ? { backgroundColor: primaryColor } : {}}>
                            {cat === 'bourse' ? 'US' : 'EURONEXT'}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Stock selector */}
                    <div>
                      <Label className="text-xs font-semibold">Action</Label>
                      <Select value={creditStockForm.symbol} onValueChange={v => {
                        const list = stockCategory === 'euronext' ? stockPricesEU : stockPricesUS;
                        const found = list.find(s => s.symbol === v);
                        setCreditStockForm(f => ({ ...f, symbol: v, name: found?.name || v }));
                      }}>
                        <SelectTrigger data-testid="stock-select"><SelectValue placeholder="Sélectionner une action" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {(stockCategory === 'euronext' ? stockPricesEU : stockPricesUS).map(s => (
                            <SelectItem key={s.symbol} value={s.symbol}>
                              {s.symbol} — {s.name} {s.priceEur ? `(${s.priceEur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €)` : s.price ? `(${s.price.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €)` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Quantity */}
                    <div>
                      <Label className="text-xs font-semibold">Nombre d'actions</Label>
                      <Input type="number" min="1" step="1" value={creditStockForm.quantity} onChange={e => setCreditStockForm(f => ({ ...f, quantity: e.target.value }))} placeholder="100" data-testid="stock-quantity-input" />
                    </div>

                    {/* Buy price */}
                    <div>
                      <Label className="text-xs font-semibold">Prix d'achat unitaire (€)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="number" step="0.01" value={creditStockForm.buy_price} onChange={e => setCreditStockForm(f => ({ ...f, buy_price: e.target.value, use_current: false }))} placeholder="Ex: 45.50" disabled={creditStockForm.use_current} className="flex-1" data-testid="stock-price-input" />
                        <Button size="sm" variant={creditStockForm.use_current ? 'default' : 'outline'} onClick={() => {
                          const cp = getStockCurrentPrice(creditStockForm.symbol, stockCategory);
                          setCreditStockForm(f => ({ ...f, use_current: !f.use_current, buy_price: !f.use_current && cp > 0 ? cp.toFixed(2) : f.buy_price }));
                        }} style={creditStockForm.use_current ? { backgroundColor: primaryColor } : {}} data-testid="use-current-price-btn">
                          Cours actuel
                        </Button>
                      </div>
                    </div>

                    {/* Use funds toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-border/30 bg-secondary/10">
                      <div>
                        <p className="text-sm font-semibold">Utiliser les fonds disponibles</p>
                        <p className="text-xs text-muted-foreground">Déduire le montant des fonds du client</p>
                      </div>
                      <button
                        onClick={() => setCreditStockForm(f => ({ ...f, use_funds: !f.use_funds }))}
                        className={`w-11 h-6 rounded-full transition-colors ${creditStockForm.use_funds ? '' : 'bg-secondary'}`}
                        style={creditStockForm.use_funds ? { backgroundColor: primaryColor } : {}}
                        data-testid="use-funds-toggle"
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${creditStockForm.use_funds ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    {/* Date */}
                    <div>
                      <Label className="text-xs font-semibold">Date d'achat (optionnel)</Label>
                      <Input type="datetime-local" value={creditStockForm.date} onChange={e => setCreditStockForm(f => ({ ...f, date: e.target.value }))} data-testid="stock-date-input" />
                    </div>

                    {/* Summary */}
                    {creditStockForm.symbol && creditStockForm.quantity && (creditStockForm.buy_price || creditStockForm.use_current) && (() => {
                      const unitPrice = creditStockForm.use_current ? getStockCurrentPrice(creditStockForm.symbol, stockCategory) : parseFloat(creditStockForm.buy_price || 0);
                      const totalCost = (parseFloat(creditStockForm.quantity) || 0) * unitPrice;
                      const insufficient = creditStockForm.use_funds && totalCost > stockClientFunds;
                      return (
                        <div className={`p-3 rounded-lg border text-sm space-y-1 ${insufficient ? 'bg-red-500/10 border-red-500/30' : 'bg-secondary/30 border-border/30'}`}>
                          <div className="flex justify-between"><span className="text-muted-foreground">Action</span><span className="font-semibold">{creditStockForm.symbol} ({creditStockForm.name})</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Quantité</span><span className="font-mono">{creditStockForm.quantity}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Prix unitaire</span><span className="font-mono">{unitPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                          <div className="flex justify-between font-bold border-t border-border/30 pt-1"><span>Total</span><span className="font-mono">{totalCost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                          {creditStockForm.use_funds && (
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Après déduction</span><span className={`font-mono ${insufficient ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>{(stockClientFunds - totalCost).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                          )}
                          {insufficient && (
                            <p className="text-xs text-red-400 font-semibold pt-1">Fonds insuffisants ! Disponible : {stockClientFunds.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € — Désactivez "Utiliser les fonds" pour créditer quand même.</p>
                          )}
                          {!creditStockForm.use_funds && (
                            <p className="text-xs text-yellow-400 pt-1">Crédit gratuit — aucune déduction des fonds</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreditStockDialog(false)}>Annuler</Button>
                    <Button onClick={handleCreditStock} style={{ backgroundColor: primaryColor }} data-testid="confirm-credit-stock-btn"
                      disabled={(() => {
                        const unitPrice = creditStockForm.use_current ? getStockCurrentPrice(creditStockForm.symbol, stockCategory) : parseFloat(creditStockForm.buy_price || 0);
                        const totalCost = (parseFloat(creditStockForm.quantity) || 0) * unitPrice;
                        return !creditStockForm.symbol || !creditStockForm.quantity || (creditStockForm.use_funds && totalCost > stockClientFunds);
                      })()}
                    >Créditer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Sell Stock Dialog */}
              <Dialog open={sellStockDialog} onOpenChange={setSellStockDialog}>
                <DialogContent className="max-w-md" data-testid="sell-stock-dialog">
                  <DialogHeader><DialogTitle>Vendre des Actions — {sellStockForm.symbol}</DialogTitle></DialogHeader>
                  {sellStockForm.symbol && (() => {
                    const mp = getStockCurrentPrice(sellStockForm.symbol, sellStockForm.category);
                    const qty = parseFloat(sellStockForm.quantity) || 0;
                    const totalCredit = qty * mp;
                    return (
                      <div className="space-y-4">
                        <div className="p-3 rounded-lg bg-secondary/30 text-center">
                          <p className="text-xs text-muted-foreground">Cours du marché</p>
                          <p className="text-2xl font-bold font-mono">{mp > 0 ? mp.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '-'} €</p>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold">Nombre d'actions à vendre</Label>
                          <Input type="number" min="1" max={sellStockForm.max_qty} value={sellStockForm.quantity} onChange={e => setSellStockForm(f => ({ ...f, quantity: e.target.value }))} placeholder={`Max: ${sellStockForm.max_qty}`} data-testid="sell-stock-quantity" />
                          <div className="flex justify-between mt-1">
                            <p className="text-xs text-muted-foreground">Détenu : {sellStockForm.max_qty} actions</p>
                            <button className="text-xs underline" style={{ color: primaryColor }} onClick={() => setSellStockForm(f => ({ ...f, quantity: String(f.max_qty) }))}>TOUT</button>
                          </div>
                          {qty > sellStockForm.max_qty && <p className="text-xs text-red-400 font-semibold mt-1">Quantité supérieure au détenu !</p>}
                        </div>
                        {qty > 0 && mp > 0 && (
                          <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-muted-foreground">Vente</span><span className="font-mono">{qty} × {mp.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                            <div className="flex justify-between font-bold text-emerald-400 text-base border-t border-border/30 pt-1"><span>Crédité au client</span><span className="font-mono">+{totalCredit.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                            <p className="text-xs text-muted-foreground">Crédité sur les fonds disponibles du client</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setSellStockDialog(false)}>Annuler</Button>
                    <Button onClick={handleSellStock} style={{ backgroundColor: primaryColor }} data-testid="confirm-sell-stock-btn"
                      disabled={!sellStockForm.quantity || parseFloat(sellStockForm.quantity) <= 0 || parseFloat(sellStockForm.quantity) > sellStockForm.max_qty || getStockCurrentPrice(sellStockForm.symbol, sellStockForm.category) <= 0}
                    >Confirmer la vente</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Debit Stock Dialog */}
              <Dialog open={debitStockDialog} onOpenChange={setDebitStockDialog}>
                <DialogContent className="max-w-md" data-testid="debit-stock-dialog">
                  <DialogHeader><DialogTitle>Retirer des Actions — {debitStockForm.symbol}</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-semibold">Nombre d'actions à retirer</Label>
                      <Input type="number" min="1" value={debitStockForm.quantity} onChange={e => setDebitStockForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Quantité" data-testid="debit-stock-quantity" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Prix de vente unitaire (€)</Label>
                      <Input type="number" step="0.01" value={debitStockForm.sell_price} onChange={e => setDebitStockForm(f => ({ ...f, sell_price: e.target.value }))} placeholder="Ex: 50.00" data-testid="debit-stock-price" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Date (optionnel)</Label>
                      <Input type="datetime-local" value={debitStockForm.date} onChange={e => setDebitStockForm(f => ({ ...f, date: e.target.value }))} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDebitStockDialog(false)}>Annuler</Button>
                    <Button onClick={handleDebitStock} variant="destructive" data-testid="confirm-debit-stock-btn">Retirer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Edit Transaction Dialog */}
              <Dialog open={editStockTxnDialog} onOpenChange={setEditStockTxnDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Modifier la transaction</DialogTitle></DialogHeader>
                  {editStockTxn && (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs font-semibold">Date et heure</Label>
                        <Input type="datetime-local" value={editStockTxn.date?.slice(0, 16)} onChange={e => setEditStockTxn(t => ({ ...t, date: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Prix unitaire (€)</Label>
                        <Input type="number" step="0.01" value={editStockTxn.price} onChange={e => setEditStockTxn(t => ({ ...t, price: e.target.value }))} />
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditStockTxnDialog(false)}>Annuler</Button>
                    <Button onClick={handleEditStockTxn} style={{ backgroundColor: primaryColor }}>Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
        </Tabs>
        <Dialog open={showTradingGuide} onOpenChange={setShowTradingGuide}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trading-guide-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Guide — Trading Automatique
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4 text-sm">

              <div className="p-4 rounded-lg border bg-purple-500/5 border-purple-500/20">
                <h3 className="font-bold text-purple-400 mb-3">PRINCIPE</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Le trading automatique génère des <b className="text-foreground">positions de trading simulées</b> pour le client. Les positions sont planifiées entre la date de début et la date de fin, avec des horaires réalistes.</p>
                  <p>Les positions sont d&apos;abord en statut <b className="text-foreground">&quot;Pending&quot;</b> (invisibles pour le client). Quand elles arrivent à leur date de clôture, elles passent automatiquement en <b className="text-foreground">&quot;Closed&quot;</b> et le profit est crédité au portefeuille du client.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <h3 className="font-bold text-blue-400 mb-3">PARAMÈTRES</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Catégories</b> : Choisissez les marchés (Crypto, Bourse, Matières premières, Devises, Biotech). Les positions seront générées sur des actifs de ces catégories.</p>
                  <p><b className="text-foreground">Montant total</b> : Le capital total à répartir entre toutes les positions. Chaque position aura un montant proportionnel avec une variation aléatoire réaliste.</p>
                  <p><b className="text-foreground">Positions gagnantes / perdantes</b> : Nombre de positions qui seront en profit (gagnantes) et en perte (perdantes). Ex: 5 gagnantes + 1 perdante = 6 positions au total.</p>
                  <p><b className="text-foreground">Date de début / fin</b> : La plage de dates sur laquelle les positions seront programmées. Les horaires sont générés aléatoirement pendant les heures de marché.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                <h3 className="font-bold text-emerald-400 mb-3">OBJECTIF DE PROFIT</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Mode Euro</b> : Vous définissez un objectif de profit en euros (ex: 50€). Le système ajuste les profits de chaque position pour que le total atteigne cet objectif.</p>
                  <p><b className="text-foreground">Mode Pourcentage</b> : Vous définissez un pourcentage de profit (ex: 10%). Le profit cible est calculé sur le montant total (10% de 500€ = 50€).</p>
                  <p className="text-xs text-amber-400 mt-2">Le profit est réparti de manière réaliste entre les positions, avec des variations naturelles. Les positions perdantes ont un profit négatif.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-amber-500/5 border-amber-500/20">
                <h3 className="font-bold text-amber-400 mb-3">LES 3 BOUTONS D&apos;ACTION</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Enregistrer</b> : Sauvegarde la configuration (catégories, montant, etc.) sans générer de positions. Utile pour préparer et revenir plus tard.</p>
                  <p><b className="text-foreground">Prévisualiser</b> : Génère les positions en mode simulation. Vous voyez un aperçu complet (symboles, prix, profits, dates) <b>sans rien créer</b>. Vous pouvez ensuite les valider ou régénérer.</p>
                  <p><b className="text-foreground">GÉNÉRER</b> : Crée directement les positions en base de données. Elles seront traitées automatiquement à leur date de clôture.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-rose-500/5 border-rose-500/20">
                <h3 className="font-bold text-rose-400 mb-3">FLUX APRÈS GÉNÉRATION</h3>
                <div className="space-y-2 text-muted-foreground">
                  <ol className="list-decimal ml-4 space-y-2">
                    <li><b className="text-foreground">Positions créées</b> → statut &quot;Pending&quot; (invisibles pour le client)</li>
                    <li><b className="text-foreground">Date de clôture atteinte</b> → le scheduler automatique (chaque minute) les passe en &quot;Closed&quot; et crédite le profit au portefeuille du client</li>
                    <li><b className="text-foreground">Côté client</b> → les positions apparaissent dans &quot;Salle des marchés&quot; / &quot;Historique&quot; avec le profit affiché</li>
                  </ol>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-muted/30">
                <h3 className="font-bold mb-3">BONNES PRATIQUES</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Diversifiez les catégories</b> : Sélectionnez 2-3 catégories pour un portefeuille réaliste.</p>
                  <p><b className="text-foreground">Ajoutez des perdantes</b> : 1-2 positions perdantes sur 6-8 rendent le résultat plus crédible.</p>
                  <p><b className="text-foreground">Utilisez Prévisualiser</b> : Vérifiez toujours avant de générer définitivement.</p>
                  <p><b className="text-foreground">Dates réalistes</b> : Espacez les dates de début et fin de quelques jours à quelques semaines.</p>
                  <p><b className="text-foreground">Quick ranges</b> : Les boutons &quot;Aujourd&apos;hui&quot;, &quot;Cette semaine&quot;, &quot;Ce mois&quot; remplissent automatiquement les dates.</p>
                </div>
              </div>

            </div>
            <DialogFooter>
              <Button onClick={() => setShowTradingGuide(false)}>Compris</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Savings Guide Modal */}
        <Dialog open={showSavingsGuide} onOpenChange={setShowSavingsGuide}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="savings-guide-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <AlertCircle className="w-5 h-5 text-blue-500" />
                Guide — Création d&apos;une épargne sur mesure
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4 text-sm">

              <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <h3 className="font-bold text-blue-400 mb-3">INFORMATIONS DE BASE</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Nom de l&apos;épargne</b> : Le nom du contrat qui sera affiché au client (ex: &quot;CryptoBoost Performance+&quot;, &quot;Livret Premium Gold&quot;).</p>
                  <p><b className="text-foreground">Montant (€)</b> : Le capital investi par le client.</p>
                  <p><b className="text-foreground">Taux fixe (%)</b> : Le taux de rendement garanti. C&apos;est le taux principal utilisé pour calculer les intérêts.</p>
                  <p><b className="text-foreground">Taux variable (%)</b> : Optionnel. Si renseigné, le contrat affiche &quot;de X% à Y%&quot;. Le calcul se fait sur le taux fixe.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                <h3 className="font-bold text-emerald-400 mb-3">DATES ET PÉRIODES</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Date de début</b> : Quand le contrat démarre. Les intérêts commencent à courir à partir de cette date.</p>
                  <p><b className="text-foreground">Date de fin / Durée</b> : Vous pouvez soit fixer une date de fin, soit une durée en mois. Si les deux sont renseignés, la date de fin prime.</p>
                  <p><b className="text-foreground">Période de rentabilité</b> : Sur quelle base le taux est exprimé. Ex: 9.75% <b>Mensuel</b> = le client touche 9.75% de son capital chaque mois. 7.5% <b>Annuel</b> = 7.5% par an.</p>
                  <p><b className="text-foreground">Période d&apos;intérêts</b> : La fréquence à laquelle les intérêts sont versés au client. Mensuel = chaque mois, Trimestriel = tous les 3 mois, Fin de contrat = tout à la fin.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-amber-500/5 border-amber-500/20">
                <h3 className="font-bold text-amber-400 mb-3">FONDS ET GARANTIES</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Disponibilité des fonds</b> :</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><b>Immédiate</b> : le client peut retirer ses fonds à tout moment</li>
                    <li><b>À échéance</b> : les fonds sont bloqués jusqu&apos;à la date de fin du contrat</li>
                    <li><b>À terme</b> : les fonds sont bloqués pour une durée fixe</li>
                  </ul>
                  <p className="mt-2"><b className="text-foreground">Garantie des fonds</b> :</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><b>Non couvert</b> : aucune garantie sur le capital</li>
                    <li><b>Partielle (capital)</b> : seul le capital initial est garanti</li>
                    <li><b>Totale (capital + intérêts)</b> : le capital et les intérêts sont garantis à 100%</li>
                  </ul>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-purple-500/5 border-purple-500/20">
                <h3 className="font-bold text-purple-400 mb-3">INTÉRÊTS ET VERSEMENT</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p><b className="text-foreground">Gains cumulé (intérêts composés)</b> : Si coché, les intérêts de chaque période sont ajoutés au capital, et les intérêts suivants sont calculés sur le nouveau capital (effet boule de neige). N&apos;a d&apos;effet que si le versement est sur &quot;Capital du livret&quot;.</p>
                  <p><b className="text-foreground">Versement des intérêts</b> :</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><b>Fonds disponibles</b> : les intérêts sont versés dans le portefeuille du client. Il peut les retirer. Le capital du livret ne bouge pas.</li>
                    <li><b>Capital du livret</b> : les intérêts s&apos;ajoutent au capital. Ex: 10 000€ + 500€ d&apos;intérêts = livret passe à 10 500€.</li>
                  </ul>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-rose-500/5 border-rose-500/20">
                <h3 className="font-bold text-rose-400 mb-3">FLUX DE VALIDATION</h3>
                <div className="space-y-2 text-muted-foreground">
                  <ol className="list-decimal ml-4 space-y-2">
                    <li><b className="text-foreground">Vous créez l&apos;épargne</b> → Le contrat apparaît chez le client en statut &quot;À signer&quot;</li>
                    <li><b className="text-foreground">Le client signe</b> → Il voit le récapitulatif (rendement, durée, modalités), renseigne sa ville, signe électroniquement, accepte les conditions → Le contrat passe en &quot;En attente de validation&quot;</li>
                    <li><b className="text-foreground">Vous validez</b> → Le contrat passe en statut &quot;Actif&quot; et les intérêts commencent à être calculés</li>
                  </ol>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-muted/30">
                <h3 className="font-bold mb-3">CONDITIONS GÉNÉRALES (CONTRAT)</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Le champ &quot;Conditions générales&quot; permet de personnaliser le texte du contrat PDF.</p>
                  <p>Si vous le laissez <b>vide</b>, un contrat standard avec 10 articles juridiques sera généré automatiquement avec les paramètres de l&apos;épargne.</p>
                  <p>Si vous écrivez du texte, il remplacera les articles standard. Séparez chaque article par une ligne vide. Les lignes commençant par &quot;ARTICLE&quot; seront en gras.</p>
                </div>
              </div>

            </div>
            <DialogFooter>
              <Button onClick={() => setShowSavingsGuide(false)}>Compris</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Savings Modal */}
        <Dialog open={showCustomSavingsModal} onOpenChange={setShowCustomSavingsModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="custom-savings-modal">
            <DialogHeader>
              <DialogTitle>Création d&apos;une épargne sur mesure</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l&apos;épargne</Label>
                  <Input value={customSavingsForm.name} onChange={(e) => setCustomSavingsForm(p => ({ ...p, name: e.target.value }))} placeholder="CryptoBoost Performance+" data-testid="cs-name" />
                </div>
                <div className="space-y-2">
                  <Label>Montant (€)</Label>
                  <Input type="number" value={customSavingsForm.amount} onChange={(e) => setCustomSavingsForm(p => ({ ...p, amount: e.target.value }))} placeholder="10000" data-testid="cs-amount" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Taux fixe (%)</Label>
                  <Input type="number" step="0.01" value={customSavingsForm.rentability_rate} onChange={(e) => setCustomSavingsForm(p => ({ ...p, rentability_rate: e.target.value }))} placeholder="9.75" data-testid="cs-rate" />
                </div>
                <div className="space-y-2">
                  <Label>Taux variable (%)</Label>
                  <Input type="number" step="0.01" value={customSavingsForm.rentability_variable} onChange={(e) => setCustomSavingsForm(p => ({ ...p, rentability_variable: e.target.value }))} placeholder="0" data-testid="cs-variable" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input type="date" value={customSavingsForm.start_date} onChange={(e) => setCustomSavingsForm(p => ({ ...p, start_date: e.target.value }))} data-testid="cs-start" />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input type="date" value={customSavingsForm.end_date} onChange={(e) => setCustomSavingsForm(p => ({ ...p, end_date: e.target.value }))} data-testid="cs-end" />
                </div>
                <div className="space-y-2">
                  <Label>Durée (mois)</Label>
                  <Input type="number" value={customSavingsForm.duration_months} onChange={(e) => setCustomSavingsForm(p => ({ ...p, duration_months: parseInt(e.target.value) || 12 }))} data-testid="cs-duration" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Période de rentabilité</Label>
                  <Select value={customSavingsForm.rentability_period} onValueChange={(v) => setCustomSavingsForm(p => ({ ...p, rentability_period: v }))}>
                    <SelectTrigger data-testid="cs-rent-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Journalier">Journalier</SelectItem>
                      <SelectItem value="Mensuel">Mensuel</SelectItem>
                      <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                      <SelectItem value="Semestriel">Semestriel</SelectItem>
                      <SelectItem value="Annuel">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Période d&apos;intérêts</Label>
                  <Select value={customSavingsForm.interest_period} onValueChange={(v) => setCustomSavingsForm(p => ({ ...p, interest_period: v }))}>
                    <SelectTrigger data-testid="cs-int-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Journalier">Journalier</SelectItem>
                      <SelectItem value="Mensuel">Mensuel</SelectItem>
                      <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                      <SelectItem value="Semestriel">Semestriel</SelectItem>
                      <SelectItem value="Annuel">Annuel</SelectItem>
                      <SelectItem value="Fin de contrat">Fin de contrat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Label>Disponibilité des fonds</Label>
                  <div className="space-y-2">
                    {[{v:'immediate',l:'Immédiate'},{v:'echeance',l:'À échéance'},{v:'terme',l:'À terme'}].map(o => (
                      <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="fund_avail" checked={customSavingsForm.fund_availability === o.v} onChange={() => setCustomSavingsForm(p => ({ ...p, fund_availability: o.v }))} />
                        <span className="text-sm">{o.l}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>Garantie des fonds</Label>
                  <div className="space-y-2">
                    {[{v:'none',l:'Non couvert'},{v:'partial',l:'Partielle (capital)'},{v:'total',l:'Totale (capital + intérêts)'}].map(o => (
                      <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="fund_guar" checked={customSavingsForm.fund_guarantee === o.v} onChange={() => setCustomSavingsForm(p => ({ ...p, fund_guarantee: o.v }))} />
                        <span className="text-sm">{o.l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Conditions générales (contrat)</Label>
                <Textarea
                  value={customSavingsForm.terms_conditions}
                  onChange={(e) => setCustomSavingsForm(p => ({ ...p, terms_conditions: e.target.value }))}
                  rows={6}
                  placeholder="Laissez vide pour le contrat standard..."
                  className="font-mono text-xs"
                  data-testid="cs-terms"
                />
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={customSavingsForm.interest_compound} onCheckedChange={(v) => setCustomSavingsForm(p => ({ ...p, interest_compound: v }))} data-testid="cs-compound" />
                  <span className="text-sm">Gains cumulé (intérêts composés)</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Versement des intérêts :</span>
                  <Select value={customSavingsForm.interest_payout_target || 'available'} onValueChange={(v) => setCustomSavingsForm(p => ({ ...p, interest_payout_target: v }))}>
                    <SelectTrigger className="w-48" data-testid="cs-payout-target"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Fonds disponibles</SelectItem>
                      <SelectItem value="livret">Capital du livret</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomSavingsModal(false)}>Annuler</Button>
              <Button onClick={handleCreateCustomSavings} disabled={saving} style={{ backgroundColor: primaryColor }} data-testid="cs-submit">
                {saving ? 'Création...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Placement Edit Modal */}
        <Dialog open={showPlacementEditModal} onOpenChange={setShowPlacementEditModal}>
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto"
            data-testid="placement-edit-modal"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" data-testid="placement-edit-modal-title">
                <Edit className="w-5 h-5" />
                ÉDITION DE L&apos;HISTORIQUE
              </DialogTitle>
            </DialogHeader>
            
            {selectedPlacement && (
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input 
                      value={placementEditData.description}
                      onChange={(e) => setPlacementEditData({...placementEditData, description: e.target.value})}
                      placeholder="Description du placement"
                      data-testid="placement-edit-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input 
                      type="date"
                      value={placementEditData.start_date}
                      onChange={(e) => setPlacementEditData({...placementEditData, start_date: e.target.value})}
                      data-testid="placement-edit-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Montant</Label>
                    <Input 
                      type="text"
                      inputMode="decimal"
                      value={placementEditData.amount_input ?? placementEditData.amount}
                      onChange={(e) => {
                        const val = e.target.value.replace(',', '.');
                        setPlacementEditData({
                          ...placementEditData, 
                          amount_input: e.target.value,
                          amount: parseFloat(val) || placementEditData.amount
                        });
                      }}
                      onBlur={(e) => {
                        const val = parseNumberOnBlur(e.target.value);
                        setPlacementEditData({...placementEditData, amount: val, amount_input: undefined});
                      }}
                      onFocus={handleInputFocus}
                      data-testid="placement-edit-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Input value="Placement" disabled data-testid="placement-edit-type-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select 
                      value={placementEditData.status}
                      onValueChange={(val) => setPlacementEditData({...placementEditData, status: val})}
                    >
                      <SelectTrigger data-testid="placement-edit-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {placementStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* SOUSCRIPTION Section */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                    <span className="font-semibold text-sm">SOUSCRIPTION</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Produit:</span>
                      <span className="ml-2 font-medium">{selectedPlacement.product?.name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Durée:</span>
                      <span className="ml-2 font-medium">{selectedPlacement.duration_months} mois</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Rentabilité:</span>
                      <span className="ml-2 font-medium text-green-600">{selectedPlacement.rentability_rate}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date souscription:</span>
                      <span className="ml-2 font-medium">{new Date(selectedPlacement.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>

                {/* PLACEMENT Section - Contrat modifiable */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                      <span className="font-semibold text-sm">CONTRAT - CONDITIONS GÉNÉRALES</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">Le contrat de base peut être personnalisé pour ce client</span>
                  </div>
                  <Textarea
                    value={placementEditData.custom_terms}
                    onChange={(e) => setPlacementEditData({...placementEditData, custom_terms: e.target.value})}
                    placeholder="Le contrat de base du produit sera utilisé si ce champ est vide..."
                    className="min-h-[200px] font-mono text-xs"
                    data-testid="placement-edit-contract-textarea"
                  />
                </div>

                {/* ACTIONS SUR LES PORTEFEUILLES */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                    <span className="font-semibold text-sm">ACTIONS SUR LES PORTEFEUILLES</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-3 bg-background">
                      <Badge className="mb-2" style={{ backgroundColor: primaryColor }}>Edit</Badge>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date:</span>
                          <span>{new Date(selectedPlacement.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Montant:</span>
                          <span className="font-medium">{placementEditData.amount?.toLocaleString('fr-FR')} €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type:</span>
                          <span>Placement</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Statut:</span>
                          <Badge variant="outline" className="text-xs">
                            {placementStatuses.find(s => s.value === placementEditData.status)?.label || placementEditData.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                          <Checkbox
                            id="apply-capital"
                            checked={placementEditData.apply_to_capital}
                            onCheckedChange={(checked) => setPlacementEditData({...placementEditData, apply_to_capital: checked})}
                            data-testid="placement-edit-apply-capital-checkbox"
                          />
                          <Label htmlFor="apply-capital" className="text-xs cursor-pointer">
                            Agir sur le capital investi
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPlacementEditModal(false)}
                data-testid="placement-edit-cancel-button"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSavePlacementEdit}
                disabled={saving}
                style={{ backgroundColor: primaryColor }}
                data-testid="placement-edit-save-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Wallet Modal */}
        <Dialog open={showWalletModal} onOpenChange={setShowWalletModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un Wallet Crypto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Crypto-monnaie *</Label>
                <Select
                  value={newWallet.currency_code}
                  onValueChange={(val) => setNewWallet(prev => ({ ...prev, currency_code: val }))}
                >
                  <SelectTrigger data-testid="wallet-currency-select">
                    <SelectValue placeholder="Sélectionner une crypto" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {supportedCurrencies
                      .filter(c => !cryptoWallets.some(w => w.currency_code === c.code))
                      .map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getCryptoColor(currency.code)}`}>
                              {getCryptoIcon(currency.code)}
                            </span>
                            <span>{currency.name}</span>
                            <span className="text-xs text-muted-foreground">({currency.network})</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Adresse du Wallet *</Label>
                <Input
                  value={newWallet.wallet_address}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, wallet_address: e.target.value }))}
                  placeholder="0x... ou bc1... ou autre"
                  data-testid="wallet-address-input"
                />
              </div>
              
              <div>
                <Label>QR Code (optionnel)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleQrCodeUpload(e, false)}
                    className="flex-1"
                  />
                  {newWallet.qr_code_url && (
                    <img src={newWallet.qr_code_url} alt="QR Preview" className="w-12 h-12 object-contain bg-white rounded" />
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowWalletModal(false);
                setNewWallet({ currency_code: '', wallet_address: '', qr_code_url: '' });
              }}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateWallet}
                style={{ backgroundColor: primaryColor }}
                data-testid="save-wallet-btn"
              >
                Créer le Wallet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auto Trading Preview Modal */}
        <Dialog open={showSimulationModal} onOpenChange={setShowSimulationModal}>
          <DialogContent className="max-w-5xl bg-[#1e293b] border-[#334155]" data-testid="auto-trading-preview-modal">
            <DialogHeader>
              <DialogTitle>Prévisualisation des positions</DialogTitle>
              <p className="text-sm text-gray-400">
                Vérifiez les positions qui seront générées avant validation.
              </p>
            </DialogHeader>

            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-[#334155] bg-slate-900/40 p-4">
                <div>
                  <p className="text-xs text-gray-400">Profit net estimé</p>
                  <p className={`text-lg font-semibold ${simulationTotalProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {simulationTotalProfit >= 0 ? '+' : ''}{simulationTotalProfit.toFixed(2)}€
                  </p>
                </div>
                {simulationDateRange && (
                  <div>
                    <p className="text-xs text-gray-400">Période</p>
                    <p className="text-sm text-gray-200" data-testid="auto-trading-preview-date-range">
                      {simulationDateRange.start} → {simulationDateRange.end}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-400">Positions</p>
                  <p className="text-sm text-gray-200" data-testid="auto-trading-preview-count">
                    {simulationPreview.length}
                  </p>
                </div>
              </div>

              <div className="max-h-[360px] overflow-y-auto rounded-lg border border-[#334155]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/60 sticky top-0">
                    <tr className="text-left text-xs uppercase text-gray-400">
                      <th className="px-4 py-2">Symbole</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Date prévue</th>
                      <th className="px-4 py-2">Open</th>
                      <th className="px-4 py-2">Close</th>
                      <th className="px-4 py-2">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulationPreview.map((pos, index) => (
                      <tr key={`${pos.id || pos.symbol}-${index}`} className="border-b border-[#334155] last:border-0" data-testid={`auto-trading-preview-row-${index}`}>
                        <td className="px-4 py-2 text-gray-200">
                          <div className="font-medium">{pos.symbol}</div>
                          <div className="text-xs text-gray-500">{pos.category}</div>
                        </td>
                        <td className="px-4 py-2 text-gray-200">{pos.trade_type === 'BUY' ? 'Achat' : 'Vente'}</td>
                        <td className="px-4 py-2 text-gray-200">{pos.trade_date} {pos.close_time}</td>
                        <td className="px-4 py-2 text-gray-200">{Number(pos.open_price || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-gray-200">{Number(pos.close_price || 0).toFixed(2)}</td>
                        <td className={`px-4 py-2 font-medium ${pos.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {pos.profit >= 0 ? '+' : ''}{Number(pos.profit || 0).toFixed(2)}€
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSimulationModal(false)}
                  data-testid="auto-trading-preview-cancel"
                >
                  Fermer
                </Button>
                <Button
                  onClick={handleConfirmPreviewPositions}
                  disabled={executingTrade}
                  data-testid="auto-trading-preview-confirm"
                >
                  {executingTrade ? 'Validation...' : 'Valider & Générer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Process Wallet Request Modal */}
        <Dialog open={showProcessRequestModal} onOpenChange={setShowProcessRequestModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Traiter la Demande de Wallet</DialogTitle>
            </DialogHeader>
            {selectedWalletRequest && (
              <div className="space-y-4 py-4">
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${getCryptoColor(selectedWalletRequest.currency_code)}`}>
                      {getCryptoIcon(selectedWalletRequest.currency_code)}
                    </div>
                    <div>
                      <p className="font-semibold">{selectedWalletRequest.currency_name}</p>
                      <p className="text-sm text-muted-foreground">Réseau: {selectedWalletRequest.network}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Demandé le {new Date(selectedWalletRequest.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <p className="text-sm font-medium">Pour approuver, remplissez les informations du wallet :</p>
                  
                  <div>
                    <Label>Adresse du Wallet *</Label>
                    <Input
                      value={walletApprovalData.wallet_address}
                      onChange={(e) => setWalletApprovalData(prev => ({ ...prev, wallet_address: e.target.value }))}
                      placeholder="Entrez l'adresse du wallet"
                      data-testid="approval-wallet-address"
                    />
                  </div>
                  
                  <div>
                    <Label>QR Code (optionnel)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleQrCodeUpload(e, true)}
                        className="flex-1"
                      />
                      {walletApprovalData.qr_code_url && (
                        <img src={walletApprovalData.qr_code_url} alt="QR Preview" className="w-12 h-12 object-contain bg-white rounded" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => handleProcessWalletRequest('reject')}
                className="text-red-500 hover:text-red-400"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Refuser
              </Button>
              <Button 
                onClick={() => handleProcessWalletRequest('approve')}
                style={{ backgroundColor: primaryColor }}
                data-testid="approve-wallet-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approuver
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Transaction Dialog */}
        <Dialog open={!!editingTx} onOpenChange={() => setEditingTx(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifier la transaction</DialogTitle>
            </DialogHeader>
            {editingTx && (
              <div className="space-y-3 py-2">
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={editingTx.description || ''} onChange={(e) => setEditingTx(p => ({...p, description: e.target.value}))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={editingTx.transaction_type} onValueChange={(v) => setEditingTx(p => ({...p, transaction_type: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="depot">Dépôt</SelectItem>
                        <SelectItem value="retrait">Retrait</SelectItem>
                        <SelectItem value="bonus">Bonus</SelectItem>
                        <SelectItem value="transfert">Transfert</SelectItem>
                        <SelectItem value="souscription">Souscription</SelectItem>
                        <SelectItem value="virement">Virement</SelectItem>
                        <SelectItem value="ajustement">Ajustement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Montant</Label>
                    <Input type="number" step="0.01" value={editingTx.amount || 0} onChange={(e) => setEditingTx(p => ({...p, amount: parseFloat(e.target.value) || 0}))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select value={editingTx.status} onValueChange={(v) => setEditingTx(p => ({...p, status: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {transactionStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={editingTx.transaction_date || ''} onChange={(e) => setEditingTx(p => ({...p, transaction_date: e.target.value}))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input value={editingTx.notes || ''} onChange={(e) => setEditingTx(p => ({...p, notes: e.target.value}))} />
                </div>
                <Button className="w-full" onClick={async () => {
                  try {
                    await axios.put(`${API}/transactions/${clientId}/${editingTx.id}`, {
                      description: editingTx.description,
                      amount: editingTx.amount,
                      transaction_type: editingTx.transaction_type,
                      status: editingTx.status,
                      transaction_date: editingTx.transaction_date,
                      notes: editingTx.notes,
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Transaction modifiée');
                    setEditingTx(null);
                    fetchClientData(true);
                  } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                }}>Enregistrer</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Livret Transfer Modal */}
      <Dialog open={livretTransferModal.open} onOpenChange={(open) => !open && setLivretTransferModal({ open: false, type: '', sub: null, amount: '', loading: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {livretTransferModal.type === 'withdraw' ? (
                <div className="p-2 rounded-full bg-orange-500/20">
                  <ArrowUpCircle className="w-5 h-5 text-orange-500" />
                </div>
              ) : (
                <div className="p-2 rounded-full bg-blue-500/20">
                  <ArrowDownCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
              {livretTransferModal.type === 'withdraw' ? 'Retrait du livret' : 'Ajout de fonds'}
            </DialogTitle>
          </DialogHeader>
          {livretTransferModal.sub && (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-xl bg-secondary/40 border border-border/50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Livret</span>
                  <span className="font-semibold">{livretTransferModal.sub.product_name || 'Livret'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Solde actuel</span>
                  <span className="text-lg font-bold" style={{ color: primaryColor }}>
                    {livretTransferModal.sub.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {livretTransferModal.type === 'withdraw' ? 'Montant à retirer vers fonds disponibles' : 'Montant à ajouter depuis fonds disponibles'}
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={livretTransferModal.amount}
                    onChange={(e) => setLivretTransferModal(prev => ({ ...prev, amount: e.target.value }))}
                    className="text-lg pr-8"
                    autoFocus
                    data-testid="livret-transfer-amount"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">€</span>
                </div>
                {livretTransferModal.type === 'withdraw' && livretTransferModal.amount && parseFloat(livretTransferModal.amount) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nouveau solde livret : <span className="font-semibold">{Math.max(0, (livretTransferModal.sub.amount || 0) - parseFloat(livretTransferModal.amount || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </p>
                )}
                {livretTransferModal.type === 'deposit' && livretTransferModal.amount && parseFloat(livretTransferModal.amount) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nouveau solde livret : <span className="font-semibold">{((livretTransferModal.sub.amount || 0) + parseFloat(livretTransferModal.amount || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </p>
                )}
              </div>

              <DialogFooter className="gap-2 pt-2">
                <Button variant="outline" onClick={() => setLivretTransferModal({ open: false, type: '', sub: null, amount: '', loading: false })}>
                  Annuler
                </Button>
                <Button
                  onClick={handleLivretTransfer}
                  disabled={livretTransferModal.loading || !livretTransferModal.amount || parseFloat(livretTransferModal.amount) <= 0}
                  className={livretTransferModal.type === 'withdraw' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}
                  data-testid="livret-transfer-submit"
                >
                  {livretTransferModal.loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : livretTransferModal.type === 'withdraw' ? (
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                  ) : (
                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                  )}
                  {livretTransferModal.loading ? 'En cours...' : livretTransferModal.type === 'withdraw' ? 'Retirer' : 'Ajouter'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal Epargne Retroactive */}
      <Dialog open={showRetroactiveModal} onOpenChange={setShowRetroactiveModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="retroactive-modal">
          <DialogHeader>
            <DialogTitle>Epargne retroactive</DialogTitle>
            <DialogDescription>Creer une epargne avec une date de debut anterieure. Les interets manques seront calcules automatiquement.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Choix du produit */}
            <div>
              <label className="text-sm font-medium">Produit d&apos;epargne</label>
              <select
                className="w-full mt-1 border rounded-md p-2 text-sm bg-background text-foreground"
                value={retroactiveForm.product_id}
                onChange={(e) => {
                  const pid = e.target.value;
                  const prod = (allProducts || []).find(p => p.id === pid);
                  if (prod) {
                    // Derive interest_period from interest_periods dict or rentability_period
                    let derivedInterestPeriod = prod.rentability_period || 'Mensuel';
                    const ip = prod.interest_periods;
                    if (ip && typeof ip === 'object') {
                      if (ip.monthly) derivedInterestPeriod = 'Mensuel';
                      else if (ip.quarterly) derivedInterestPeriod = 'Trimestriel';
                      else if (ip.biannual) derivedInterestPeriod = 'Semestriel';
                      else if (ip.annual) derivedInterestPeriod = 'Annuel';
                      else if (ip.end_of_contract) derivedInterestPeriod = 'Fin de contrat';
                    }
                    setRetroactiveForm(f => ({
                      ...f,
                      product_id: pid,
                      name: prod.name || '',
                      rentability_rate: prod.rentability_rate || '',
                      rentability_variable: prod.rentability_variable || 0,
                      duration_months: prod.duration_months || 12,
                      rentability_period: prod.rentability_period || 'Mensuel',
                      interest_period: derivedInterestPeriod,
                      interest_compound: prod.interest_compound || false,
                      interest_payout_target: prod.interest_payout_target || 'livret',
                      fund_availability: prod.fund_availability || 'immediate',
                      fund_guarantee: prod.fund_guarantee || (prod.contract_guarantee ? 'total' : 'none'),
                    }));
                  } else {
                    setRetroactiveForm(f => ({ ...f, product_id: pid }));
                  }
                }}
                data-testid="retro-product-select"
              >
                <option value="">-- Personnalise --</option>
                {(allProducts || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.rentability_rate}% {p.rentability_period})</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Nom</label>
                <Input value={retroactiveForm.name} onChange={e => setRetroactiveForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom du livret" data-testid="retro-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Montant initial *</label>
                <Input type="number" value={retroactiveForm.amount} onChange={e => setRetroactiveForm(f => ({ ...f, amount: e.target.value }))} placeholder="5000" data-testid="retro-amount" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Date de debut *</label>
                <Input type="date" value={retroactiveForm.start_date} onChange={e => {
                  const sd = e.target.value;
                  const dm = parseInt(retroactiveForm.duration_months) || 12;
                  const endD = sd ? new Date(new Date(sd).setMonth(new Date(sd).getMonth() + dm)).toISOString().slice(0,10) : '';
                  setRetroactiveForm(f => ({ ...f, start_date: sd, end_date_display: endD }));
                }} max={new Date().toISOString().slice(0,10)} data-testid="retro-start-date" />
              </div>
              <div>
                <label className="text-sm font-medium">Duree (mois)</label>
                <Input type="number" value={retroactiveForm.duration_months} onChange={e => {
                  const dm = parseInt(e.target.value) || 12;
                  const sd = retroactiveForm.start_date;
                  const endD = sd ? new Date(new Date(sd).setMonth(new Date(sd).getMonth() + dm)).toISOString().slice(0,10) : '';
                  setRetroactiveForm(f => ({ ...f, duration_months: dm, end_date_display: endD }));
                }} data-testid="retro-duration" />
              </div>
            </div>

            {retroactiveForm.start_date && (
              <p className="text-xs text-muted-foreground">Date de fin calculee : <span className="font-semibold">{retroactiveForm.end_date_display || '—'}</span></p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Taux de rentabilite (%) *</label>
                <Input type="number" step="0.01" value={retroactiveForm.rentability_rate} onChange={e => setRetroactiveForm(f => ({ ...f, rentability_rate: e.target.value }))} placeholder="9.75" data-testid="retro-rate" />
              </div>
              <div>
                <label className="text-sm font-medium">Periode du taux</label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background text-foreground" value={retroactiveForm.rentability_period} onChange={e => setRetroactiveForm(f => ({ ...f, rentability_period: e.target.value }))} data-testid="retro-rate-period">
                  <option value="Mensuel">Mensuel</option>
                  <option value="Trimestriel">Trimestriel</option>
                  <option value="Semestriel">Semestriel</option>
                  <option value="Annuel">Annuel</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Periode des versements</label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background text-foreground" value={retroactiveForm.interest_period} onChange={e => setRetroactiveForm(f => ({ ...f, interest_period: e.target.value }))} data-testid="retro-interest-period">
                  <option value="Mensuel">Mensuel</option>
                  <option value="Trimestriel">Trimestriel</option>
                  <option value="Semestriel">Semestriel</option>
                  <option value="Annuel">Annuel</option>
                  <option value="Fin de contrat">Fin de contrat</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Destination des interets</label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background text-foreground" value={retroactiveForm.interest_payout_target} onChange={e => setRetroactiveForm(f => ({ ...f, interest_payout_target: e.target.value }))} data-testid="retro-payout-target">
                  <option value="livret">Capitaliser (Livret)</option>
                  <option value="available">Fonds disponibles</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" checked={retroactiveForm.interest_compound} onChange={e => setRetroactiveForm(f => ({ ...f, interest_compound: e.target.checked }))} data-testid="retro-compound" />
              <label className="text-sm">Interets composes</label>
            </div>

            {/* Contrat */}
            <div>
              <label className="text-sm font-medium">Contrat</label>
              <div className="flex gap-2 mt-1">
                {[{v:'none',l:'Pas de contrat'},{v:'generate',l:'Generer un contrat'},{v:'upload',l:'Importer un PDF'}].map(opt => (
                  <button key={opt.v} type="button"
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${retroactiveForm.contract_option === opt.v ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    onClick={() => setRetroactiveForm(f => ({ ...f, contract_option: opt.v, contract_file: null }))}
                    data-testid={`retro-contract-${opt.v}`}
                  >{opt.l}</button>
                ))}
              </div>
              {retroactiveForm.contract_option === 'upload' && (
                <div className="mt-2">
                  <Input type="file" accept=".pdf" onChange={e => setRetroactiveForm(f => ({ ...f, contract_file: e.target.files?.[0] || null }))} data-testid="retro-contract-upload" />
                  {retroactiveForm.contract_file && <p className="text-xs text-green-600 mt-1">{retroactiveForm.contract_file.name}</p>}
                </div>
              )}
            </div>

            {/* Mouvements retroactifs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Mouvements retroactifs</label>
                <Button type="button" variant="outline" size="sm" onClick={() => setRetroactiveForm(f => ({
                  ...f,
                  movements: [...f.movements, { date: '', amount: '', type: 'ajout_livret' }]
                }))} data-testid="retro-add-movement">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>
              {retroactiveForm.movements.length === 0 && (
                <p className="text-xs text-muted-foreground">Aucun mouvement. Vous pouvez ajouter des depots ou retraits passes.</p>
              )}
              {retroactiveForm.movements.map((mv, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <select className="border rounded-md p-1.5 text-xs" value={mv.type} onChange={e => {
                    const updated = [...retroactiveForm.movements];
                    updated[idx].type = e.target.value;
                    setRetroactiveForm(f => ({ ...f, movements: updated }));
                  }} data-testid={`retro-mv-type-${idx}`}>
                    <option value="ajout_livret">Depot</option>
                    <option value="retrait_livret">Retrait</option>
                  </select>
                  <Input type="date" className="text-xs" value={mv.date} onChange={e => {
                    const updated = [...retroactiveForm.movements];
                    updated[idx].date = e.target.value;
                    setRetroactiveForm(f => ({ ...f, movements: updated }));
                  }} data-testid={`retro-mv-date-${idx}`} />
                  <Input type="number" className="text-xs" placeholder="Montant" value={mv.amount} onChange={e => {
                    const updated = [...retroactiveForm.movements];
                    updated[idx].amount = e.target.value;
                    setRetroactiveForm(f => ({ ...f, movements: updated }));
                  }} data-testid={`retro-mv-amount-${idx}`} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                    setRetroactiveForm(f => ({ ...f, movements: f.movements.filter((_, i) => i !== idx) }));
                  }} data-testid={`retro-mv-remove-${idx}`}>
                    <Trash className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowRetroactiveModal(false)}>Annuler</Button>
            <Button onClick={handleCreateRetroactiveSavings} disabled={saving || !retroactiveForm.amount || !retroactiveForm.start_date || !retroactiveForm.rentability_rate} style={{ backgroundColor: primaryColor }} data-testid="retro-submit-btn">
              {saving ? 'Creation en cours...' : 'Creer et calculer les interets'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );

  return inWorkspace ? content : <DashboardLayout>{content}</DashboardLayout>;
}
class ClientDetailErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return <div className="p-8 text-center"><h2 className="text-red-500 text-xl mb-4">Erreur lors du chargement de la fiche client</h2><p className="text-muted-foreground">{this.state.error?.message}</p><button onClick={() => window.history.back()} className="mt-4 px-4 py-2 bg-primary text-white rounded">Retour</button></div>;
    }
    return this.props.children;
  }
}

export default function AdminClientDetail() {
  return <ClientDetailErrorBoundary><AdminClientDetailInner /></ClientDetailErrorBoundary>;
}
